/**
 * The legacy-to-REST operation table.
 *
 * This file is DATA. `legacyBridge.js` is the only code that reads it, and it
 * contains no per-operation logic — which is what makes the migration
 * tractable: adding or correcting a mapping is a table edit, and deleting one
 * is how a component graduates to a native `api2/*` client.
 *
 * An OperationSpec:
 *
 *   {
 *     method:     'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
 *     path:       (p) => `/patients/${p.patientid}/allergies`,
 *     query?:     (p) => ({ ... }),        // GET only
 *     body?:      (p) => ({ ... }),        // writes; omit to forward `p` as-is
 *     paginated?: true,                    // unwrap {data, meta, links}
 *     upload?:    true,                    // send multipart
 *     download?:  true,                    // expect a blob
 *     shape:      (result, p) => legacyShapedResponse,
 *     errorStyle?: 'status' | 'code' | 'msg' | 'message',
 *     refusals?:  { [apiErrorCode]: legacyBody }, // answered with that body
 *   }
 *
 * `p` is the legacy payload: the contents of the envelope for keyon calls, or
 * the `data` member for pacmny-be calls. Identity fields that used to travel in
 * that payload — `accountnumber`, `unique_ID`, `subaccountnumber`, `actorId` —
 * are deliberately IGNORED here. Tenancy and identity now come from the access
 * token, and re-sending them would be both useless and misleading.
 *
 * Keys:
 *   KEYON_OPERATIONS       "<EnvelopeKey>.<API_Meth>"
 *   KEYON_FLAT_OPERATIONS  "<API_Meth>"          (calls with no envelope)
 *   BE_OPERATIONS          "<route>#<action>"    (the retired Yii2 backend)
 *   UNMAPPED               legacy names with no REST equivalent, and why
 *
 * `KEYON_FLAT_OPERATIONS` is also the envelope-agnostic tier: `legacyBridge`
 * falls back to it when an enveloped call's `<Envelope>.<Meth>` key is absent,
 * which is how the handful of operations posted under two different envelope
 * spellings (`NursyInfo`/`NursysInfo`, `ManageNurseList`/`ManageNurseLList`)
 * resolve from one entry.
 */

import {
  okSuccessful, okStatus, okMessage, okMsg, okCode1,
  records, dataEnvelope, dateOnly, hhmm, bool, id, rekey,
  orderRef, routeId, idList, fileList,
  scheduleTimes, holdTimes, noteType, noteTypeLabel, billableMinutes,
  diagnosisStatus, procedureStatus, conditionStatus, codedTerm,
  isProcedureLine, discontinuationType, wallClockTime,
  withMarSlots, scheduleSlots, careTeamAssignmentId, stateBlob,
  cptBillingBuckets, cptPageTables, allergyType, allergySeverity, allergyCriticality,
  allergyVerification, allergyClinicalStatus, allergyRetraction, allergyReactions,
  toLocalYMD, medicationRows, currentCourseRows, diagnosisCode, legacyDateTime, holdWindow, orderStatusWord, orderTypeWord,
  cptCycleSpan, foradataRow,
} from './bridgeShapes';
// Used only by the locally-resolved operations, which make their own calls
// rather than being described by a path.
import api, { ApiError } from './client';
// Nursys: keyon's answer shapes, and which failures resolve and which reject.
import {
  lookupNursesInfo, addNurse, deleteNurse, sendNurseSettings, lookupBatchNursesInfo,
} from './nursysBridge';
// The clinical operations that are more than one request or need more than a
// shape: orders, the Current Diagnosis / Procedures grid, ICD lookup, the
// signing pre-check and the report save.
import {
  insertNewOrder, orderDetailRow, icdLookup, signerShape, populateDiagGrid,
  chartProcedureRows, diagnosisLabel, stripCodeSystem, saveExtraction,
  vocabularyChoices, fileRef, renderedCdaHtml, cdaMedicationImportItems, cdaMedicationImportFields,
} from './clinicalBridge';
// Main's self profile (AccountUser.GetSelfProfile / UpdateSelfProfile) <-> /me.
import { meToSelfProfile, selfProfileToMePatch, selfProfileRefusal } from './selfProfileShape';
// Whether a message send may ask to alert its recipients (NT-11).
import { MESSAGE_NOTIFY_AVAILABLE, MESSAGE_NOTIFY_UNAVAILABLE_TEXT } from '@/utils/notifyChannels';
// The API's account-type slug in main's words ('Home Health Aid').
import { legacyAccountType } from '@/utils/accountTypeLabel';
// The widgets' inbox: keyon's flat message rows from the API's threads.
import { fetchInboxRows } from './messageInbox';
// Main's PatientContacts envelope <-> /patients/{patient}/contacts (ACC-16).
import {
  getPatientContacts as getPatientContactsLegacy,
  savePatientContact as savePatientContactLegacy,
  deletePatientContact as deletePatientContactLegacy,
  setPrimaryContact as setPrimaryContactLegacy,
} from './contactBridge';
// Main's CreateSubAccount, completed or performed (xltSi5PP), and the record
// Edit User opens on (ACC-22).
import {
  createSubAccount, editRecord, searchTerm as editSearchTerm, updateProvider, internalUpdateNurse, isAideAccountType,
} from './accountBridge';
// The Privileges page's rows (self or another subject) and the audit report.
import {
  readsOwnPrivileges, privilegeSubjectUser, legacyAssignmentRows, privilegeReportFromExport,
} from './privilegesBridge';
// Main's vitals feeds (vital-sign code keys, every reading grouped under them).
import { vitalSignCodeKeys, readExternalMeasures, listAllMeasurements } from './vitalsBridge';
import { listAllPatientDevices } from './devices';
// Main's continuous-glucose diary (the Continual tab), from the chart's LibreLink measurements.
import { readContinuousGlucose } from './glucoseBridge';
// Main's Fora device calls: removal by serial, nebulizer sessions by chart.
import { registerForaDeviceLegacy, removePatientDeviceBySerial, nebulizerSessionsLegacy } from './deviceBridge';
// The help modal's role sections: main's role_name rows out, a {role: html} map in.
import { helpRoleSectionRows, helpRoleSectionMap } from './helpBridge';
// The HIPAA audit page's filters and rows in keyon's vocabulary.
import { readAuditLogs } from './auditBridge';
// Video (#334): the note save that names videos, keeping its real refusal status.
import { saveNoteWithVideos } from './videoBridge';
// The PDF extraction screen: a drawn selection (screen pixels) placed on its page (PDF points) for the grab, the
// preview and the report save.
import { grabImage, previewDocument, reportExtractions } from './pdfExtractionBridge';
// The order sign-off popup: its one row (order, signatures, wall-clock times, medication), what it posts back per
// action, and the pending-orders query by whose signature is missing (NUR339-09, NUR339-11, NUR339-12).
import { lookupOrderSignOff, signOffBody, countersignBody, pendingOrdersFilter } from './orderSignOff';

/** What `systemData.GetOrderNumber` answers: the number is the server's, given at save (MV-26). */
export const ORDER_NUMBER_ON_SAVE = 'Assigned on save';

/**
 * Whether a legacy line names no order at all: main's chart-level quick-add
 * sends `ordernumber: 0`, and a few callers send no order field. Anything
 * else that `orderRef` cannot route - ORDER_NUMBER_ON_SAVE above, a display
 * number - names an order the bridge cannot address, which is not "none".
 */
function namesNoOrder(p) {
  return ['orderid', 'order_id', 'ordernumber', 'ordnum'].every((key) => {
    const value = p ? p[key] : undefined;
    if (value === undefined || value === null) return true;
    const text = String(value).trim();
    return text === '' || text === '0';
  });
}

/**
 * The new `users` resource against the field names the legacy rosters bind to.
 * Applied per-row rather than per-endpoint because ten legacy reads collapsed
 * onto `GET /users` (catalogue row 28) and every one of them named the columns
 * differently.
 */
const USER_ROW = {
  id: 'unique_ID',
  role: 'account_type',
  first_name: 'firstname',
  last_name: 'lastname',
  phone_primary: 'phone',
  address_line1: 'address1',
  address_line2: 'address2',
  postal_code: 'zip',
};

/**
 * As above, for the grids that read `user_email` / `subaccountnumber`.
 *
 * The aliases below are not decoration. Every staff grid and card in the SPA
 * binds the legacy spelling, so a name the API returns under its own key reads
 * as blank on screen — and several of those blanks are not cosmetic: an empty
 * `primaryphone` renders a `tel:undefined` link, and an empty licence number is
 * also the click target that launches Nursys verification.
 *
 * `rekey` DELETES the source key, so anything USER_ROW already renamed must be
 * read here under its new name (`row.phone`, `row.address1`, `row.zip`).
 * Everything else still carries the API's own field name.
 */
const staffRows = (rows) => rekey(rows, USER_ROW).map((row) => ({
  ...row,
  user_email: row.email,
  // The account grid's DataTables columns. `firstname`/`lastname` come from
  // USER_ROW; these are the same two names again, capitalised the way the
  // account table happens to ask for them. Missing, DataTables raises its own
  // alert — "Requested unknown parameter 'user_Fname'" — once per row.
  user_Fname: row.firstname,
  user_Lname: row.lastname,
  subaccountnumber: row.unique_ID,
  providerID: row.unique_ID,
  providerid: row.unique_ID,
  pmid: row.unique_ID,

  // --- contact -----------------------------------------------------------
  // Four spellings of one column. `users.phone` carries the comment "Was
  // providers.tel / LA_user.ZV_user_phone1", which is why the same value has to
  // answer to all of them.
  primaryphone: row.phone,
  tel: row.phone,
  phone1: row.phone,
  cell: row.phone_secondary,
  secondaryphone: row.phone_secondary,
  addr1: row.address1,
  addr2: row.address2,
  postalcode: row.zip,

  // --- identity ----------------------------------------------------------
  dob: row.date_of_birth,
  // The legacy carried the person's state in two columns on two tables; both
  // collapsed onto `users.status`, so both names read from it.
  activestatus: row.status,
  accountStatus: row.status,
  // My Account's status column and Resend link (accountStatusCell): main's
  // words, Pending and Expired, which are the two a resend acts on. The API
  // reports `expired` for an invitation past its 30-day clock before the
  // nightly sweep has stored it. `invite_sent_at` is the "Last Invite Sent"
  // column, which read blank without it.
  account_status: accountStatusLabel(row.status),
  invite_sent_at: row.invited_at,
  professionType: row.profession_type,
  providerType: row.provider_segment,
  // The legacy misspelling is the one the grids bind, so both are emitted.
  acountnumber: row.organization_number ?? row.organization_id,
  accountnumber: row.organization_number ?? row.organization_id,

  // --- malpractice cover -------------------------------------------------
  // Read by the nurse and aide cards. They used to substitute a hard-coded
  // "Blue Cross Blue Shield" for a falsy carrier (main's PR #235 removed it,
  // and the stores now show none), so an unmapped field here rendered as a
  // carrier the person does not have; unmapped now, it would read as no cover.
  insuranceCompany: row.malpractice_carrier,
  insuranceEnd: row.malpractice_ends_on,
  perIncidentAmnt: row.malpractice_per_incident_limit,
  annualAggAmnt: row.malpractice_aggregate_limit,

  // --- credentials -------------------------------------------------------
  // Only present when the spec asked for `?include=licenses`; absent otherwise,
  // which is the honest outcome — a blank licence number is better than one
  // borrowed from the wrong row.
  ...licenceAliases(row),

  // --- the person's own paperwork ----------------------------------------
  ...documentAliases(row),
}));

/**
 * A colleague from `GET /staff-directory`, in the roster's spellings
 * (staffRows) with the contact fields the directory does not carry present
 * and empty.
 *
 * The directory is names, role, work phones and profession, and no e-mail (the
 * port's sign-in identifier, which only `users:read` may see). Main's rows
 * always had these keys, and the cards call string methods on them: the Allied
 * Professionals search lowercases `email` and takes `phone.includes(...)`. A
 * missing key made that search throw for every non-administrator the first
 * time the term matched neither name; "none on file" is the empty string, as
 * it is everywhere else a card shows a blank.
 */
const DIRECTORY_CONTACT_KEYS = ['email', 'user_email', 'phone', 'primaryphone', 'tel', 'phone1', 'cell', 'secondaryphone'];
const directoryRows = (rows) => staffRows(rows).map((row) => ({
  ...row,
  ...Object.fromEntries(DIRECTORY_CONTACT_KEYS.map((key) => [key, row[key] ?? ''])),
}));

/**
 * Main's `providers.professionType` for a provider-directory row: 'Provider'
 * or 'Allied Professional'.
 *
 * `users.profession_type` holds that classification for an allied
 * professional (pacmny-api AlliedSpecialty: 'Provider' for a forced-provider
 * specialty with an NPI, otherwise 'Allied Professional'). For a provider the
 * same column carries the specialty the profile mapping stored, which is not a
 * classification, so the account type decides.
 */
function providerProfessionType(row) {
  const stored = String((row && row.professionType) || '').trim().toLowerCase();

  if (stored === 'provider') return 'Provider';
  if (stored === 'allied professional') return 'Allied Professional';

  return row && row.account_type === 'allied_professional' ? 'Allied Professional' : 'Provider';
}

/** keyon's GetAllNurseStaff rows, out of the API's people (users or the staff directory). */
function nurseStaffRows(users) {
  return records(users.map((u) => ({
    ...u,
    providerID: u.id,
    firstname: u.first_name,
    lastname: u.last_name,
    caregivetype: legacyAccountType(u.role),
    primaryphone: u.phone_primary,
  })));
}

/**
 * Whose orders the home grid's order widget asks for (main's NEW_OrderFilter,
 * by the viewer's account type, `emraccounttype`): a patient session is shown
 * none, an administrator every order in the account, and anyone else the
 * orders of the patients on their own panel - not the orders they happened to
 * write. The bridge used to send the viewer as `filter[owner_user_id]`, which
 * the API reads as the AUTHOR, so an administrator saw almost nothing and a
 * nurse missed every order someone else wrote on her patients. The API bounds
 * all three by what the viewer may see; this only chooses the narrowing.
 */
function orderWidgetScope(accountType) {
  const kind = String(accountType || '').trim().toLowerCase();

  if (kind === 'patient' || kind === 'patients') return 'none';

  return kind === 'administrator' || kind === 'administrators' ? 'all' : 'panel';
}

/** The widget's "start end" date range (two tokens) or a two-item array, as `[from, to]`. */
function orderWidgetRange(range) {
  const tokens = Array.isArray(range) ? range : String(range || '').trim().split(/\s+/);

  return [tokens[0], tokens[1]];
}

/**
 * The widget's Order Type select, in the API's words. Main filtered `procOrMed`
 * on 'Medication' and 'Procedure' and nothing else - the select's placeholder
 * 'Select Order Type' (its default, and what Clear writes back) included
 * (SqlClass NEW_OrderFilter). Here that column is `order_category`, lower case.
 * It is not `filter[type]`, which is who ordered it (physician, nurse, verbal):
 * 'Medication' sent there matched no order, so a filtered widget listed none.
 */
function orderWidgetCategory(choice) {
  const word = String(choice ?? '').trim().toLowerCase();

  return word === 'medication' || word === 'procedure' ? word : undefined;
}

/**
 * The widget's Order Status select, in the API's words: 'Active', 'Pending' and
 * 'Active-Pending' are `active`, `pending` and `active_pending`. Main skipped
 * the select's placeholders ('Select Order Type' - Clear writes it into this
 * select too - and the labels 'Order Status' and 'Order Type'); sent on they
 * matched no order, so a widget that had been cleared listed nothing. Any other
 * word goes on as it reads and matches nothing, as it did in main.
 */
function orderWidgetStatus(choice) {
  const word = String(choice ?? '').trim();

  if (!word || /select|order status|order type/i.test(word)) return undefined;

  return word.toLowerCase().replace(/[\s-]+/g, '_');
}

/** The lines and the patient the widget's rows print (OrderWidget.vue), beside the order itself. */
const ORDER_WIDGET_INCLUDES = 'medications,procedures,diagnoses,patient';

/**
 * The licence and insurance a person's slots name - main's
 * `license_file_uid` / `insurance_file_uid`, which the nurse and aide cards
 * read (nurseCardsStore, aideCardStore) and open by uid through the file
 * endpoint. Never a URL or a path: main strips the legacy path fields from
 * every response, and its cards decide a document is on file by the presence
 * of a well-formed uid.
 *
 * The API returns the slots' files as a list (`slot` license | insurance),
 * and only to a caller holding `caregiver_documents:read`. An absent list
 * therefore means "you may not see these", not "there are none" — either way
 * the fields stay empty and the card renders without the links. A slot whose
 * file is gone still names it, and the card shows "Document unavailable".
 */
function documentAliases(row) {
  const docs = Array.isArray(row.documents) ? row.documents : [];

  if (docs.length === 0) return {};

  const inSlot = (slot) => docs.find((doc) => (doc.slot || doc.category) === slot) || null;
  const licence = inSlot('license');
  const insurance = inSlot('insurance');

  return {
    license_file_uid: licence?.file_uid ?? null,
    licenseFileName: licence?.original_filename ?? '',
    insurance_file_uid: insurance?.file_uid ?? null,
    insuranceFileName: insurance?.original_filename ?? '',
  };
}

// --- Credential documents and message attachments (main's file operations) --

/** A retired legacy operation: main answers 410 endpoint_retired, and so does the bridge, sending nothing. */
function retiredCredentialOperation() {
  return {
    local: () => {
      throw new ApiError({
        status: 410,
        code: 'endpoint_retired',
        message: 'This page is out of date. Reload it to continue.',
      });
    },
    errorStyle: 'status',
  };
}

/** A stored file's uid for a path, or a 404 without a request: main refuses any other shape first. */
function fileUidSegment(value) {
  const uid = String(value ?? '');

  if (!/^[a-f0-9]{32}$/.test(uid)) {
    throw new ApiError({ status: 404, code: 'not_found', message: 'That document could not be found.' });
  }

  return uid;
}

/**
 * Every page of a list, for a legacy call that answered with the whole set.
 *
 * The query should carry a total order, a unique key last (`sort: 'last_name,id'`).
 * Rows that tie are free to come back in a different order for page 1 and for
 * page 2, and a row on the boundary is then read twice or not at all; the
 * calendars' reads (diaryQuery, the time-off and block lists) and the staff
 * rosters (ROSTER_SORT) say so with `id`.
 */
async function allPages(path, query) {
  const items = [];

  // Bounded: 200 pages of 100 is far past any organization's paperwork, and a
  // loop that could not end is not a list.
  for (let page = 1; page <= 200; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await api.list(path, { ...query, page, per_page: 100 });
    items.push(...(result.items || []));

    const last = result.meta && Number(result.meta.last_page);
    if (!last || page >= last) break;
  }

  return items;
}

/**
 * A directory read main answered whole: the entry's own query read to its
 * last page (allPages), then its own shape. Main's provider directories had
 * no LIMIT (SqlClass getAllAccountAlliedNProviders, getAccountProviders);
 * the API's pages hold 100, and one request dropped everyone after the
 * hundredth surname from the scheduling picker and the patient cards. The
 * entry keeps its `query` and `shape`, so what it asks and how it answers
 * stay stated (and tested) in one place.
 *
 * An operation that is `local` skips legacyBridge's rule for a read whose path
 * names a record that is not in view (`/patients/null/notes`): no request, and
 * the shape's own answer for an empty list. This keeps it, so a chart's list
 * read before a chart is selected is "none", not a 404 the page has to explain.
 */
function wholeDirectory(spec) {
  return {
    ...spec,
    local: async (p) => {
      const path = spec.path(p);
      const items = /\/(null|undefined|NaN)(\/|$)/.test(path) ? [] : await allPages(path, spec.query(p));

      return spec.shape({ items, meta: null, links: null }, p);
    },
  };
}

/**
 * Whether the signed-in person may read the full user list (`users:read`:
 * administrators). Unknown - no session loaded, no store - counts as yes, so
 * the full read is tried and its own refusal falls back (rosterOrDirectory).
 */
async function mayReadUsers() {
  try {
    const { useSessionStore } = await import('@/stores/session');
    const session = useSessionStore();

    if (!session.me) await session.hydrate();

    // A session that could not be read, or has no permissions loaded, says
    // nothing about what the person may do (everyone holds some): only a loaded
    // one decides up front.
    const loaded = session.me && Array.isArray(session.permissions) && session.permissions.length > 0;

    return loaded ? session.can('users:read') : true;
  } catch (error) {
    return true;
  }
}

/**
 * The order a staff roster is read in: surname, then the unique key. Every
 * roster is walked to its last page (wholeDirectory), and surnames tie, so
 * the query carries a total order (allPages) - `GET /users` and
 * `GET /staff-directory` both take `id` as the tie-break.
 */
const ROSTER_SORT = 'last_name,id';

/**
 * A staff roster main served to every caller, read for everyone.
 *
 * `full` is the roster as the admin screens need it (`GET /users`: licences,
 * documents, addresses - `users:read`, administrators only). Anyone else got a
 * 403, which the roster's callers turn into an empty list: nurses,
 * providers, care managers and aides saw no providers to book with, no
 * colleagues to message, no allied cards. They are read from the staff
 * directory instead (`GET /staff-directory`: Active staff by name, role and
 * work phone, no e-mail or username), in the same shape with fewer fields.
 * The session decides up front, so a nurse's page load does not begin with a
 * refused request; a 403 from the full read (a permission taken away since the
 * session loaded) falls back all the same.
 */
function rosterOrDirectory(full, directory) {
  const wholeFull = wholeDirectory(full);
  const wholeDirectoryRead = wholeDirectory(directory);

  return {
    ...wholeFull,
    local: async (p) => {
      if (!(await mayReadUsers())) return wholeDirectoryRead.local(p);

      try {
        return await wholeFull.local(p);
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) return wholeDirectoryRead.local(p);

        throw error;
      }
    },
  };
}

/** Main's scope rule, as the API applied it: owner without an owner is own, anything unknown is own. */
function listedScope(scope, p) {
  if (scope === 'org') return 'org';

  return scope === 'owner' && p.owner_unique_id ? 'owner' : 'own';
}

/** MySQL's text form, which main's list carries as `created_at`: 'YYYY-MM-DD HH:MM:SS', UTC. */
function mysqlDateTime(value) {
  const date = value ? new Date(value) : null;

  return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 19).replace('T', ' ') : '';
}

/**
 * Main's ListCredentialDocuments body: groups of one owner's documents, in the
 * order the owners first appear, each item named by uid only.
 */
function credentialGroups(documents) {
  const groups = new Map();

  documents.forEach((doc) => {
    const owner = doc.subject || {};
    const ownerId = String(owner.id ?? doc.subject_user_id ?? '');

    if (!groups.has(ownerId)) {
      groups.set(ownerId, { owner_unique_id: ownerId, owner_name: owner.name || '', items: [] });
    }

    groups.get(ownerId).items.push({
      file_uid: doc.file_uid || doc.public_id,
      category: doc.category,
      name: doc.filename || '',
      content_type: doc.content_type || '',
      file_class: doc.file_class,
      byte_size: Number(doc.byte_size) || 0,
      created_at: mysqlDateTime(doc.uploaded_at),
      owner_unique_id: ownerId,
      scan_status: doc.scan_status,
    });
  });

  return Array.from(groups.values());
}

/**
 * A message's attachments as main's `thread_attachments` items
 * (MessageAttachmentItems::decorate): `{name, file_uid, kind, scan_status,
 * patient_focus_id?}` from the message's own link rows, and `{name,
 * file_uid: null, kind: null, scan_status: null, removed: true}` for a file
 * its sender took back. An item with no file keeps only its name.
 */
function threadAttachmentItems(attachments) {
  return attachments
    .slice()
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
    .map((attachment) => {
      const opens = !attachment.removed && /^[a-f0-9]{32}$/.test(String(attachment.file_uid || ''));
      const item = {
        name: attachment.original_name || '',
        file_uid: opens ? attachment.file_uid : null,
        kind: opens ? (attachment.file_class || null) : null,
        scan_status: opens ? (attachment.scan_status || null) : null,
      };

      // Main's decorate (#334): a video carries its size, and nothing else does.
      if (opens && attachment.file_class === 'video' && Number.isFinite(attachment.byte_size)) {
        item.byte_size = attachment.byte_size;
      }

      if (attachment.removed === true) item.removed = true;
      if (attachment.patient_focus_id) item.patient_focus_id = String(attachment.patient_focus_id);

      return item;
    });
}

/**
 * A send's `video_file_uids` as the JSON part takes it (#334): main's widgets
 * append a JSON list as a multipart field. Not judged here - the API answers a
 * malformed list or a second video in main's codes (MessageVideoRefs) - and
 * absent when there is none, so a send without video is the body it was.
 */
function videoFileUids(raw) {
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (Array.isArray(raw)) return raw.length ? raw : undefined;
  try {
    const list = JSON.parse(String(raw));
    return Array.isArray(list) && list.length === 0 ? undefined : list;
  } catch (_) {
    // Sent on as it came: the API refuses it as invalid_file_uid, which is what
    // the sender should be told.
    return raw;
  }
}

/**
 * A new thread's answer is the thread; main's answers the message's
 * attachments as `thread_attachments`, which the widgets read to know a video
 * they named went on the message (unattachedUids, #334). Read back - the
 * newest message of the thread, which is the one just posted - only when the
 * send named a video: a send without one has nothing the widgets check.
 */
async function postedThreadAttachments(thread, p) {
  if (!videoFileUids(p && p.video_file_uids) || !thread) return {};
  const threadId = thread.id || thread.public_id;
  if (!threadId) return {};
  const page = await api.list(`/message-threads/${threadId}/messages`, { sort: '-id', per_page: 1 });
  const message = (page.items || [])[0];
  return message && Array.isArray(message.attachments)
    ? { thread_attachments: threadAttachmentItems(message.attachments) }
    : {};
}

/**
 * The people a new thread's send left out because they are not Active
 * accounts: the API's `skipped_recipients`, as the widgets name a person
 * (`subaccountnumber`, `name`). Main's group send skips a recipient who is not
 * Active and answers with the number it inserted for, so the widgets say how
 * many got the message; the names ride along for the one that says who did
 * not. A reply's answer is a message, which has none.
 */
function skippedRecipients(result) {
  const list = result && Array.isArray(result.skipped_recipients) ? result.skipped_recipients : [];

  return list.map((person) => ({ subaccountnumber: String(person.id), name: person.name }));
}

/**
 * A message send's body, refused when it asks for its recipients to be
 * alerted while MESSAGE_NOTIFY_AVAILABLE is off (NT-11).
 *
 * With the switch on (it is: the message routes take `notify`, P-NOTIFY's
 * MessageNotifier), this only builds the body, and {@see messageNotify} maps
 * main's two fields into it. Off, forwarding them would store the message and
 * drop the alert without a word, so a caller that still asks is refused before
 * anything is stored, in the envelope it reads, with the reason.
 */
function refusingUnsupportedNotify(build) {
  return (p) => {
    const asks = bool(p && p.notify_recipients) || Boolean(p && p.notify_channels);

    if (asks && !MESSAGE_NOTIFY_AVAILABLE) {
      throw new ApiError({ status: 0, code: 'notify_unavailable', message: MESSAGE_NOTIFY_UNAVAILABLE_TEXT });
    }

    return build(p);
  };
}

/**
 * Main's "Notify recipients" (#322) as the API's `notify` (NT-08), or
 * undefined when the send did not ask — JSON.stringify then leaves it out.
 *
 * `notify_recipients` is '1' or 'true'. `notify_channels` is a JSON object (or
 * an object) of recipient id → email | sms | phone | all, sent only for the
 * overrides the sender saved; its keys become the API's user ids and its
 * values go as they are — the API drops a value it does not know and an id
 * that is not a recipient, as main does, and never refuses the message for
 * one. Who is notified is the message's recipients, decided by the server.
 */
function messageNotify(p) {
  if (!bool(p && p.notify_recipients)) return undefined;

  let raw = p.notify_channels;

  if (typeof raw === 'string') {
    try {
      raw = raw.trim() === '' ? null : JSON.parse(raw);
    } catch (e) {
      raw = null;
    }
  }

  const channels = {};

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    Object.entries(raw).forEach(([key, channel]) => {
      const userId = routeId(key);
      if (userId !== null && typeof channel === 'string') channels[userId] = channel.toLowerCase();
    });
  }

  return Object.keys(channels).length ? { enabled: true, channels } : { enabled: true };
}

/** The API's notify channel in main's words, as AddUserForm and the modal spell it. */
const NOTIFY_CHANNEL_LABELS = { email: 'Email', sms: 'SMS', phone: 'Phone', all: 'All' };

/** Main's send stage for the port's row status (main: queued | sent | failed). */
const LEGACY_SEND_STATUS = { pending: 'queued', sent: 'sent', failed: 'failed', unreachable: 'failed', skipped: 'failed' };

/** Main's notification channel names for the API's: its `phone` is our `voice`. */
function legacyNotifyChannel(channel) {
  return channel === 'voice' ? 'phone' : channel;
}

/** Main's channels list (email | sms | phone) as the API's, or undefined for "every channel". */
function templateChannels(channels) {
  const list = (Array.isArray(channels) ? channels : String(channels || '').split(','))
    .map((channel) => String(channel).trim().toLowerCase())
    .filter(Boolean)
    .map((channel) => (channel === 'phone' ? 'voice' : channel));

  return list.length ? [...new Set(list)] : undefined;
}

/**
 * Main's two message times (MX-08): `msgTimestamp`, the instant it was sent,
 * and `msgDate`, its day. The widgets date, order and pick the latest message
 * by `msgTimestamp || msgDate`, and utils/messageTimestamp.js renders
 * "Today · 9:41 AM" from it in the viewer's zone. Main's `msgTimestamp` was a
 * naive UTC wall time; the API's `sent_at` (a thread's `last_message_at`) is
 * ISO 8601 with its offset, which parseMessageInstant keeps as it is.
 * `msgDate` is that instant's day where the viewer is, not in UTC, so a
 * message sent at 22:00 New York time does not read as tomorrow's.
 */
function withMessageTimes(row, instant) {
  if (!row || typeof row !== 'object' || !instant) return row;

  return { ...row, msgTimestamp: instant, msgDate: toLocalYMD(instant) };
}

/** A message row with main's `thread_attachments`, when the API sent its attachments. */
function withThreadAttachments(row) {
  if (!row || typeof row !== 'object' || !Array.isArray(row.attachments)) return row;

  return { ...row, thread_attachments: threadAttachmentItems(row.attachments) };
}

/**
 * keyon's FetchAllMessagesByIDS answer for the signed-in person: the token's
 * subject, as `GET /me` names them, never the ids the legacy payload carries.
 * Each message gets main's attachment items and times before it is shaped.
 */
async function inboxRecords() {
  const { useSessionStore } = await import('@/stores/session');
  const session = useSessionStore();
  const me = session.me || (await session.hydrate());

  // Decision D4: nobody acts AS a patient. Under an act-as token (the dormant
  // RFC 8693 exchange) the API's inbox is the acted-as person's, while the
  // widgets thread it against the human at the keyboard (sessionIdentity
  // resolves `me.impersonating`): nothing would read as unread, and the
  // acted-as person's own messages would read as a conversation with
  // themselves. Neither person's inbox is right, so there is none, with the
  // reason; the widgets log a refused fetch and show no conversations.
  if (me && me.impersonating) {
    throw new ApiError({
      status: 0,
      code: 'acting_as_not_permitted',
      message: 'Messages are not available while acting as someone. Return to your own session to read your messages.',
    });
  }

  const rows = await fetchInboxRows({
    viewerId: me && me.id,
    decorate: (message) => withMessageTimes(withThreadAttachments(message), message && message.sent_at),
  });

  return records(rows);
}

/** Main's upload answer's file fields, from the API's. */
function uploadedFile(document, extra = {}) {
  const data = document || {};

  return {
    file_uid: data.file_uid || data.public_id || null,
    file_class: data.file_class || null,
    scan_status: data.scan_status || null,
    category: data.category || null,
    ...extra,
  };
}

/** `registered_nurse` -> `Registered Nurse`. */
function humanLabel(value) {
  if (!value) return '';

  return String(value)
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The active licence, flattened onto the row.
 *
 * `users.licenses[]` holds every credential a person has; the staff grids each
 * want one — a nurse's state professional licence, an aide's certificate. The
 * state licence wins where there is one, because that is what the legacy column
 * held and what an order prints; NPI is a registry number, not a licence, and is
 * exposed separately.
 */
function licenceAliases(row) {
  const licences = Array.isArray(row.licenses) ? row.licenses : [];

  if (licences.length === 0) return {};

  const primary = licences.find((l) => l.type === 'state_professional')
    || licences.find((l) => l.type === 'hha_certificate')
    || licences.find((l) => l.type !== 'npi')
    || null;

  const npi = licences.find((l) => l.type === 'npi') || null;

  const nursys = primary?.nursys || null;

  return {
    licensenum: primary?.number ?? '',
    certificatenumber: primary?.number ?? '',

    // What the licensing board says, which is a different fact from the
    // account status already aliased above. A nurse whose account is active but
    // whose licence the board has suspended must not read as simply "Active".
    ncsbnid: nursys?.ncsbn_id ?? '',
    licensestatus: nursys?.license_status ?? '',
    // Nursys's own Active flag, once Nursys has answered: main's roster
    // writeback stores it in nurses.activestatus, which the cards show as the
    // nurse's "Status" (NursysWriteback). Until then the account status
    // aliased above stands.
    ...(typeof nursys?.is_license_active === 'boolean'
      ? { activestatus: nursys.is_license_active ? 'YES' : 'NO', activeStatus: nursys.is_license_active ? 'YES' : 'NO' }
      : {}),
    checkstatus: nursys?.checked_at ? String(nursys.checked_at).replace('T', ' ').slice(0, 19) : '',
    hasdiscipline: nursys?.has_discipline ? 'Yes' : '',
    // The grids print this beside the person's name, so it wants the discipline
    // ("Registered Nurse"), not the licence's storage category
    // ("state_professional") — which is what a bare `type` renders as.
    licensetype: humanLabel(primary?.profession) || humanLabel(primary?.type),
    licenseexpdate: primary?.expires_on ?? '',
    licensestate: primary?.state ?? '',
    hasCertificate: primary !== null,
    npi: npi?.number ?? '',
    npinumber: npi?.number ?? '',
  };
}

/**
 * One slot out of a care-team response, in the legacy row shape.
 *
 * `GET /patients/{id}/care-team` answers with an object keyed by slot —
 * `providers`, `nurses`, `home_health_aides`, `allied_professionals`,
 * `geriatric_care_managers` — and it returns every slot regardless of
 * `filter[role]`. The bridge specs originally read `team.members`, a key the
 * endpoint has never emitted, so every provider and nurse list in the SPA came
 * back empty: the chart's Providers panel, the prescriber pickers on the
 * medication and order popups, the provider directories in search, and the
 * assigned-nurses panel.
 *
 * The member objects also carry the API's field names, while the call sites
 * read the legacy ones, so rekeying only the outer key would produce a
 * populated-but-unusable list. The aliases below are the spellings those grids
 * actually bind.
 */
const careTeamSlot = (team, slot) => {
  const rows = (team && Array.isArray(team[slot])) ? team[slot] : [];

  return rows.map((row) => ({
    ...row,
    unique_ID: row.user_id,
    subaccountnumber: row.user_id,
    member_unique_ID: row.user_id,
    providerid: row.user_id,
    providerID: row.user_id,
    nurseid: row.user_id,
    externid: row.user_public_id,
    firstname: row.first_name,
    lastname: row.last_name,
    Firstname: row.first_name,
    Lastname: row.last_name,
    fname: row.first_name,
    lname: row.last_name,
    npinumber: row.npi || '',

    // Contact, identity and credentials under their legacy names.
    //
    // The care-team resource carries all of this, but until these aliases
    // existed the provider card read `item.tel`, `item.cell`, `item.dob` and
    // `item.addr1` off a row that only ever spelled them `phone`,
    // `phone_secondary`, `date_of_birth` and `address_line1` — so every one of
    // those rows rendered blank next to a populated label.
    tel: row.phone,
    phone1: row.phone,
    primaryphone: row.phone,
    cell: row.phone_secondary,
    secondaryphone: row.phone_secondary,
    dob: row.date_of_birth,
    addr1: row.address_line1,
    addr2: row.address_line2,
    postalcode: row.postal_code,
    activestatus: row.status,

    // Supplies `npi`/`npinumber` for real, overriding the empty default above:
    // NPI is a licence row, not a column, so `row.npi` never existed.
    ...licenceAliases(row),

    // The team grids show membership with a varchar flag, not a boolean.
    onTeam: row.is_on_team ? '1' : '0',
  }));
};

/** The slots, in the order the pickers list them, with their display names. */
const CARE_TEAM_SLOTS = [
  ['providers', 'Providers'],
  ['nurses', 'Nurses'],
  ['home_health_aides', 'Home Health Aides'],
  ['allied_professionals', 'Allied Professionals'],
  ['geriatric_care_managers', 'Geriatric Care Managers'],
];

/**
 * The care team as the legacy "groups with members" shape.
 *
 * `Messages.GetPatientTeamForMessaging` fed a picker that walks
 * `group.members` and reads `group_name`. The messaging projection is the same
 * slot-keyed object as the full one, so the groups are synthesised from the
 * slots. Empty slots are dropped rather than rendered as empty headings.
 */
const careTeamGroups = (team) => CARE_TEAM_SLOTS
  .map(([slot, label]) => ({ group_name: label, members: careTeamSlot(team, slot) }))
  .filter((group) => group.members.length > 0);

/**
 * The `/directives` landing rows, in the spellings that page binds.
 *
 * The roster reads `patient_name`, `patient_pid` and `mrn`; the resource emits
 * `name`, `patient_id` and `medical_record_number`. Passed through untouched,
 * every row rendered a blank name and a row click navigated to
 * `/directives/undefined`.
 */
const directivePatientRows = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
  ...row,
  patient_name: row.name,
  patient_pid: row.patient_id,
  mrn: row.medical_record_number,
  // keyon's '1'/'0': the list page tests `String(p.reauth_expired) === "1"`.
  reauth_expired: row.reauth_expired ? '1' : '0',
}));

/**
 * One Directive Record in the flat row main's `GetDirectiveRecords` returns
 * (keyon SqlClass::GetDirectiveRecordsForPatient), which the detail page,
 * `directiveDocument.js` and `fileDelete.js` read field by field.
 *
 * The API groups the same facts — `submitter`, `approval`, `review`,
 * `reauthorization`, `termination`, `document` — so passed through untouched
 * every `rec.approved_by_name`, `rec.reauth_due_at` and `rec.document_file_uid`
 * was undefined: a signed directive rendered as unsigned, and a removable
 * document offered no Remove. The grouped blocks stay on the row too.
 *
 * `document_file_uid` is null once a person removed the document, as keyon's
 * is (DeleteDirectiveDocument clears it), so the page says "Document
 * unavailable" rather than opening a retired file. A file that is merely not
 * servable (missing, quarantined) keeps its uid with `has_document` false, as
 * on main.
 */
const directiveRecordRow = (row) => {
  const submitter = row.submitter || {};
  const approval = row.approval || {};
  const review = row.review || {};
  const reauth = row.reauthorization || {};
  const term = row.termination || {};
  const doc = row.document || null;
  const removed = Boolean(row.document_removed_at)
    || Boolean(doc && ['deleted', 'purged'].includes(doc.status));

  return {
    ...row,
    patient_pid: row.patient_id,
    document_authorized: row.is_document_authorized == null ? null : (row.is_document_authorized ? 1 : 0),
    created_by_id: submitter.user_id ?? null,
    created_by_name: submitter.name ?? '',
    relationship_to_patient: submitter.relationship_to_patient ?? null,
    relationship_other: submitter.relationship_other ?? null,
    is_legal_surrogate: submitter.is_legal_surrogate ? 1 : 0,
    authority_type: submitter.authority_type ?? null,
    approved_at: approval.approved_at ?? null,
    approved_by_id: approval.approved_by_id ?? null,
    approved_by_name: approval.approved_by_name ?? null,
    approved_by_npi: approval.approved_by_npi ?? null,
    last_reviewed_at: review.last_reviewed_at ?? null,
    last_reviewed_by_id: review.last_reviewed_by_id ?? null,
    last_reviewed_by_name: review.last_reviewed_by_name ?? null,
    needs_review: Boolean(review.needs_review),
    reauth_due_at: reauth.reauth_due_at ?? null,
    governing_state: reauth.governing_state ?? null,
    reauth_days: reauth.reauth_days ?? null,
    superseded_by_id: term.superseded_by_id ?? null,
    revoked_at: term.revoked_at ?? null,
    revoked_by_id: term.revoked_by_id ?? null,
    revoked_by_name: term.revoked_by_name ?? null,
    expired_at: term.expired_at ?? null,
    document_file_uid: doc && !removed ? doc.file_uid : null,
    document_name: doc ? doc.name : null,
    document_content_type: doc ? doc.content_type : null,
    document_file_class: doc ? doc.file_class : null,
    document_scan_status: doc ? doc.scan_status : null,
    document_uploaded_by_id: doc ? doc.uploaded_by_id : null,
    has_document: Boolean(row.has_document),
    directives: Array.isArray(row.directives) ? row.directives : [],
    events: Array.isArray(row.events) ? row.events : [],
  };
};

const directiveRecordRows = (rows) => (Array.isArray(rows) ? rows : []).map(directiveRecordRow);

/**
 * `Directives.GetDirectiveCounts`: main's `{counts: {status: n}, attention_patients}`.
 *
 * The statistics resource also carries `reauth_overdue` and
 * `attention_patients` beside the statuses; only the statuses are counts here,
 * and `attention_patients` — distinct patients awaiting authorization, sign-off
 * or re-authorization — is the nav badge's number.
 */
const directiveCounts = (stats) => {
  const counts = {};
  const source = (stats && (stats.by_status || stats)) || {};

  ['unconfirmed_authorization', 'pending_review', 'active', 'superseded', 'revoked', 'expired'].forEach((status) => {
    const n = Number(source[status]) || 0;
    if (n > 0) counts[status] = n;
  });

  return {
    status: 'Successful',
    counts,
    attention_patients: Number(stats && stats.attention_patients) || 0,
  };
};

/** Legacy patient grids key off `mid` / `pid` / `psersonalID`, not `id`. */
const patientRows = (rows) => (rows || []).map((row) => ({
  ...row,
  mid: row.id,
  pid: row.id,
  patient_id: row.id,
  // `personal_identifier` is not a field PatientResource emits — the legacy
  // `psersonalID` (sic) is the patient's USER id, which is what the grids match
  // appointments and group membership on.
  psersonalID: row.user_id,
  firstname: row.first_name,
  lastname: row.last_name,

  // The home grid's Phone Number, Status and Assignment Date columns. Without
  // these the grid renders three empty columns beside a populated name, which
  // reads as "this patient has no phone" rather than "this column is not wired".
  primaryphone: row.phone,
  contact_number: row.phone,
  // The Status cell branches on this: "pending" renders a button that reopens
  // the invitation, anything else renders as text. The state it is asking about
  // is the portal account's, not the chart's — a patient whose chart is active
  // but who has never accepted their invitation is exactly the case the button
  // exists for.
  accntstatus: portalStatus(row),
  activestatus: portalStatus(row),
  ...assignmentAliases(row),
}));

/**
 * The patient's portal account state, in the word the grid tests for.
 *
 * `users.status` uses the new enum; the grid compares lower-cased against the
 * single literal "pending". Both pre-acceptance states map onto it, because
 * both mean the same thing to the person looking at the column: this patient
 * cannot sign in yet.
 */
function portalStatus(row) {
  const status = String((row.portal && row.portal.status) || row.status || '');

  if (status === 'expired') return 'Expired';

  return ['invited', 'pending_verification'].includes(status) ? 'Pending' : status;
}

/**
 * A `users.status` in main's `account_status` words.
 *
 * The two pre-acceptance states are main's `Pending`; `expired` — an
 * invitation past its 30-day clock, which the API reports before the nightly
 * sweep has stored it — is main's `Expired`, the state a resend revives. The
 * grids and pendingAccountStatus.js key on exactly those two words, so an
 * expired patient stays gated (HomeGrid) and gets the Resend link (My Account)
 * rather than reading as a status nobody tests for. Everything else is
 * title-cased as the legacy column held it.
 */
function accountStatusLabel(status) {
  const value = String(status || '');

  if (value === 'expired') return 'Expired';
  if (['invited', 'pending_verification'].includes(value)) return 'Pending';

  return humanLabel(value);
}

/**
 * When this patient came onto the caller's caseload.
 *
 * Only available when the spec asked for `?include=care_team`. A patient can
 * have several assignments, so the caller's own is the one that answers "since
 * when have I been looking after them" — falling back to the primary-care
 * assignment, then the earliest, so a chart shared across a team still shows a
 * date rather than nothing.
 */
function assignmentAliases(row) {
  const team = Array.isArray(row.care_team) ? row.care_team : [];

  if (team.length === 0) return {};

  const me = currentUserId({});
  const active = team.filter((m) => m.assigned_at);

  const mine = (me !== null && active.find((m) => Number(m.user_id) === Number(me)))
    || active.find((m) => m.is_primary_care)
    || active[0]
    || null;

  if (!mine) return {};

  // The grid prints this straight into a cell, so it arrives formatted the way
  // the legacy column was stored — `2026-06-08 18:00:23` — rather than as the
  // ISO-8601 instant with its `T` and offset that the API correctly returns.
  const stamp = String(mine.assigned_at).replace('T', ' ').slice(0, 19);

  return { assignment_date: stamp, assignmentdate: stamp };
}

/**
 * An order, in the spellings the legacy grids bind.
 *
 * Five specs return orders and every one of them was a raw pass-through, so the
 * pending-orders grid rendered a full-width row of empty cells. Two of those
 * columns are not cosmetic: the grid sorts on `orderdate`, so an absent key left
 * the list in arbitrary order, and both Sign buttons key the popup on
 * `patientid`, so clicking them did nothing at all.
 *
 * `ordered_at` is one ISO-8601 instant; the legacy stored the date and the time
 * in separate columns and the grid still renders them in separate columns, so
 * it is split here rather than in six call sites.
 *
 * ## `ordernumber` is the routing key, `order_number` is the label
 *
 * The legacy had one value for both jobs, because `orders` was keyed on the
 * order number. The port is not: `{order}` binds `orders.id`, while
 * `order_number` is a zero-padded sequence allocated **per organization**
 * (`OrderNumbers.php`). This shaper used to prefer `order_number` for
 * `ordernumber`, and every per-order path is built from `p.ordernumber` — so a
 * grid row handed the display number back into `/orders/{id}` and, in any
 * database with a second tenant, addressed a *different* order that happened to
 * have that id. Signing and discontinuing are among those paths.
 *
 * So the two keys are separated at the source. Every legacy spelling of "the
 * order this row is about" — `ordernumber`, `order_number`, `orderid`,
 * `order_id` — now carries the **id the routes bind**, which is also what
 * `OrderTemp.InsertOrder`'s shape has always returned; the human-facing number
 * is emitted once, under the new and unambiguous `order_display_number`.
 *
 * `order_number` is deliberately on the routing side of that split even though
 * it is the API's own field name: every component that reads it reads it as a
 * target (`openOrderModal(med.order_number, …)`, `holdInfo.ordernumber`), and
 * `medicationRows` can only ever put the id there — `MedicationResource` emits
 * `order_id` and no number at all. One meaning for the key across both shapers
 * beats a field that routes on one row type and labels on the other.
 *
 * Components that *print* `ordernumber` (PendingOrderPopup's title, the Orders
 * page's fallback link text) show the surrogate id until they are moved to
 * `order_display_number`. That is a label regression, weighed against signing
 * the wrong order.
 */
const orderRows = (rows) => (rows || []).map((row) => {
  // The day and the time come from one clock, so the grid prints a moment
  // that happened. The API's pair is the organization's wall clock
  // (`ordered_on` + `ordered_time`), the clock the order was written on.
  // Without it, the UTC instant is split on the browser's clock — split at
  // its 'T', an order written at 21:30 in New York printed as the next day
  // at 01:30. The organization's day beside the browser's time printed, for
  // a viewer in another zone, a date and a time that never met.
  const local = row.ordered_at ? legacyDateTime(row.ordered_at) : null;
  const [datePart, timePart = ''] = row.ordered_on && row.ordered_time
    ? [row.ordered_on, String(row.ordered_time)]
    : (local && local.includes(' ') ? local.split(' ') : [row.ordered_on || '', '']);
  const med = Array.isArray(row.medications) ? row.medications[0] : null;
  const sig = row.signatures_summary || {};
  // A hold or discontinue order says 'Hold' / 'discontinue' where the rest say the ladder's word: main's order kept
  // that status through both signatures (api2/bridgeShapes.js ORDER_CHANGE_STATUS_WORDS).
  const statusWord = orderStatusWord(row.status, row.change_type);

  return {
    ...row,
    orderid: row.id,
    order_id: row.id,
    // Stringified because the legacy value was a varchar and call sites treat
    // it as one: HomeGrid sorts the pending queue with
    // `b.ordernumber.localeCompare(a.ordernumber)` (HomeGrid.vue:2375, :2428),
    // which throws on a number and takes the whole widget's assignment with it.
    // `orderRef` runs Number() over this again for routing, so the string costs
    // nothing on the way back out.
    ordernumber: String(row.id),
    order_number: String(row.id),
    order_display_number: row.order_number ?? row.id,
    patientid: row.patient_id,
    orderdate: datePart,
    orderDate: datePart,
    ordertime: timePart.slice(0, 8),
    orderTime: timePart.slice(0, 8),
    // Main's words, for every grid that shapes its rows here: 'Physicians
    // Order', 'Active-Pending', 'Hold' (api2/bridgeShapes.js orderStatusWord).
    // The API's own `order_type` and `status` are not the columns the grids
    // print.
    ordertype: orderTypeWord(row.order_type),
    orderType: orderTypeWord(row.order_type),
    procOrMed: row.order_category,
    orderdescription: row.description,
    status: statusWord,
    orderstatus: statusWord,
    order_status: statusWord,
    primary_physician: sigName(row.prescriber),

    // Both signatures are nested a level down. The grid does not read the
    // object — it reads the name, and treats a blank as "not yet signed", which
    // is why an unmapped key rendered a Sign button on an order that was
    // already signed.
    nursesignature: sigName(sig.nurse),
    nurseSignature: sigName(sig.nurse),
    providersignature: sigName(sig.provider),
    providerSignature: sigName(sig.provider),

    // Present only when the spec asked for `?include=medications`.
    ...(med ? {
      medication: med.name,
      med_medname: med.name,
      medname: med.name,
      medName: med.name,
      orderItemName: med.name,
      orderitemname: med.name,
      med_amount: med.dose_amount,
      med_doseuom: med.dose_unit,
      med_frequency: med.frequency_code,
    } : {}),
  };
});

/** A signature or prescriber block reduced to the name the grids print. */
function sigName(block) {
  if (!block) return '';

  return block.signature_name || block.name || '';
}

/**
 * The name the widget's Medication/Procedure column prints. Main's COALESCE
 * was medication, diagnosis, order description, then the category. The port
 * keeps a procedure's own name on its line (`orders` had no column for it), so
 * the procedure is named before the diagnosis it was ordered for.
 */
function orderWidgetItemName(row) {
  const text = (value) => String(value ?? '').trim();
  const first = (lines, label) => (Array.isArray(lines) ? lines : []).map(label).find(Boolean);

  return first(row.medications, (line) => text(line.name))
    || first(row.procedures, (line) => text(line.short_name) || text(line.description) || text(line.code))
    || first(row.diagnoses, (line) => text(diagnosisLabel(line.code, line.description)))
    || text(row.description)
    || text(row.order_category);
}

/**
 * An order list, as the home grid's order widget reads it: the columns of
 * main's NEW_OrderFilter (SqlClass.php), in its words, on top of the aliases
 * every order grid gets from `orderRows`.
 *
 * The widget prints `patient_first_name` / `patient_last_name`, `orderItemName`,
 * `orderDate`, `orderTime`, `orderType`, `status` and both signatures, and
 * HomeGrid opens a row through `pid`, `orderId`, `procOrMed` and `procOrMedId`
 * (it returns early, silently, without a patient or an order). Raw API rows
 * had none of the names, the item or the ids, so the widget showed a dash in
 * every column but one and Open did nothing. `orderType` and `status` are
 * main's words ('Physicians Order', 'Active-Pending'), which `orderRows` says
 * for every order grid, this one no differently.
 *
 * `orderId` is the id the routes bind, like `ordernumber` (see orderRows).
 * Main's `procOrMedId` mirrored the order number; here it is the id of the line
 * the row shows - the first medication or procedure - because that is what
 * GetOrderDetails looks a line up by, and an order id could name another line
 * of the same order.
 */
const orderWidgetRows = (rows) => orderRows(rows).map((row) => {
  const lines = row.order_category === 'procedure' ? row.procedures : row.medications;
  const line = Array.isArray(lines) && lines.length > 0 ? lines[0] : null;
  const patient = row.patient || {};
  const itemName = orderWidgetItemName(row);

  return {
    ...row,
    pid: row.patient_id,
    orderId: row.ordernumber,
    procOrMed: String(row.order_category || '').trim().toLowerCase(),
    procOrMedId: line ? line.id : row.id,
    patient_first_name: patient.first_name ?? '',
    patient_last_name: patient.last_name ?? '',
    orderItemName: itemName,
    orderitemname: itemName,
  };
});

/**
 * `OrderFilter.NEW_OrderLookUp`: whose orders is orderWidgetScope's - nobody's
 * for a patient session (no request), everyone's for an administrator (no
 * narrowing), and the viewer's own panel for anyone else
 * (`filter[assigned_to_me]`). The viewer is the token's, so `owner_unique_ID`
 * is not forwarded.
 *
 * Main had no LIMIT, so the read walks every page (wholeDirectory) in a total
 * order: orders written at the same minute tie on `ordered_on`, and the
 * organization's unique order number breaks the tie, so no order is read twice
 * or skipped between two pages.
 */
function orderWidgetLookup() {
  const read = {
    method: 'GET',
    path: () => '/orders',
    query: (p) => ({
      'filter[category]': orderWidgetCategory(p.ordtype),
      'filter[status]': orderWidgetStatus(p.orderstatus),
      'filter[assigned_to_me]': orderWidgetScope(p.emraccounttype) === 'panel' ? 1 : undefined,
      q: String(p.patientname || '').trim() || undefined,
      from: dateOnly(orderWidgetRange(p.srchdtrange)[0]),
      to: dateOnly(orderWidgetRange(p.srchdtrange)[1]),
      include: ORDER_WIDGET_INCLUDES,
      sort: '-ordered_on,-order_number',
      per_page: 100,
    }),
    paginated: true,
    shape: (page) => records(orderWidgetRows(page.items)),
  };
  const whole = wholeDirectory(read);

  return {
    ...read,
    // `local` so that a patient session sends nothing and the walk reads every
    // page; the path, query and shape are the ones above, read once.
    local: (p) => (orderWidgetScope(p.emraccounttype) === 'none' ? records([]) : whole.local(p)),
  };
}

// A medication row in the legacy spellings is `medicationRows` in
// bridgeShapes.js, exported so its aliases are tested (MED-15); its comments
// say why each alias exists.

/**
 * A note, in the legacy spellings.
 *
 * `NoteResource` splits the encounter into two ISO-8601 instants; the notes
 * screen renders a date column and a time column for each end, so the four keys
 * are derived here rather than in the template.
 */
const noteRows = (rows) => (rows || []).map((row) => {
  const [startDt, startTime] = splitInstant(row.start_at);
  const [endDt, endTime] = splitInstant(row.end_at);

  return {
    ...row,
    // The popup's words, which the notes screens filter and label on; the
    // API's slug stays as `note_type` (MX-07).
    type: noteTypeLabel(row.type),
    note_type: row.type,
    // Main's note id is `pid` (patientnotes.pid): its readers sort and key by
    // it, and its videos hang off it (#334). `attachments` passes through as
    // the API sends it (NoteAttachmentPresenter).
    pid: row.pid ?? row.id,
    postDate: row.posted_on,
    pnotes: row.body_html ?? row.body,
    // The author is nested; the list prints the name and branches on the role.
    name: row.author?.name ?? row.author_name ?? '',
    accountType: row.author?.role ?? row.author_role ?? '',
    startDt,
    startTime,
    endDt,
    endTime,
  };
});

/**
 * The order a chart's notes are walked in: main's (SqlClass GetAllNotes orders
 * by the visit's start, then its end, newest first - not by the day a note is
 * filed under), and a total one, the id last (allPages). Two notes of one
 * visit tie on its start and its end, and a tie on a page boundary may come
 * back in another order on the next page: a note read twice or not at all,
 * with the videos listed on it and the list row that audits them.
 */
const NOTE_WALK = { sort: '-start_at,-end_at,-id' };

/** `2026-08-25T14:30:00Z` -> `['2026-08-25', '14:30:00']`. */
function splitInstant(value) {
  const at = String(value || '');

  if (!at.includes('T')) return [at, ''];

  const [d, t] = at.split('T');

  return [d, t.slice(0, 8)];
}

/**
 * A diagnosis or procedure row, in the spellings the grid binds.
 *
 * Three screens filter these rows on `diagnosis_type === 'Diagnosis'` /
 * `'Procedure'` (CurrentDiag.vue:236, CurrentProc.vue:517,
 * PendingOrderPopup.vue:1717) because legacy `orders_diagnosis` held both kinds
 * behind that capitalised discriminator. The port split the table and says the
 * kind on the row as `category`, deliberately lower-case like every other enum
 * on the wire — so the capitalisation is the bridge's to restore, and without it
 * every filter dropped every row and the grids rendered empty over data that
 * had arrived.
 *
 * The code is nested now: `codes[]` is `{role, system, code, display}` and a
 * bare string is exactly what the port refused to keep emitting ("a code
 * without its system is not a code"). The grids print one, so the condition's
 * own code is flattened back out — falling back to `uncoded_text`, which is
 * where a term that matched no concept is kept.
 */
const diagnosisRows = (rows) => (rows || []).map((row) => ({
  ...row,
  diagnosis_type: legacyCategory(row.type ?? row.category),
  // Main's Current Diagnosis row stores the picker's label, `'I10 - Essential
  // hypertension'`, in this one column (keyon `insertChartDiagnosisFromOrder`),
  // and DiagnosisCombobox splits it back on `' - '` to offer the chart's own
  // diagnoses first and hide them from the ICD search. The bare code travels
  // separately as `code`, without the `ICD10:` storage prefix a migrated or
  // unresolved code carries in `uncoded_text`.
  code: stripCodeSystem(primaryCode(row)),
  diagnosis_code: diagnosisLabel(stripCodeSystem(primaryCode(row)), row.description),
  diagnose_code: diagnosisLabel(stripCodeSystem(primaryCode(row)), row.description),
  // Four resources, four spellings of the same two dates: the problem list has
  // `onset_date`/`abated_date`, procedure history `performed_from`/`performed_to`,
  // and the two order-line resources `onset_on`/`start_on` and `end_on`.
  diagnosis_date: row.diagnosed_on ?? row.onset_date ?? row.onset_on
    ?? row.performed_from ?? row.start_on,
  end_date: row.resolved_on ?? row.abated_date ?? row.end_on ?? row.performed_to,
  instruction: row.notes ?? row.instructions,
  writer: row.diagnosing_provider?.name
    ?? row.diagnosing_provider_name
    ?? row.performing_provider_name
    ?? '',
}));

/** `'diagnosis'` -> `'Diagnosis'`, which is the spelling the grid filters on. */
function legacyCategory(value) {
  const text = String(value ?? '');

  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** The row's own code, out of the `{role, system, code, display}` list. */
function primaryCode(row) {
  const codes = Array.isArray(row.codes) ? row.codes : [];
  // `role` separates a condition's own code from the allergen's; procedure and
  // immunization codes carry no role, so "no role" is the row's own code too.
  const own = codes.find((entry) => !entry.role || entry.role === 'condition') || codes[0];

  return (own && own.code) || row.uncoded_text || row.code;
}

/**
 * An appointment, in the legacy spellings.
 *
 * The scheduling UI reads a plain date and a plain time; `AppointmentResource`
 * emits `local_date` and `local_start_time`. Left unmapped, the Manage modal
 * threw on `date.split()` and opened with Save disabled — an appointment that
 * existed could not be viewed or edited.
 */
const appointmentRows = (rows) => (rows || []).map((row) => ({
  ...row,
  appointment_date: row.local_date,
  appointment_time: row.local_start_time,
  series_id: row.appointment_series_id,
  // The visit context's label (`In-Home`, `Telehealth`...), which is what
  // main's calendar wrote here and what it reads back: the Visit Details
  // "Context", MasterCalendar's telehealth styling (`=== 'Telehealth'`) and
  // the reschedule modals' locked context. The modality — what this used to
  // carry — showed `in_person` as a context and matched none of them. The
  // modality stays on the row as `modality`.
  meeting_type: (row.visit_context && row.visit_context.label) || row.modality,
  notify_email: row.notification_channel === 'email' || row.notification_channel === 'both' ? 1 : 0,
  notify_sms: row.notification_channel === 'sms' || row.notification_channel === 'both' ? 1 : 0,
}));

/**
 * How many patients one request of the calendars' read names. They travel in
 * the query string (main sent them in a POST body), and a proxy caps a request
 * line at about 8 KB: a roster of a thousand patients would fail outright, and
 * the call site swallows a failure as an empty calendar.
 */
const PATIENTS_PER_DIARY_READ = 100;

/**
 * The calendars' read of some patients' visits: the live ones (main's
 * `status != 'cancelled'`; the API's statuses are these three and cancelled),
 * with their visit context, newest first.
 *
 * Newest first because the walk is bounded (allPages stops at 200 pages of a
 * group, 20,000 visits) and the API's own order is oldest first: a read cut off
 * at the bound would lose the upcoming visits, which are what the calendars,
 * their conflict previews and a series' "this and following" are for. Cut off
 * newest first, it loses the oldest history instead. diaryOfPatients puts main's
 * order back.
 *
 * The id breaks ties. Visits that share a start time (every 09:00 visit of the
 * day) have no order of their own, and a database may order them differently
 * for page 1 and page 2, so a visit on a page boundary could be read twice or
 * not at all.
 */
const diaryQuery = (patientIds) => ({
  'filter[patient_id]': (patientIds || []).map(id).join(','),
  'filter[status]': 'pending,approved,completed',
  include: 'visit_context',
  sort: '-starts_at,-id',
  per_page: 100,
});

/**
 * Every live visit of these patients, in main's ORDER BY date, time. Each
 * group of patients is read to its last page (allPages), so no visit is left
 * behind a page boundary.
 */
async function diaryOfPatients(patientIds) {
  // Each patient once: one named in two groups would bring every one of their
  // visits twice.
  const ids = [...new Set((patientIds || []).map(String))];
  const visits = [];

  for (let from = 0; from < ids.length; from += PATIENTS_PER_DIARY_READ) {
    // eslint-disable-next-line no-await-in-loop
    visits.push(...await allPages('/appointments', diaryQuery(ids.slice(from, from + PATIENTS_PER_DIARY_READ))));
  }

  // ISO dates and times, so text order is calendar order.
  const compare = (a, b) => {
    const [x, y] = [String(a ?? ''), String(b ?? '')];
    if (x < y) return -1;
    return x > y ? 1 : 0;
  };

  return visits.sort((a, b) => compare(a.local_date, b.local_date)
    || compare(a.local_start_time, b.local_start_time)
    || Number(a.id) - Number(b.id));
}

/** One provider's pending and approved visits on one day, by a total order (Appointment.GetAppointmentsForProviderDate). */
const providerDayQuery = (p) => ({
  'filter[provider_id]': id(p.providerId),
  date: dateOnly(p.date),
  'filter[status]': 'pending,approved',
  sort: 'starts_at,id',
  per_page: 100,
});

/** The booked times in keyon's shape: each visit with its `appointment_time`. */
const providerDayShape = (page) => records(page.items.map((a) => ({ ...a, appointment_time: hhmm(a.local_start_time) })));

/**
 * The API's 422 `visit_conditional`, in keyon's appointment failure body
 * ({code: 0, error}) plus the one key the booking screens branch on. The rubric
 * allows the visit only once a person confirms it (`confirm_conditional`); the
 * calendar sends that when its own preview showed the verdict, and a screen
 * that showed none — the patient card has no rubric preview — learns it here
 * and asks before re-sending. A fixed body, because `refusals` are answered
 * as they stand, so the sentence is written here for the person reading it.
 */
const CONDITIONAL_VISIT_REFUSAL = Object.freeze({
  code: 0,
  status: 'Error',
  error: 'The visit compatibility rules allow this visit only conditionally. Confirm it to book it and take responsibility for that decision.',
  viability: 'conditional',
  data: null,
  results: [],
});

/**
 * The medication relations every legacy medication read asked for, spelt the
 * way `MedicationController`'s allow-list spells them.
 *
 * Verified against `app/Http/Controllers/Api/Medications/MedicationController.php`
 * — `includes: ['order', 'medicationAdministration', 'schedules', 'prescription']`
 * on the collection, the same plus `prescription.pharmacy` on the single read.
 * Spatie throws `InvalidIncludeQuery` (HTTP 400) on any name outside that list
 * and no `config/query-builder.php` disables it, so this string is load-bearing:
 * one wrong member fails the whole request.
 */
const MEDICATION_INCLUDES = 'order,medicationAdministration,schedules,prescription,release_order,hold_order';

/** A pharmacy, flattened out of its nested resource. */
const pharmacyRows = (rows) => (rows || []).map((row) => ({
  ...row,
  pharmacyname: row.pharmacy?.name ?? row.name,
  npinumber: row.pharmacy?.npi ?? row.npi,
  pharmaddress: row.pharmacy?.address_line1 ?? row.address_line1,
}));

export const KEYON_OPERATIONS = {
  // --- Organization ------------------------------------------------------
  'Account.GetOrganizationSnapshot': {
    method: 'GET',
    path: () => '/organization/snapshot',
    shape: (org) => dataEnvelope(org),
  },
  'AccountUser.UpdateAccountInfo': {
    // The org-level half of the Edit User flow; the person's own fields go to
    // `PATCH /users/{user}` (AccountUser.UpdateAccountUser) in the same save.
    method: 'PATCH',
    path: () => '/organization',
    body: (p) => ({
      phone: p.phone,
      address_line1: p.address,
      city: p.city,
      state: p.state,
      postal_code: p.zip,
    }),
    shape: () => okSuccessful(),
  },

  // --- Users and the staff roster ---------------------------------------
  //
  // Sixteen legacy reads collapse onto `GET /users`; each one kept its own
  // response key, so the difference between these entries is the envelope the
  // rows come back in, not the request.
  'Account.GetAccountUsers': {
    method: 'GET',
    path: () => '/users',
    query: () => ({ per_page: 100 }),
    paginated: true,
    shape: (page) => dataEnvelope(staffRows(page.items)),
  },
  'Account.GetPendingAccountInfo': {
    method: 'GET',
    path: () => '/users',
    // `users.status` has no member called `pending`
    // (2026_08_22_001500_create_users_table.php:91). The two pre-acceptance
    // states are `invited` and `pending_verification`, and `AllowedFilter::exact`
    // compares literally — so this asked for a value no row can hold and the
    // screen answered "No Records Found" every time, over however many accounts
    // were actually waiting. Comma lists are supported, and the bridge already
    // knows the mapping from the other direction (`portalStatus`).
    //
    // Not a cosmetic filter: this list is also how an administrator finds an
    // account whose invitation e-mail failed, so an always-empty one hides the
    // people who most need finding.
    //
    // `expired` too: main's GetPendingAccountInfo answers Expired for a pending
    // row past its invitation clock, and that person is exactly who an
    // administrator is looking for to resend. `account_status` is main's word
    // (Pending / Expired), from `staffRows`.
    query: (p) => ({
      'filter[status]': 'invited,pending_verification,expired',
      'filter[role]': p.accountype || undefined,
      per_page: 100,
    }),
    paginated: true,
    shape: (page) => (page.items.length
      ? dataEnvelope(staffRows(page.items))
      : okStatus('No Records Found')),
  },
  'AccountUser.SearchAccountForEdit': {
    // The Administration Add/Edit chooser and Edit User (ACC-22, FEM-60) read
    // main's merged record — uniqueID, accountType in main's words, foundIn,
    // and the role table's row — not a roster row, or Edit User could not
    // open straight onto the person picked (accountBridge.editRecord). The
    // chooser's `%` means everyone.
    method: 'GET',
    path: () => '/users',
    // `patient`: a patient's chart id, which Edit User saves the chart to.
    query: (p) => ({ q: editSearchTerm(p.searchTerm), include: 'licenses,documents,patient', per_page: 50 }),
    paginated: true,
    shape: (page) => records((page.items || []).map(editRecord)),
  },
  // `GetAccountNurse` was three reads behind one name, chosen by an `action`
  // string: the org roster, one patient's nurses, and one nurse's patients.
  // Two of them survive as call sites, and each reads the rows out of a
  // different key — so both keys carry the same rows.
  'NursyInfo.GetAccountNurse': {
    method: 'GET',
    path: (p) => (p.action === 'GetNursesAssigned'
      ? `/patients/${id(p.patientid)}/care-team`
      : '/users'),
    query: (p) => (p.action === 'GetNursesAssigned'
      ? { 'filter[role]': 'nurses' }
      : { 'filter[role]': 'registered_nurse,licensed_practical_nurse', include: 'licenses,licenses.nursys,documents', per_page: 100 }),
    // `/users` is paginated; `/patients/{id}/care-team` is not.
    paginated: (p) => p.action !== 'GetNursesAssigned',
    shape: (page, p) => {
      const rows = p.action === 'GetNursesAssigned'
        ? careTeamSlot(page, 'nurses')
        : staffRows(page.items);

      // `assignments` was a second query stapled onto the same answer; the
      // care-team endpoint carries it per row now, so an empty map is honest.
      return { nurseInfo: rows, records: rows, assignments: {} };
    },
  },
  'NursyInfo.GetAccountAids': {
    method: 'GET',
    path: () => '/users',
    query: () => ({ 'filter[role]': 'home_health_aid', include: 'licenses,licenses.nursys,documents', per_page: 100 }),
    paginated: true,
    shape: (page) => {
      const rows = staffRows(page.items);

      return { nurseInfo: rows, records: rows, assignments: {} };
    },
  },
  'NursyInfo.GetAccountAlliedProfessionals': {
    method: 'GET',
    path: () => '/users',
    query: () => ({ 'filter[role]': 'allied_professional', include: 'licenses,licenses.nursys,documents', per_page: 100 }),
    paginated: true,
    shape: (page) => ({ nurseInfo: staffRows(page.items) }),
  },
  'NursyInfo.GetAccountGeriatricCareManagers': {
    method: 'GET',
    path: () => '/users',
    query: () => ({ 'filter[role]': 'geriatric_care_manager', include: 'licenses,licenses.nursys,documents', per_page: 100 }),
    paginated: true,
    shape: (page) => ({ gcmInfo: staffRows(page.items) }),
  },
  'HomeAide.GetAccountAides': {
    method: 'GET',
    path: () => '/users',
    query: () => ({ 'filter[role]': 'home_health_aid', include: 'licenses,licenses.nursys,documents', per_page: 100 }),
    paginated: true,
    shape: (page) => records(staffRows(page.items)),
  },
  'NursyInfo.GetIndividualAccountNurse': {
    method: 'GET',
    path: () => '/users',
    query: (p) => ({ 'filter[license_number]': p.licensenum, 'filter[role]': 'registered_nurse' }),
    paginated: true,
    shape: (page) => records(staffRows(page.items)),
  },
  'NursyInfo.GetIndividualAccountAid': {
    method: 'GET',
    path: () => '/users',
    query: (p) => ({ 'filter[certificate_number]': p.certificatenumber, 'filter[role]': 'home_health_aid' }),
    paginated: true,
    shape: (page) => records(staffRows(page.items)),
  },
  'Account.GetAccountType': {
    // Two independent reads in one legacy answer: the caller's role and the
    // licence the order templates print. Both are on the user record now, and
    // `/me` carries the caller's active licences.
    method: 'GET',
    path: () => '/me',
    shape: (me) => {
      const user = me || {};
      const licenses = user.licenses || [];

      // The state professional licence is the one an order prints. NPI is a
      // registry number, not a licence to practise, and the legacy field was
      // filled from the state row.
      const professional = licenses.find((l) => l.type === 'state_professional')
        || licenses.find((l) => l.type !== 'npi')
        || null;

      return {
        // Main's words ('Administrator', 'Registered Nurse', 'Home Health
        // Aid'), as keyon answered (MX-14): main's screens compare them -
        // OrdersPage's and HomeGrid's "Administrators without an NPI" gate,
        // HomeGrid's staff-scoped view - and the slug matched none of them
        // where a word had a space. The slug stays beside it.
        providerAssignment: { accounttype: legacyAccountType(user.account_type), account_type: user.account_type || '', records: [] },
        professionalLicense: {
          license: (professional && professional.number) || '',
          state: (professional && professional.state) || '',
          // `records` is read as `records[0]` by the welcome grid. It used to be
          // absent from this shape, so that read threw
          // "Cannot read properties of undefined (reading '0')" during the
          // grid's mounted hook and took the whole page down.
          records: licenses.map((l) => ({
            license: l.number,
            licensenum: l.number,
            state: l.state,
            licensetype: l.type,
            profession: l.profession,
            expires: l.expires_on,
          })),
        },
      };
    },
  },
  // Main's My Profile and session identity (#315 via #321, MX-12/MX-13). keyon
  // read and wrote the session actor's own row and the body never named the
  // identity; here that is `/me`, answered for the token's subject, through
  // the one mapping in selfProfileShape.js that the sessionSelf store uses too.
  //
  // The page reads `/me/profile`, not `/me`: main's GetSelfProfile is behind a
  // live MFA session in every mode, and so is that route (`mfa`). Without a
  // recent second factor it answers 401 `mfa_required` and api2/client.js
  // sends the person to sign in again, as main sent them to the code page;
  // `/me` would answer the shell's document without the self-profile fields,
  // and the page would show an empty form to save over the real one.
  'AccountUser.GetSelfProfile': {
    method: 'GET',
    path: () => '/me/profile',
    shape: (me) => dataEnvelope(meToSelfProfile(me)),
  },
  'AccountUser.UpdateSelfProfile': {
    // Local, not a path: the body depends on whether the signed-in person is a
    // patient (gender is theirs alone), a refusal must keep its code and its
    // field reasons (selfProfileRefusal), and the saved profile replaces the
    // session's, so the account menu shows the new name without a reload.
    local: async (p) => {
      const { useSessionStore } = await import('@/stores/session');
      const session = useSessionStore();
      const isPatient = (session.me?.account_type ?? session.accountType) === 'patient';

      let me;
      try {
        me = await api.patch('/me', selfProfileToMePatch(p.profile, { isPatient }));
      } catch (error) {
        if (error instanceof ApiError) return selfProfileRefusal(error);
        throw error;
      }

      if (me && session.me && String(session.me.id) === String(me.id)) {
        session.$patch({ me: { ...session.me, ...me } });
      }

      return dataEnvelope(meToSelfProfile(me));
    },
  },
  'AccountUser.UpdateAccountUser': {
    // Edit User's profile save (Trello bMZnxWqc). Three things it used to get
    // wrong, each of which made every save fail or lose data:
    //  - it sent `role`, which PATCH /users prohibits (an account type is not
    //    an edit), so every save answered 422;
    //  - it sent the licence as `license_number`, which nothing accepts, so the
    //    licence was dropped; main's one `proffesionalLicense` field is the NPI
    //    for a Provider or Administrator and the state licence for everyone
    //    else, and goes to the field the API reads for that;
    //  - main's preference words are capitalised ('Email'); the API's are not.
    // `emrLicenseNumber` is the internal EMR licence the API mints itself, and
    // is not an edit. Sign-in identifiers are never in this body: they change
    // only through the credentials route.
    method: 'PATCH',
    path: (p) => `/users/${routeId(p.uniqueID)}`,
    body: (p) => ({
      first_name: p.firstname,
      last_name: p.lastname,
      phone_primary: p.phone,
      address_line1: p.address,
      city: p.city,
      state: p.state,
      postal_code: p.zip,
      ...editedLicence(p),
      communication_preference: p.communicationPreference
        ? String(p.communicationPreference).toLowerCase()
        : undefined,
    }),
    shape: () => okStatus('Successfull'),
    errorStyle: 'message',
  },
  'Account.UpdatePendingAccountInfo': {
    method: 'PATCH',
    path: (p) => `/users/${id(p.uniqueNine)}`,
    body: (p) => ({ status: (p.Accountstatus || '').toLowerCase() }),
    shape: () => okMessage('Updated'),
    errorStyle: 'message',
  },

  // Main's pending_group_assignments: patients held for a caregiver until they
  // verify (#280). All three are `groups:admin` (main's privilege 5), the read
  // included. The organization and the assigning user come from the token, so
  // `accountnumber` and `assigned_by_unique_ID` are not forwarded; neither is
  // `staff_account_type` — the role is the caregiver's own account type.
  'Account.GetPendingGroupAssignments': {
    method: 'GET',
    path: (p) => `/users/${id(p.staff_unique_ID)}/pending-patients`,
    paginated: true,
    shape: (page, p) => okStatus('Successful', {
      data: (page.items || []).map((row) => ({
        id: row.assignment_id,
        staff_unique_ID: p.staff_unique_ID,
        patient_unique_ID: row.patient_id,
        staff_account_type: row.caregiver_role,
        created_at: row.assigned_at,
      })),
    }),
  },
  'Account.SavePendingGroupAssignments': {
    method: 'PUT',
    path: (p) => `/users/${id(p.staff_unique_ID)}/pending-patients`,
    body: (p) => ({ patient_ids: (Array.isArray(p.patient_unique_IDs) ? p.patient_unique_IDs : []).map(id) }),
    shape: () => okStatus('Successful', { message: 'Pending group assignments saved' }),
  },
  'Account.PromotePendingGroupAssignments': {
    method: 'POST',
    path: (p) => `/users/${id(p.staff_unique_ID)}/pending-patients/promotion`,
    // Main's vocabulary — Successful, Partial, Failure — with per-row failures.
    shape: (result) => (result && result.status === 'Nothing'
      ? okStatus('Successful', { message: 'No pending group assignments to promote', promoted: 0 })
      : okStatus(result ? result.status : 'Failure', {
        promoted: result ? (result.promoted || []).length : 0,
        failed: result ? result.failed || [] : [],
      })),
  },
  'Account.UpdatePendingAccountUsername': {
    method: 'PATCH',
    path: (p) => `/users/${id(p.uniqueNine)}/credentials`,
    body: (p) => ({ username: p.username }),
    shape: () => okStatus('200', { message: 'Updated' }),
    errorStyle: 'message',
  },
  // Main's pending-invite address correction (the My Account Resend Invitation
  // modal): only while the person is Pending or Expired, 409 on an address
  // another account holds, and — the port's addition — the link already mailed
  // to the wrong address dies before this returns. Not the general
  // `/credentials` route, which would move an Active person's sign-in address
  // from a modal meant for invitees.
  'Account.UpdatePendingAccountEmail': {
    method: 'PATCH',
    path: (p) => `/users/${id(p.unique_ID || p.uniqueNine)}/invitation`,
    body: (p) => ({ email: p.email }),
    shape: () => okStatus('200', { message: 'Updated' }),
    errorStyle: 'message',
  },
  // The Account Access panel of Edit User (Trello bMZnxWqc). Both act on the
  // person being EDITED — AddUserForm names them `targetUniqueID` — and never
  // on `actorId`, the administrator at the keyboard, which they used to fall
  // back to: the panel then loaded the administrator's own sign-in details
  // and saved the edit onto the administrator's own account. No target is no
  // request (the unresolved segment), never the actor in its place.
  'Account.GetAccountLoginIdentity': {
    method: 'GET',
    path: (p) => `/users/${routeId(p.targetUniqueID)}/credentials`,
    shape: (creds) => okStatus('200', {
      exists: !!creds,
      username: creds && creds.username,
      email: creds && creds.email,
    }),
  },
  'Account.AdminUpdateAccountCredentials': {
    // Legacy wrote MySQL and PostgreSQL in one call and reported each half in
    // `results`; there is one store now, so there is one result. A password is
    // never sent: an administrator never sets or sees one, and main's
    // `newPassword` (forwarded to an unauthenticated pacmny-be action) is how
    // any holder of an administrator's id took over any account. The reset is
    // a link pacmny-auth mails (`send_reset_invite`).
    method: 'PATCH',
    path: (p) => `/users/${routeId(p.targetUniqueID)}/credentials`,
    body: (p) => ({
      username: p.username || undefined,
      email: p.email || undefined,
      send_reset_invite: bool(p.sendResetInvite),
    }),
    shape: () => okStatus('200', { results: { api: { status: '200' } } }),
    errorStyle: 'status',
  },

  // Identity-uniqueness probes. Four legacy names, one endpoint (row 33); each
  // reads its answer out of a differently-named boolean.
  'Account.IsNpiExist': {
    method: 'GET',
    path: () => '/users/availability',
    query: (p) => ({ npi: p.npi }),
    shape: (result) => ({ isNpiExist: !(result && result.available) }),
  },
  'Account.IsAccountUserNpiExist': {
    method: 'GET',
    path: () => '/users/availability',
    query: (p) => ({ npi: p.npi }),
    shape: (result) => ({ exists: !(result && result.available) }),
  },
  'Account.IsAccountUserEmailExist': {
    method: 'GET',
    path: () => '/users/availability',
    query: (p) => ({ email: p.email }),
    shape: (result) => ({ exists: !(result && result.available) }),
  },
  'Account.IsRoleIdentifierExist': {
    method: 'GET',
    path: () => '/users/availability',
    query: (p) => ({
      license: p.license || undefined,
      license_state: p.licenseState || undefined,
      certificate: p.certificate || undefined,
      role: p.accountType || undefined,
    }),
    shape: (result) => ({ exists: !(result && result.available) }),
  },

  'MedicationAdmin.VerifyAccountSignature': {
    // The signing pre-check main's pad asks before it opens (#244): may the
    // signed-in person sign, and if not, main's one specific sentence. Only a
    // Registered Nurse (nurse half) and a Provider (provider half) may; the pad
    // compares `accountType` against exactly those legacy labels. This used to
    // answer code '1' for everyone with the licence suffix (`'RN'`) as the
    // account type, so the pad refused every signer, and it read the account
    // to check out of the body — `/me/order-signature` asks only about the
    // caller, and needs no `users:read`.
    method: 'GET',
    path: () => '/me/order-signature',
    shape: (signer) => signerShape(signer),
  },
  'Directives.GetDirectiveSignerCredential': {
    // Display only. The sign-off gate itself moved server-side (row 171), so a
    // client that lies about the licence gets a 403 from the signature call.
    method: 'GET',
    path: (p) => `/users/${id(p.unique_ID)}/signature`,
    shape: (sig) => dataEnvelope({ professional_license: (sig && sig.professional_license) || '' }),
  },

  // --- Patients ----------------------------------------------------------
  'NursyInfo.GetAccountPatients': {
    method: 'GET',
    path: () => '/patients',
    query: (p) => ({ 'filter[assigned_user_id]': id(p.nurseid) || undefined, per_page: 100 }),
    paginated: true,
    shape: (page) => ({ patientInfo: patientRows(page.items) }),
  },
  'ProviderInfo.GetProviderAccountPatients': {
    method: 'GET',
    path: (p) => `/users/${id(p.providerid)}/patients`,
    paginated: true,
    shape: (page) => ({ patientInfo: patientRows(page.items) }),
  },
  'Patients.GetPatientsFromStaffPatientsGroup': {
    method: 'GET',
    path: (p) => `/users/${id(p.staff_unique_ID)}/patients`,
    paginated: true,
    shape: (page) => ({ patients: patientRows(page.items) }),
  },
  'GetPatientInfo.GetPatientInfoByID': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}`,
    shape: (patient) => records(patientRows([patient])),
  },
  'GetPatientInfo.GetCurrentPatientData': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}`,
    shape: (patient) => ({ patientInfo: patientRows([patient]) }),
  },
  'AddPatientInfo.AddPatient': {
    method: 'POST',
    path: () => '/patients',
    shape: (patient) => okCode1(patient && patient.id),
    errorStyle: 'code',
  },
  'UpdatePatientData.Upate_Patients': {
    // Legacy misspelling preserved as the key only; the endpoint is spelled correctly.
    method: 'PATCH',
    path: (p) => `/patients/${id(p.patientid)}`,
    body: (p) => ({
      // No `personal_identifier` from `p.personalID`: main's personalID is
      // the person's own id (psersonalID - Edit User sends the user id, the
      // patient card the same), which the API would have written over the
      // chart's medical record number.
      first_name: p.firstname,
      last_name: p.lastname,
      // Main's form codes (1, 2) and words ('Male') as the API's enum; a
      // blank one is no change.
      gender: patientGender(p.gender),
      address_line1: p.address,
      city: p.city,
      state: p.state,
      postal_code: p.zip,
      emergency_contact_name: p.emergencycontact,
      // The API's enum (`married`, `domestic_partner`); blank is no change -
      // sent as '' it became NULL, which the enum refuses with a 422.
      marital_status: String(p.maritalstatus ?? '').trim() === ''
        ? undefined
        : String(p.maritalstatus).trim().toLowerCase().replace(/\s+/g, '_'),
      // Absent is no change; empty is a clear. Edit User leaves it out for a
      // person with no date of birth on record, rather than send its
      // placeholder or a null for nothing (AddUserForm.editedDob).
      dob: p.dob === undefined ? undefined : dateOnly(p.dob),
      phone_primary: p.phone,
      email: p.email,
    }),
    shape: () => okMessage('Updated'),
    errorStyle: 'message',
  },

  // --- Care team ---------------------------------------------------------
  //
  // Six legacy operations in two directions ("assign patient to nurse" and
  // "assign nurse to patient") over one relationship.
  'NursyInfo.GetPatientNurse': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientnumber)}/care-team`,
    query: () => ({ 'filter[role]': 'nurses' }),
    shape: (team) => careTeamSlot(team, 'nurses'),
  },
  'Provider.GetProvidersFromPatientProvidersGroup': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patient_unique_ID)}/care-team`,
    query: () => ({ 'filter[role]': 'providers' }),
    shape: (team) => ({ provider: careTeamSlot(team, 'providers') }),
  },
  'Provider.GetAllProviders': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/care-team`,
    query: () => ({ 'filter[role]': 'providers' }),
    shape: (team) => ({ provider: careTeamSlot(team, 'providers') }),
  },
  'Messages.GetPatientTeamForMessaging': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patient_unique_ID)}/care-team`,
    query: () => ({ view: 'messaging' }),
    shape: (team) => dataEnvelope(careTeamGroups(team)),
  },
  'NursyInfo.AssigNurseToPatient': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patientid)}/care-team/nurses`,
    body: (p) => ({ user_id: id((p.nurseInfo && p.nurseInfo[0] && p.nurseInfo[0].nurseid) || p.nurseid) }),
    shape: () => okMessage('Nurse Assigned Successfully', { nurseInfo: [] }),
    errorStyle: 'message',
  },
  'NursyInfo.AssignPatientToNurse': {
    // Inverse direction: one nurse, many patients — which is row 40, not 76.
    method: 'PUT',
    path: (p) => `/users/${id(p.nurseid)}/patients`,
    body: (p) => ({ patient_ids: (p.patientInfo || []).map((row) => id(row.pid || row.patientid || row)) }),
    shape: (result, p) => okMessage('Patient Assigned Successfully', { patientInfo: p.patientInfo || [] }),
    errorStyle: 'message',
  },
  'ProviderInfo.AssignPatientToProvider': {
    method: 'PUT',
    path: (p) => `/users/${id(p.providerid)}/patients`,
    body: (p) => ({ patient_ids: (p.patientInfo || []).map((row) => id(row.pid || row.patientid || row)) }),
    shape: (result, p) => okMessage('Patient Assigned Successfully', { patientInfo: p.patientInfo || [] }),
    errorStyle: 'message',
  },
  'Provider.UpdatePatientProvidersGroupAccountUser': {
    // The route binds a `CareAssignment` by its own primary key; the legacy
    // payload names the member by *user* id, because keyon's
    // `providerassignment` rows had no identity the client ever saw. The two
    // are looked up against each other rather than the user id being fed into
    // the `{member}` position, where it 404s — or, when the numbers happen to
    // collide inside the tenant, silently edits a different membership.
    //
    // `local` because that is two round trips. The alternative was a path that
    // pretends one request can do it, which is what was here.
    local: async (p) => {
      const patient = routeId(p.patient_unique_ID || p.patientid);

      if (patient === null) {
        throw new ApiError({
          status: 0,
          code: 'bridge_unresolved_target',
          message: 'This provider could not be updated: no chart was identified.',
        });
      }

      const team = await api.get(`/patients/${patient}/care-team`);
      const member = careTeamAssignmentId(team, p.member_unique_ID);

      if (member === null) {
        // Not shaped as a success: the edit was not written, and the roster the
        // form was opened from is stale.
        throw new ApiError({
          status: 404,
          code: 'care_team_member_not_found',
          message: 'That provider is no longer on this patient’s care team. Reload the list and try again.',
        });
      }

      await api.patch(`/patients/${patient}/care-team/${member}`, {
        first_name: p.firstname,
        last_name: p.lastname,
        address_line1: p.address,
        city: p.city,
        state: p.state,
        postal_code: p.zip,
        phone: p.phone,
        cell: p.cell,
        email: p.email,
        // `npi` is deliberately NOT sent. `UpdateCareTeamMemberRequest` rules it
        // `prohibited`, so forwarding the form's NPI box 422'd every save — and
        // the rule is the point: an NPI is a credential with a verification
        // trail in `user_licenses`, and accepting it as free text on an
        // assignment is what let a client-supplied value satisfy the legacy
        // directive sign-off gate (SEC-32, finding 12).
      });

      return okMsg('Updated');
    },
    errorStyle: 'msg',
  },
  'NursyInfo.DischargePatientFromCard': {
    method: 'DELETE',
    path: (p) => `/patients/${id(p.patientid)}/care-team/nurses/${id(p.providerid || p.subaccount)}`,
    shape: (result, p) => [p.patientid],
  },
  'NursyInfo.RemoveMemberFromPatientNursesGroup': {
    method: 'DELETE',
    path: (p) => `/patients/${id(p.patient_unique_ID || p.patientid)}/care-team/nurses/${id(p.member_unique_ID)}`,
    shape: () => okStatus('Deleted'),
    errorStyle: 'status',
  },
  'Provider.RemoveMemberFromPatientProvidersGroup': {
    method: 'DELETE',
    path: (p) => `/patients/${id(p.patient_unique_ID || p.patientid)}/care-team/providers/${id(p.member_unique_ID)}`,
    shape: () => okStatus('Deleted'),
    errorStyle: 'status',
  },

  // --- Groups ------------------------------------------------------------
  // Main's GetGroupsByAccount: the organization's own groups (no owner), every
  // one. The Privileges, Groups and home pages find Patients, Nurses and the
  // rest by name in this list. Unfiltered and one page, it was the first 100
  // of every group by name, patient circles included, and an organization with
  // about a hundred patient-owned groups never saw its staff groups at all.
  // `name,id`: a stable order, so no row moves between the pages read.
  'Groups.GetGroupsByAccount': {
    local: async () => dataEnvelope(await allPages('/groups', { 'filter[org_level]': 1, sort: 'name,id' })),
  },
  'Groups.GetGroupsByOwner': {
    method: 'GET',
    path: () => '/groups',
    query: (p) => ({ 'filter[owner_id]': id(p.owner_unique_ID), per_page: 100 }),
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  // Main's GetGroupMembers: the whole group. The Privileges and Groups pages
  // build their trees from it and have no other way to reach a person, so the
  // first page of 25 left everyone after it unreachable, with nothing on
  // screen to say so. A stable order (`id` breaks name ties), so no one moves
  // between the pages read. No group in view is no members, and no request.
  'Groups.GetGroupMembers': {
    local: async (p) => {
      const group = id(p.group_id);

      if (!group) return dataEnvelope([]);

      return dataEnvelope(staffRows(await allPages(`/groups/${group}/members`, { sort: 'last_name,first_name,id' })));
    },
  },
  'Groups.GetPatientsForHomeGrid': {
    // The Patients group, read as people rather than as membership rows.
    method: 'GET',
    path: () => '/patients',
    query: (p) => ({
      'filter[assigned_user_id]': id(p.owner_unique_ID) || undefined,
      // The Assignment Date column. `assigned_at` lives on the care
      // assignment, not the patient, so the relation has to be asked for.
      include: 'care_team',
      per_page: 100,
    }),
    paginated: true,
    shape: (page) => dataEnvelope(patientRows(page.items)),
  },
  'Groups.CreateGroup': {
    method: 'POST',
    path: () => '/groups',
    body: (p) => ({
      name: p.name,
      parent_group_id: id(p.parent_group_id),
      owner_user_id: id(p.owner_unique_ID),
      type: p.type,
    }),
    shape: (group) => okStatus('Successful', { group_id: group && group.id }),
  },
  'Groups.UpdateGroup': {
    method: 'PATCH',
    path: (p) => `/groups/${id(p.group_id)}`,
    body: (p) => ({ name: p.name, parent_group_id: id(p.parent_group_id) }),
    shape: () => okStatus('Successful'),
  },
  'Groups.DeleteGroup': {
    method: 'DELETE',
    path: (p) => `/groups/${id(p.group_id)}`,
    shape: () => okStatus('Successful'),
  },
  'Groups.AddUserToGroup': {
    method: 'POST',
    path: (p) => `/groups/${id(p.group_id)}/members`,
    body: (p) => ({ user_id: id(p.unique_ID) }),
    shape: () => okStatus('Successful'),
  },
  'Groups.RemoveUserFromGroup': {
    method: 'DELETE',
    path: (p) => `/groups/${id(p.group_id)}/members/${id(p.unique_ID)}`,
    shape: () => okStatus('Successful'),
  },

  // Group-based assignment. The legacy names encode the role in the method
  // name; the role is a path segment now, so these differ only in which key
  // carries the subject.
  'Groups.SetNursesForPatient_GroupBased': {
    method: 'PUT',
    path: (p) => `/patients/${id(p.patient_unique_ID)}/care-team/nurses`,
    body: (p) => ({ user_ids: (p.nurse_unique_IDs || []).map(id) }),
    shape: () => okStatus('Successful'),
  },
  'Groups.SetProvidersForPatient_GroupBased': {
    method: 'PUT',
    path: (p) => `/patients/${id(p.patient_unique_ID)}/care-team/providers`,
    body: (p) => ({ user_ids: (p.provider_unique_IDs || []).map(id) }),
    shape: () => okStatus('Successful'),
  },
  'Groups.AddProviderForPatient_GroupBased': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patient_unique_ID)}/care-team/providers`,
    body: (p) => ({ user_id: id(p.provider_unique_ID) }),
    shape: () => okStatus('Successful'),
  },
  'Groups.SetPatientsForNurse_GroupBased': {
    method: 'PUT',
    path: (p) => `/users/${id(p.nurse_unique_ID)}/patients`,
    body: (p) => ({ patient_ids: (p.patient_unique_IDs || []).map(id) }),
    shape: () => okStatus('Successful'),
  },
  'Groups.SetPatientsForProvider_GroupBased': {
    method: 'PUT',
    path: (p) => `/users/${id(p.provider_unique_ID)}/patients`,
    body: (p) => ({ patient_ids: (p.patient_unique_IDs || []).map(id) }),
    shape: () => okStatus('Successful'),
  },
  'Groups.SetPatientsForHomeHealthAids_GroupBased': {
    method: 'PUT',
    path: (p) => `/users/${id(p.hha_unique_ID)}/patients`,
    body: (p) => ({ patient_ids: (p.patient_unique_IDs || []).map(id) }),
    shape: () => okStatus('Successful'),
  },
  // Main's #280 branches (keyon `SetPatientsForStaffRoleGroup` with the
  // "Allied Professionals" / "Geriatric Care Managers" role groups), gated like
  // their siblings on privilege 5 — `groups:admin` on the route. Replace
  // semantics: unlisted patients leave the panel. A caregiver still invited has
  // the panel held until they verify (`PendingCareAssignments`). A non-array
  // list is an empty panel, as main coerces it. HomeGrid alerts
  // `res.data.message` on anything but Successful, so the refusal's sentence
  // travels in `message` (the default error style).
  'Groups.SetPatientsForAlliedProfessional_GroupBased': {
    method: 'PUT',
    path: (p) => `/users/${id(p.allied_professional_unique_ID)}/patients`,
    body: (p) => ({ patient_ids: (Array.isArray(p.patient_unique_IDs) ? p.patient_unique_IDs : []).map(id) }),
    shape: () => okStatus('Successful', { message: 'Patients updated for Allied Professional' }),
  },
  'Groups.SetPatientsForGeriatricCareManager_GroupBased': {
    method: 'PUT',
    path: (p) => `/users/${id(p.gcm_unique_ID)}/patients`,
    body: (p) => ({ patient_ids: (Array.isArray(p.patient_unique_IDs) ? p.patient_unique_IDs : []).map(id) }),
    shape: () => okStatus('Successful', { message: 'Patients updated for Geriatric Care Manager' }),
  },

  // --- Privileges and break-glass ---------------------------------------
  'Privileges.GetPrivilegesByAccount': {
    method: 'GET',
    path: () => '/privileges',
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  // Main's PrivilegesPage, both views. The read-only "My Privileges" view
  // (#284) asks for the caller's own: that is `/me/privilege-assignments`,
  // which takes no subject (the token names it), so none is sent. A read
  // naming anyone else is row 53, privileges:admin, whoever asks: a caller
  // without it is refused there rather than shown their own privileges under
  // that person's name.
  // Either answer is reshaped to the fields the page reads (effective_granted,
  // user_override, group_granted, inherited_from_groups); see privilegesBridge.
  'Privileges.GetPrivilegeAssignmentsForSubject': {
    method: 'GET',
    path: (p) => (readsOwnPrivileges(p) ? '/me/privilege-assignments' : '/privilege-assignments'),
    query: (p) => (readsOwnPrivileges(p) ? {} : {
      subject_type: p.subject_type || 'user',
      subject_id: id(privilegeSubjectUser(p) || p.group_id),
    }),
    paginated: true,
    shape: (page, p) => dataEnvelope(legacyAssignmentRows(page.items, (p && p.subject_type) || 'user')),
  },
  'Privileges.SavePrivilegeGrant': {
    // `{privilege}` binds a `Permission` by its **code** — `Permission::
    // getRouteKeyName()` returns 'code' — and the catalogue read that fills the
    // page (`PermissionResource`) emits no `id` at all. Wrapping the segment in
    // `id()` made it `Number(code)` = `NaN`, so the assignment could never
    // resolve even when a value did arrive. The code goes through as text, and
    // PrivilegesPage sends it as `privilege_code` (privilegeCodeOf).
    method: 'PUT',
    path: (p) => `/privilege-assignments/${p.subject_type || 'user'}`
      + `/${id(p.unique_ID || p.group_id)}`
      // `null` rather than an empty segment so the unresolved-target guard sees
      // it and the save fails loudly instead of PUTting to a collection.
      + `/${encodeURIComponent(p.privilege_code || p.code || p.privilege_id || null)}`,
    // `null` is "inherit" — the third state the legacy `user_mode` carried and
    // the reason this is not a plain boolean.
    body: (p) => ({
      granted: p.user_mode === undefined
        ? bool(p.granted)
        : ({ grant: true, deny: false }[p.user_mode] ?? null),
    }),
    shape: () => okStatus('Successful'),
  },
  // Main's Privileges Audit Report (#227): the whole-organization resolver
  // payload the Privilege Reports page builds its workbook and JSON from,
  // under privileges:report. The body's accountnumber and actor_unique_ID are
  // not sent: the organization and the actor are the token's. Row 56's
  // paginated holder list stays at /privilege-report (api2/permissions.js).
  'Privileges.GetPrivilegeReport': {
    method: 'GET',
    path: () => '/privilege-report/export',
    shape: (report) => dataEnvelope(privilegeReportFromExport(report)),
  },
  'Privileges.GetBreakGlassStatuses': {
    method: 'GET',
    path: () => '/break-glass-incidents',
    // In effect NOW, asked of the server (`filter[effective]`), not `status`:
    // an incident past its expiry is over even before anyone relabels it, and
    // filtering on the label let lapsed rows fill the one page this reads.
    query: () => ({ 'filter[effective]': 1, per_page: 100 }),
    paginated: true,
    // Main's map (processClass GetBreakGlassStatuses): user id =>
    // {is_active, incident}, which BreakGlassPage reads as `.is_active`
    // (`is_effective` still decides it, in case the clock ran out between the
    // query and the answer). The incident itself rides along so the page can
    // approve, extend or revoke it by its own id.
    shape: (page) => dataEnvelope((page.items || []).reduce((acc, incident) => {
      const key = incident.target_user_id;
      if (!acc[key] || !acc[key].is_active) {
        acc[key] = { is_active: Boolean(incident.is_effective), incident };
      }
      return acc;
    }, {})),
  },
  'Privileges.StartBreakGlass': {
    // Local, because it refuses before sending anything (MX-02 (5)): the
    // justification is the person's own words or nothing. A canned sentence
    // used to stand in for it, so every incident's compliance record read
    // "activated from Administration". The API refuses a missing reason too;
    // this answers in the page's own envelope without a round trip.
    //
    // `patient_id` when the caller names a chart scopes the incident to it
    // (decision D8); without one it is organization-wide, opens at once and
    // waits for a second administrator's approval, which that administrator
    // gives in their own request — never by being named here.
    errorStyle: 'status',
    local: async (p) => {
      const reason = String(p.reason || '').trim();

      if (reason.length < 10) {
        return {
          status: 'Failure',
          message: 'Type the reason for emergency access (at least 10 characters). It is the compliance record.',
        };
      }

      const incident = await api.post('/break-glass-incidents', {
        target_user_id: id(p.target_unique_ID),
        reason,
        ...(p.patient_id || p.patientid ? { patient_id: id(p.patient_id || p.patientid) } : {}),
        ...(p.reason_code ? { reason_code: p.reason_code } : {}),
        ...(p.duration_minutes ? { duration_minutes: Number(p.duration_minutes) } : {}),
      });

      return okStatus('Successful', { incident_id: incident && incident.public_id });
    },
  },
  'Privileges.EndBreakGlass': {
    // Main ends break-glass by naming the USER (target_unique_ID); the API
    // revokes an INCIDENT, by its public id. So this finds the user's open
    // incident first and revokes that. A user id is never put where an
    // incident id goes: route binding read a number as the incident's own
    // key, which 404'd or — worse — revoked whichever incident happened to
    // have that number.
    errorStyle: 'status',
    local: async (p) => {
      let incidentId = typeof p.incident_id === 'string' && p.incident_id ? p.incident_id : null;

      if (!incidentId) {
        // The one in effect now. One that only lapsed on the clock is not
        // revoked: it ended at its expiry, and the server records it so.
        const page = await api.list('/break-glass-incidents', {
          'filter[effective]': 1,
          'filter[target_user_id]': id(p.target_unique_ID),
          per_page: 100,
        });
        const chosen = (page.items || []).find((incident) => incident.is_effective);

        if (!chosen) {
          return { status: 'Failure', message: 'This user has no active break-glass incident to revoke.' };
        }

        incidentId = chosen.public_id;
      }

      await api.post(`/break-glass-incidents/${encodeURIComponent(incidentId)}/revocation`, {
        reason: String(p.reason || '').trim() || 'Revoked from Administration.',
      });

      return okStatus('Successful', { incident_id: incidentId });
    },
  },

  // --- Patient contacts and devices -------------------------------------
  'Contact.InsertContact': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patientid || p.subaccount)}/contacts`,
    body: (p) => ({
      first_name: p.firstname,
      last_name: p.lastname,
      email: p.email,
      phone_primary: p.phone,
      address_line1: p.address,
      address_line2: p.address2,
      city: p.city,
      state: p.state,
      postal_code: p.zip,
      dob: dateOnly(p.dob),
      notes: p.notes,
    }),
    shape: () => okStatus('Successfull'),
  },
  // Main's relational contact store (#260): PatientContacts.<Meth> from
  // src/api/patientContactsApi.js, answered in main's shapes. The chart is the
  // patient each call NAMES (AddUserForm writes for a patient other than the
  // open chart), a contact is addressed only on its own chart, and nothing
  // naming a chart, login, tenant or SSN goes in a body (contactBridge.js).
  // Each answers main's {status:'Error', message} on a refusal, which is what
  // patientContactsApi turns into the [message] its callers alert.
  'PatientContacts.GetPatientContacts': {
    local: getPatientContactsLegacy,
  },
  'PatientContacts.SavePatientContact': {
    local: savePatientContactLegacy,
  },
  'PatientContacts.CreatePatientContact': {
    local: savePatientContactLegacy,
  },
  'PatientContacts.UpdatePatientContact': {
    local: savePatientContactLegacy,
  },
  'PatientContacts.DeletePatientContact': {
    local: deletePatientContactLegacy,
  },
  'PatientContacts.SetPrimaryContact': {
    local: setPrimaryContactLegacy,
  },
  'AddPatientInfo.AddPatientInformation': {
    // Named for what it did in keyon (enrol the patient with Fora) rather than
    // for what it is: registering a meter. The catalogue points row `:317` at
    // `POST /patients`, but the patient already exists at this point in the
    // flow — the only new record is the device, so this lands on row 86 and
    // the caller's `ReCode == '00'` branch runs exactly once.
    //
    // `source` is the data source's code, `fora_device`: the 'fora' this
    // sent before matched no data source and every registration was a 422.
    // Fora's own half (A0001, or A0002 for a patient Fora has) runs on the
    // server's queue; '00' here means the meter is registered to the chart,
    // and the device list shows how Fora answered.
    //
    // A local operation so a refusal (a blank serial, a meter still being
    // removed from Fora for another chart) is answered in the shape the page
    // reads, {ReCode, ReMsg, errorMessage}: the bridge's generic failure body
    // has no ReCode, and the page's `ReCode[0]` threw on it, uncaught.
    local: registerForaDeviceLegacy,
  },
  'AddPatientInfo.AddPatientDevice': {
    local: registerForaDeviceLegacy,
  },
  'RemovePatientInfo.RemovePatientDevice': {
    // By serial, as the page names the meter: putting the serial where the
    // device id goes made every removal a 404 (deviceBridge). The caller
    // reloads when `ReMsg` is absent, which is the success path.
    local: removePatientDeviceBySerial,
  },
  'GetPatientInfo.GetNebulizerData': {
    // The chart's sessions, as main's GetNeb read them — not one device's
    // readings filtered on a type the API never stored (deviceBridge).
    local: nebulizerSessionsLegacy,
  },

  // --- Providers (NPI registry) and provider groups ---------------------
  // Every page of each directory below (wholeDirectory): main's had no LIMIT.
  // The four staff rosters below are read for everyone (rosterOrDirectory):
  // `GET /users` for those who hold users:read, the staff directory for the
  // rest, in the same shape.
  'Provider.GetAccountProviders': rosterOrDirectory({
    // Active staff only, as main's GetAccountProviders listed (providers linked
    // to an Active account_user) and the directory below lists; `GET /users`
    // has no default status.
    method: 'GET',
    path: () => '/users',
    query: () => ({
      'filter[role]': 'provider',
      'filter[status]': 'active',
      include: 'licenses,licenses.nursys,documents',
      sort: ROSTER_SORT,
      per_page: 100,
    }),
    paginated: true,
    shape: (page) => ({ provider: staffRows(page.items) }),
  }, {
    path: () => '/staff-directory',
    query: () => ({ 'filter[role]': 'provider', sort: ROSTER_SORT, per_page: 100 }),
    shape: (page) => ({ provider: directoryRows(page.items) }),
  }),
  'Provider.GetAllAccountProviders': rosterOrDirectory({
    // Main's "allied and regular providers" (SqlClass
    // getAllAccountAlliedNProviders): the account's active Providers AND
    // Allied Professionals, by surname. The scheduling calendar and the
    // patient cards segment the list on `professionType` ('Allied
    // Professional' files as allied, anything else as a provider), so asking
    // for providers alone dropped every allied professional from both.
    method: 'GET',
    path: () => '/users',
    query: () => ({
      'filter[role]': 'provider,allied_professional',
      'filter[status]': 'active',
      sort: ROSTER_SORT,
      per_page: 100,
    }),
    paginated: true,
    shape: (page) => records(staffRows(page.items).map((row) => ({
      ...row,
      professionType: providerProfessionType(row),
    }))),
  }, {
    // The directory lists Active staff only, by surname.
    path: () => '/staff-directory',
    query: () => ({ 'filter[role]': 'provider,allied_professional', sort: ROSTER_SORT, per_page: 100 }),
    shape: (page) => records(directoryRows(page.items).map((row) => ({
      ...row,
      professionType: providerProfessionType(row),
    }))),
  }),
  'Provider.GetAlliedProfessionalByAccountID': rosterOrDirectory({
    method: 'GET',
    path: () => '/users',
    query: () => ({
      'filter[role]': 'allied_professional',
      include: 'licenses,licenses.nursys,documents',
      sort: ROSTER_SORT,
      per_page: 100,
    }),
    paginated: true,
    // keyon's `providers` rows named the person twice, `id` and
    // `member_unique_ID`, and the allied card store keys its cards on them
    // (alliedCardStore: `id: item.id`, `providerid: item.member_unique_ID`).
    // staffRows renames `id` to `unique_ID`, which left every card with no id.
    shape: (page) => ({
      providers: staffRows(page.items).map((row) => ({ ...row, id: row.unique_ID, member_unique_ID: row.unique_ID })),
    }),
  }, {
    path: () => '/staff-directory',
    query: () => ({ 'filter[role]': 'allied_professional', sort: ROSTER_SORT, per_page: 100 }),
    shape: (page) => ({
      providers: directoryRows(page.items).map((row) => ({ ...row, id: row.unique_ID, member_unique_ID: row.unique_ID })),
    }),
  }),
  'Provider.UpdateAlliedProfessionDataInfo': {
    method: 'PATCH',
    path: (p) => `/users/${id(p.subaccountnumber)}`,
    body: (p) => ({
      first_name: p.firstname,
      last_name: p.lastname,
      // No `email`: in the port it is the person's sign-in identifier, which
      // changes only through the credentials route (Trello bMZnxWqc) — PATCH
      // /users refuses it. Main's card wrote a contact copy on `providers`.
      specialty: p.professionType,
      address_line1: p.address,
      city: p.city,
      state: p.state,
      postal_code: p.zip,
      phone_primary: p.phone,
      phone_secondary: p.cell,
      // No `status`: the allied card always sends 'Active', which PATCH
      // /users refuses (its enum is lower case), so every card save was a
      // 422. Lower-cased it would be worse - it would pass, and activate an
      // invited person from a card edit. Main's card status was a role-row
      // flag, not whether the person may sign in (InternalUpdateNurse leaves
      // it out for the same reason).
      provider_type: p.providerType,
      npi: p.npinumber,
      // The card fills `licensenum` with the NPI it shows (alliedCardStore:
      // `licensenum: updates.npinumber`), so the same number is not sent a
      // second time as a state licence.
      professional_license: p.licensenum && p.licensenum !== p.npinumber ? p.licensenum : undefined,
      license_type: p.licensetype,
    }),
    shape: () => okCode1(null, { message: 'Provider Update' }),
  },
  'Provider.AddProvider': {
    method: 'POST',
    path: (p) => `/patients/${id(p.pid)}/providers`,
    body: (p) => ({
      npi: p.npinumber,
      first_name: p.provFname,
      last_name: p.provLname,
      email: p.provEmail,
      phone: p.provTel,
      cell: p.provCell,
      fax: p.provFax,
      address_line1: p.provAddr1,
      address_line2: p.provAddr2,
      city: p.provCity,
      state: p.provState,
      postal_code: p.provZip,
      status: p.status,
      order_id: orderRef(p),
    }),
    shape: () => okMessage('Inserted'),
    errorStyle: 'message',
  },
  'Provider.UpdateProvider': {
    // A chart's provider, or — with main's account-level sentinel patient
    // '0099', which Edit User sends — the Provider or Administrator themself
    // (accountBridge.updateProvider says why that is PATCH /users).
    local: updateProvider,
    errorStyle: 'msg',
  },
  'Provider.DeleteProviderGroup': {
    method: 'DELETE',
    path: (p) => `/provider-groups/${id(p.group_id || p.groupname)}`,
    shape: () => okStatus('200', { message: 'Deleted' }),
  },

  // --- Nursys: the nurse-licence registry -------------------------------
  //
  // `subactioncode` was keyon's verb-inside-the-verb: A added a registration,
  // D removed one. The method carries it now.
  'ManageNurseList.InternalAddNurse': {
    method: 'POST',
    path: () => '/users',
    upload: true,
    body: (p) => ({
      role: p.nurseType || 'registered_nurse',
      first_name: p.firstname,
      last_name: p.lastname,
      email: p.email,
      phone_primary: p.primearyphone || p.primephone,
      phone_secondary: p.secondaryphone,
      address_line1: p.address1,
      address_line2: p.address2,
      city: p.city,
      state: p.state,
      postal_code: p.zip,
      professional_license: p.licensnum,
      license_state: p.jurisdiction,
      license_type: p.ltype,
      practice_setting: p.hospitalpracsetting,
      practice_setting_other: p.hostpracsettingother,
    }),
    shape: () => okMessage('Inserted'),
    errorStyle: 'message',
  },
  'ManageNurseList.InternalUpdateNurse': {
    // A nurse or aide card's save, PATCH /users. Local because an aide's
    // number is her HHA certificate, not a state licence, and the Bio cards
    // do not say which the person is, so the bridge may have to ask
    // (accountBridge.internalUpdateNurse, nurseUpdateBody).
    local: internalUpdateNurse,
    errorStyle: 'message',
  },
  'ManageNurseList.DeleteNurseInternal': {
    method: 'DELETE',
    path: (p) => `/users/${id(p.unique_ID || p.providerid)}`,
    shape: () => okMessage('Deleted'),
    errorStyle: 'message',
  },
  'ManageNurseList.DeleteNurse': {
    // The call sites send licence, jurisdiction and type, never a
    // registration id (`recid` is NCSBN's RecordId): the enrolment is found by
    // those, then withdrawn — eNotify's `R`, with its NCSBN id. keyon's shared
    // "Nurse Added" answer lets the call site's account deletion follow.
    local: deleteNurse,
  },
  'ManageNurseList.SendNurseSettings': {
    // Retired — decision H6. keyon answers 410 `retired` rather than saving a
    // re-verification cadence nothing ever read; so does this.
    local: sendNurseSettings,
  },
  'NursyInfo.LookupBatchNursesInfo': {
    // "Verify All" on four roster screens (CurrentNurse.vue:1398,
    // PatientNurse.vue:1319, homehealthAide/CurrentAid.vue:1401,
    // patients/PatientCards.vue:1684). None of them sends a registration list —
    // the legacy operation took an account number and verified the whole roster
    // — and this used to send `registration_ids: []` regardless.
    //
    // That is the one value the request treats as meaningful:
    // `StoreNursysLookupBatchRequest::registrationIds()` returns NULL for an
    // *absent* field and the service reads NULL as "every enrolment", while a
    // present-and-empty array becomes `whereIn('id', [])` — a batch of zero
    // jobs. The four screens then alerted "Verifying Nurses Completed!" while
    // no licence had been checked.
    //
    // `local` because the contract also changed shape: the legacy answered
    // synchronously, the port answers `202` and a `status_url`, so the wait
    // happens here. The answer is main's object — `{status, checked, written,
    // truncated, rateLimited, message, results}` — which the four handlers now
    // read, so a run Nursys blocked, a run capped at ten, or one still going is
    // never alerted as "Completed!" (lookupBatchNursesInfo).
    local: lookupBatchNursesInfo,
  },

  // --- Allergies ---------------------------------------------------------
  'Allergy.GetPatientAllergies': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/allergies`,
    paginated: true,
    shape: (page) => ({ status: 'Successful', results: page.items }),
  },
  'Allergy.GetAllergies': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/allergies`,
    paginated: true,
    shape: (page) => ({ allergies: page.items }),
  },
  'Allergy.InsertNewAllergy': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patientid)}/allergies`,
    shape: () => okSuccessful(),
  },
  'Allergy.AddAllergies': {
    // The free-text quick-add on the vitals page: a name and nothing else.
    method: 'POST',
    path: (p) => `/patients/${id(p.pid)}/allergies`,
    body: (p) => ({ name: p.name, status: 'active', verification_status: 'unconfirmed' }),
    shape: () => okStatus('Inserted'),
  },
  'Allergy.UpdateAllergyInfo': {
    method: 'PATCH',
    path: (p) => `/patients/${id(p.patientid)}/allergies/${id(p.allergyId)}`,
    shape: () => okSuccessful(),
  },
  // The structured allergy form posts under the medication envelope and hides
  // the record in `params` — a keyon accident, not a shape worth preserving.
  'MedicationAdmin.getPatientAllergies': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/allergies`,
    paginated: true,
    shape: (page) => okMessage(page.items),
  },
  'MedicationAdmin.AddPatientAllergyInfo': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patientid)}/allergies`,
    body: (p) => allergyBody(p.params),
    shape: () => okStatus('200-Successfull', { code: '1' }),
    errorStyle: 'status',
  },
  'MedicationAdmin.UpdatePatientAllergyInfo': {
    method: 'PATCH',
    path: (p) => `/patients/${id(p.patientid)}/allergies/${id(p.allergyId)}`,
    body: (p) => allergyBody(p.params),
    shape: () => okStatus('200-Successfull', { code: '1' }),
    errorStyle: 'status',
  },

  // --- Diagnoses and procedures -----------------------------------------
  'Diagnosis.PopulateDiagGrid': {
    // Main's one mixed list: Current Diagnosis keeps the `'Diagnosis'` rows and
    // Current Procedures (and the Orders page) the `'Procedure'` rows of the
    // same answer. Reading only `/diagnoses` left Current Procedures always
    // empty, and a procedure saved there vanished from the grid on reload.
    // `gridhtml` was a server-rendered table the page only ever tested for
    // truthiness before reading `data`; keep the flag, drop the HTML.
    local: (p) => populateDiagGrid(p, diagnosisRows),
  },
  'Diagnosis.PopulateDiagGridByOrderID': {
    method: 'GET',
    path: (p) => `/orders/${orderRef(p)}/diagnoses`,
    paginated: true,
    // `OrderDiagnosisResource` also says `category: 'diagnosis'` now, and the
    // pending-order popup filters on the capitalised discriminator, so these
    // rows go through the same shaper as the chart-level grid. The popup's
    // *procedure* list stays empty: `/orders/{order}/procedures` is a second
    // endpoint and the component only calls this one — a component change,
    // recorded rather than faked here.
    shape: (page) => ({ gridhtml: true, data: diagnosisRows(page.items) }),
  },
  'Diagnosis.InsertDiagnosis': {
    // One legacy operation, four endpoints.
    //
    // Two axes decide which. **What kind of line**: keyon kept procedures in
    // `orders_diagnosis` behind a `diagnosis_type` discriminator spelt
    // 'Diagnosis' / 'Procedure' (SqlClass.php:16778), so CurrentProc posts a
    // *procedure* through the diagnosis operation. The port split the table and
    // repurposed the name `diagnosis_type` to mean primary-versus-secondary
    // rank, so the discriminator now chooses the path and must not travel in
    // the body — forwarded raw it 422s against `Rule::in(['primary','secondary'])`,
    // and a procedure that did validate would land in `order_diagnoses` and
    // never appear in the procedure list.
    //
    // **Whether an order was named**: `ordernumber: 0` is the chart-level
    // quick-add on Current Diagnoses / Current Procedures. keyon wrote an
    // `order_id = 0` sentinel row; the port puts a line nobody ordered on the
    // chart's own history, at `POST /patients/{patient}/diagnoses` and
    // `.../procedures`. Those two are the problem list and the procedure
    // history, not order lines, so they take that vocabulary — `onset_date`,
    // `performed_from`, a FHIR clinical status — rather than the order line's.
    //
    // The statuses are translated on every branch: the legacy sent 'Active' and
    // 'Pending' into four different lowercase enums, none of which shares a
    // member with the pair.
    //
    // Only main's explicit "no order" takes the chart branch. An order that was
    // named but cannot be addressed - the GetOrderNumber placeholder
    // (ORDER_NUMBER_ON_SAVE), a display number, NaN - is refused loudly by
    // the unresolved-segment guard (`/orders/null/...`, legacyBridge). Falling
    // back to the chart there turned a pending order's line into a COMPLETED
    // procedure on the patient's history (StoreProcedureRequest's default),
    // the order never got it, and the page said "Procedure successfully
    // added": HomeGrid once handed an open PendingOrderPopup the placeholder
    // as its order number.
    method: 'POST',
    path: (p) => {
      const order = orderRef(p);
      const kind = isProcedureLine(p) ? 'procedures' : 'diagnoses';

      if (order !== null) return `/orders/${order}/${kind}`;

      return namesNoOrder(p)
        ? `/patients/${id(p.pid || p.patientid || p.patientID)}/${kind}`
        : `/orders/null/${kind}`;
    },
    body: (p) => {
      // The pickers post `'E11.9 - Type 2 diabetes mellitus'` in the code field.
      const primary = codedTerm(isProcedureLine(p) ? (p.proccode || p.diagcode) : p.diagcode);
      const onOrder = orderRef(p) !== null;

      if (isProcedureLine(p)) {
        return onOrder ? {
          cpt_code: primary.code,
          description: primary.text,
          shorthand: p.procShorthand,
          performed_on: dateOnly(p.diagnosisDate),
          end_date: dateOnly(p.endDate),
          instruction: p.instruction,
          status: procedureStatus(p.status),
          // PendingOrderPopup's ICD for the added procedure: the Diagnosis
          // column of the row the line puts on Current Procedures. Only when
          // a procedure code was given — without one the ICD box is what
          // `cpt_code` was taken from above.
          indication: (p.proccode && p.diagcode) || undefined,
        } : {
          cpt_code: primary.code,
          description: primary.text,
          short_name: p.procShorthand,
          instructions: p.instruction,
          performed_from: dateOnly(p.diagnosisDate),
          performed_to: dateOnly(p.endDate),
          // The ICD the procedure was for, as picked — Current Procedures
          // prints it in its Diagnosis column.
          indication: p.diagcode || undefined,
          // No `writer`: the performer on the chart is the signed-in person,
          // decided by the API from the token. The form's `writer` came from
          // `allData.manager`, which the bridge once filled with the
          // organization's name, and every quick-add named the agency.
          // No status: the legacy 'Active' has no member in
          // `Procedure::STATUSES`, and the request's own default —
          // `completed` — is what a procedure recorded onto the history with a
          // date means. Guessing 'in_progress' here would assert something the
          // clinician did not.
        };
      }

      return onOrder ? {
        icd10_code: primary.code,
        description: primary.text,
        diagnosed_on: dateOnly(p.diagnosisDate),
        end_date: dateOnly(p.endDate),
        onset_or_exacerbation: p.onset_exacerbation,
        control_rating: p.diagControlRating,
        instruction: p.instruction,
        status: diagnosisStatus(p.status),
      } : {
        icd10_code: primary.code,
        description: primary.text,
        onset_date: dateOnly(p.diagnosisDate),
        abated_date: dateOnly(p.endDate),
        notes: p.instruction,
        // No `writer` (see the procedure branch): the API names the signed-in
        // clinician, and nobody when a patient adds to their own chart.
        status: conditionStatus(p.status),
      };
    },
    shape: () => ({ result: 'Inserted' }),
  },
  'Diagnosis.EditDiagnosis': {
    method: 'GET',
    path: (p) => `/diagnoses/${id(p.diagid)}`,
    shape: (diagnosis) => ({ result: diagnosis }),
  },
  'Diagnosis.UpdateDiagnosis': {
    method: 'PATCH',
    path: (p) => `/diagnoses/${id(p.diagid)}`,
    shape: () => ({ result: 'Updated' }),
  },
  'Diagnosis.DeleteDiagnosis': {
    method: 'DELETE',
    path: (p) => `/diagnoses/${id(p.diagid)}`,
    shape: () => ({ result: 'Deleted' }),
  },
  'Diagnosis.SearchProcedure-Code': {
    // Hyphenated because keyon dispatched on the string, not on an identifier.
    method: 'GET',
    path: () => '/vocabularies/procedures',
    query: (p) => ({ code: p.searchterms }),
    paginated: true,
    shape: (page) => ({ count: page.items.length, result: page.items.length ? page.items : 'No Data Found' }),
  },
  'Diagnosis.SearchProcedure-Description': {
    method: 'GET',
    path: () => '/vocabularies/procedures',
    query: (p) => ({ q: p.description, per_page: 100 }),
    paginated: true,
    shape: (page) => ({ count: page.items.length, result: page.items }),
  },
  'MedGrid.GetPatientProcs': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/procedures`,
    paginated: true,
    // The Current Procedures spellings (`procedure_code`, `procShorthand`, the
    // indication as `diagnosis_code`, the order's signatures), with the
    // capitalised `'Procedure'` discriminator the screens filter on.
    shape: (page) => ({ gridhtml: true, data: chartProcedureRows(page.items) }),
  },

  // --- Medications -------------------------------------------------------
  //
  // `?include=` names **relations**, not response keys.
  //
  // `MedicationController` allow-lists `order`, `medicationAdministration`,
  // `schedules` and `prescription` (plus `prescription.pharmacy` on the single
  // read); `MedicationResource` renders those same relations under
  // `administration_schedule` and `holds`. The table used to request the
  // response spellings, and Spatie diffs requested against allowed and throws
  // `InvalidIncludeQuery` — a 400 on every load of the medication grid, the
  // inactive list, the MAR and both order popups. The relation names are what
  // goes on the wire; `MEDICATION_INCLUDES` is shared so the four reads cannot
  // drift apart again.
  //
  // There is no `prescriber` relation to include at all. `MedicationResource`
  // has no prescriber block — the ordering clinician is on the *order*, which
  // `include=order` carries — so that name is simply dropped rather than
  // renamed.
  'MedGrid.PopulateMedGrid': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/medications`,
    query: () => ({ include: MEDICATION_INCLUDES, per_page: 100 }),
    paginated: true,
    shape: (page) => ({ gridhtml: true, data: medicationRows(page.items) }),
  },
  'MedGrid.PopulateMedGridByOrderID': {
    method: 'GET',
    path: (p) => `/orders/${orderRef(p)}/medications`,
    paginated: true,
    // Through the same shaper as the chart-level grid: the order popups render
    // the same columns and launch the same per-order actions off these rows.
    shape: (page) => ({ gridhtml: true, data: medicationRows(page.items) }),
  },
  'MedicationAdmin.GetPatientMeds': {
    // The MAR screen's initial load (MedAdmin.vue:2185), which renders today.
    //
    // Same split as `GetPatientsMedByID` below — the legacy read carried the
    // administration log with the medications, and without it every dose due
    // today rendered as unsigned, including the ones already given. The MAR
    // read is called with no window, so the day it answers for is the
    // *organization's* calendar day (`OrganizationTimeZone::for`), which is the
    // right one: keyon forced America/New_York process-globally while MySQL
    // wrote UTC, so a late-evening dose could be filed under tomorrow.
    local: async (p) => {
      const patient = routeId(p.pid);

      if (patient === null) return records([]);

      const medications = await listAll(`/patients/${patient}/medications`, {
        include: MEDICATION_INCLUDES,
      });
      const cells = await listAll(`/patients/${patient}/medication-administrations`, {});

      // Today's view lists what is current: superseded versions are dropped,
      // and the doses they gave today are shown on the line that replaced
      // them, so a time already given is never drawn as due (MED-15, PP-18).
      // The date-range read below keeps every version.
      const current = currentCourseRows(medications, cells);

      // The one MAR read whose cells are the organization's today and no other
      // day, so the only one whose held cells are the holds in force: it gives
      // the row its hold window, as main's findpatientactiveMedOrders does
      // (P-NURSE-H12).
      return records(withMarSlots(medicationRows(current.rows), current.cells, { ofToday: true }));
    },
  },
  'MedicationAdmin.GetPatientsMedByID': {
    // The MAR grid, for a date range.
    //
    // The legacy read returned the medications AND their administration log in
    // one payload — `findpatientactiveMedOrdersByStrtEndDate` aggregated
    // `medicationlog` into a per-date `medlogtimes` array with each slot's
    // status, taken time and hold window (SqlClass.php:5428-5512). The port
    // deliberately split that read in two, because the old one also ran hold
    // and discontinue *maintenance*, so opening a chart's medication tab
    // mutated it.
    //
    // This mapping only ever called the first half, so the grid received bare
    // medication rows: no slots, no taken times, no hold windows. Every cell
    // rendered as an unsigned scheduled dose — including the ones a nurse had
    // already signed — which is worse than an error, because it reads as a
    // complete MAR.
    //
    // `local` because the two halves are two endpoints and the cell collection
    // is paginated at 100 (`ResourceQuery::MAX_PER_PAGE`), so a fortnight of a
    // real chart does not fit in one page.
    local: async (p) => {
      const patient = routeId(p.pid);

      if (patient === null) {
        // A read with no chart in view is normal — the same answer
        // `legacyBridge`'s unresolved-path branch would have given.
        return records([]);
      }

      const from = dateOnly(p.startDate);
      const to = dateOnly(p.endDate);
      const medications = await listAll(`/patients/${patient}/medications`, {
        from,
        to,
        // `order` for the order's status and signatures, which main's
        // `ifOrderSigned` reads to disable a half-signed row's dose, time and
        // status controls (MED-19); without it the guard never fired here.
        include: 'administration_schedule,order,release_order,hold_order',
      });

      const cells = await listAll(`/patients/${patient}/medication-administrations`, {
        // `from`/`to` are the MAR read's own window parameters, not filters, and
        // it clamps anything past 92 days rather than refusing it.
        from,
        to,
      });

      // No hold window on these rows, as main's date-range query gives none: a
      // range holds the held cells of days gone by, including those of a hold
      // since released, and an edit that sent that window back would ask the
      // API for a hold starting in the past and be refused whole (P-NURSE-H12).
      return records(withMarSlots(medicationRows(medications), cells));
    },
  },
  'MedicationAdmin.GetPrePourMeds': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/medications`,
    // What may be poured is what is current: a superseded line beside its
    // successor would be poured twice (MED-OM-02). The server narrows it
    // (`filter[current]`), and the shaper again for a server that does not.
    query: () => ({ 'filter[is_prepour]': 1, 'filter[current]': 1, per_page: 100 }),
    paginated: true,
    shape: (page) => medicationRows(currentCourseRows(page.items, []).rows),
  },
  'MedicationAdmin.GetOrderedMedEditInfo': {
    // The pharmacy is reached through the prescription, which is where the
    // schema puts it — `MedicationController::show` allow-lists
    // `prescription.pharmacy` for exactly this screen.
    method: 'GET',
    path: (p) => `/medications/${id(p.medentryid)}`,
    query: () => ({ include: `${MEDICATION_INCLUDES},prescription.pharmacy` }),
    // Through the same row shaper as the grids: the edit form reads
    // `editFormdata.order_number` and posts it back as the order to update.
    shape: (medication) => ({ record: medication ? medicationRows([medication])[0] : medication }),
  },
  'MedicationInsert.InsertMedication': {
    method: 'POST',
    path: (p) => `/orders/${orderRef(p)}/medications`,
    body: (p) => medicationBody(p),
    shape: () => ({ sqlresult: 'Inserted' }),
  },
  'MedicationEdit.UpdateMedication': {
    method: 'PATCH',
    path: (p) => `/medications/${id(p.medentryid)}`,
    body: (p) => ({
      ...medicationBody(p),
      is_available: p.medadmin === undefined ? undefined : bool(p.medadmin),
    }),
    shape: () => okMsg('Updated'),
    errorStyle: 'msg',
  },
  'MedicationUpdate.DeleteMedication': {
    method: 'DELETE',
    path: (p) => `/medications/${id(p.medentryid)}`,
    shape: () => okStatus('Deleted'),
    errorStyle: 'msg',
  },
  'MedicationAdmin.UpdateMedAdminAvailable': {
    method: 'PATCH',
    path: (p) => `/medications/${id(p.medId)}`,
    body: (p) => ({ is_available: bool(p.medAvailable) }),
    shape: () => okMsg('Updated'),
  },
  'MedicationAdmin.UpdateMedAdminDoseUOM': {
    method: 'PATCH',
    path: (p) => `/medications/${id(p.medId)}`,
    body: (p) => ({ dose_uom: p.medDoseUOM }),
    shape: () => okMsg('Updated'),
  },

  // --- Medication administration, holds and sign-off --------------------
  'MedicationAdmin.InsertAdminMecationInfo': {
    // The single largest legacy payload: one flat object carrying a
    // medication, a prescriber, a pharmacy, a prescription, a schedule and an
    // order. Row 133 keeps the transaction and gives each part a name.
    //
    // Which write it is follows what the signed-in person may do (MED-16).
    // Main's 16 (`medications:add_unordered`) and 17 (`medications:write`)
    // gate the Add Meds buttons on different screens, and every one of them
    // posted the same order-creating call — so an administrator, a care
    // manager or a patient, who hold 16 and may not write an order, had every
    // save fail (on main, silently, for want of a licence; here, as a 403 from
    // `/medication-orders`, which needs `orders:write`). Someone who may write
    // an order writes one; someone who may only add an un-ordered medication
    // adds one to the chart (row 124), with its times; anyone else is sent to
    // the order route and shown its refusal.
    local: async (p) => {
      const patient = routeId(p.patientid);

      if (patient === null) {
        throw new ApiError({
          status: 0,
          code: 'bridge_unresolved_target',
          message: 'This medication could not be saved: no patient is selected.',
        });
      }

      const mayOrder = sessionCan('medications:write') && sessionCan('orders:write');

      // Main #328 (PP-09): Add Meds opened from pre-pour asks Prescription or
      // Non-prescription, and a non-prescription medication is written with
      // no order — active at once, on its own row — whoever adds it. Sent
      // down the order path, a nurse's vitamin became an order waiting for a
      // provider's signature, which is what non-prescription exists to avoid.
      // Onto the MAR (and so onto the pre-pour sheet) only for someone who
      // may administer: the port's interim rule for an un-ordered addition
      // (MED-16), stated beside the route; anyone else's lands off the MAR.
      if (bool(p.nonprescription) && sessionCan('medications:add_unordered')) {
        const schedule = scheduleBody(p);
        const created = await api.post(`/patients/${patient}/medications`, {
          ...medicationBody(p),
          is_unordered: true,
          available_quantity: openingStock(p),
          times: schedule.times,
          administration: sessionCan('medications:administer') ? (schedule.administration ?? true) : false,
          is_prepour: schedule.is_prepour,
          is_nebulizer: schedule.is_nebulizer,
        });

        return okStatus('200 Successfull', {
          message: 'Medication Added Successfully',
          medentryid: created && created.id != null ? String(created.id) : '',
          nonprescription: 1,
        });
      }

      if (!mayOrder && sessionCan('medications:add_unordered')) {
        const schedule = scheduleBody(p);

        await api.post(`/patients/${patient}/medications`, {
          ...medicationBody(p),
          available_quantity: openingStock(p),
          times: schedule.times,
          // Never put on the nurses' MAR from here. Those who take this path
          // by default — administrators, care managers, patients — do not
          // administer, and an un-ordered line is live on sight: a patient's
          // self-added drug showed on the MAR as due, with no order and no
          // signature. The line records what the patient takes, with its
          // times; a nurse turns administration on from the time editor.
          // Whether such additions should be Pending orders instead is MED-16's
          // open product question.
          administration: false,
          is_prepour: schedule.is_prepour,
          is_nebulizer: schedule.is_nebulizer,
        });
      } else {
        await api.post(`/patients/${patient}/medication-orders`, {
          medication: { ...medicationBody(p), available_quantity: openingStock(p) },
          prescriber: prescriberBody(p),
          pharmacy: pharmacyBody(p),
          prescription: {
            rx_number: p.rxNumber,
            ndc_code: p.ndcnumber,
            quantity: p.totalTabs,
            refills: p.refills,
            filled_on: dateOnly(p.datescriptFilled),
            expires_on: dateOnly(p.expirationDate),
            refill_reminder_on: dateOnly(p.refillReminderDate),
            start_date: dateOnly(p.startDate),
            end_date: dateOnly(p.endDate),
          },
          schedule: scheduleBody(p),
          order: {
            ordered_on: dateOnly(p.orderDate),
            ordered_at: p.orderTime,
            setting: p.medsetting,
            source: p.source,
            link: p.link,
          },
        });
      }

      // keyon's success body (MED-14). This used to answer `{count: 1}`,
      // which every add screen reads as "Duplicate medication": CurrentMed and
      // MedAdmin then asked to add it to the Administration setting and posted
      // a second, 'update' write; the pre-pour sheet said "Duplicate
      // medication". Success is recognised by this message and status.
      return okStatus('200 Successfull', { message: 'Medication Added Successfully' });
    },
    // A refusal arrives as `{message, status: 'Error'}` with the server's
    // sentence, which the add screens show instead of "system error".
    errorStyle: 'message',
  },
  'MedicationAdmin.InsertUpdateMedLogTimes': {
    // Catalogue row 135 is per-medication, and the caller has only an order
    // number — so this lands on row 134, which is the same write addressed by
    // the id the legacy payload actually carries.
    //
    // A medication with no order (un-ordered, or main's non-prescription row,
    // which #336's MedAdmin now edits as such) has nothing for row 134 to
    // bind; its times go to row 135 by the medication's own id.
    method: 'PUT',
    path: (p) => (orderRef(p) === null && routeId(p.medentryid) !== null
      ? `/medications/${routeId(p.medentryid)}/administration-schedule`
      : `/medication-orders/${orderRef(p)}/schedule`),
    body: (p) => ({
      ...scheduleBody(p),
      // The MAR edits one medication's times; naming it keeps the rewrite and
      // the line's flags on that medication, not on every line of the order.
      // `provsubaccount` (main #336, "who changed the times") is not sent: the
      // actor is the token.
      medication_id: routeId(p.medentryid) ?? undefined,
      change_order: bool(p.changeorder),
      change_reason: p.changereason,
    }),
    shape: () => ({ results: 'Updated' }),
    errorStyle: 'message',
  },
  'MedicationAdmin.ChangeOrderForMedLogTimes': {
    // `change_reason` is `required_if:change_order,true` and the caller sends
    // `Reasaon4change.value || ''`, which the API's empty-string middleware
    // nulls — so an unfilled reason box is a 422 naming `change_reason`. That
    // is the correct outcome and is left alone: a change order exists to be
    // countersigned by somebody who was not in the room, and inventing a reason
    // here would put words in the signer's mouth.
    method: 'PUT',
    path: (p) => `/medication-orders/${orderRef(p)}/schedule`,
    body: (p) => ({
      ...scheduleBody(p),
      medication_id: routeId(p.medentryid) ?? undefined,
      change_order: true,
      change_reason: p.changereason,
      // The hold picker's days are local calendar days (PP-22). The row's own
      // hold window rides back with every edit of a held medication (the edit
      // form shows it, NUR339-16): one window or none (holdWindow). The API
      // takes a window already in force as no new hold, and the start of a hold
      // in force beside another end as that hold's end moved - how a hold begun
      // before today is extended or brought to a close sooner (P-NURSE-H17).
      ...holdWindow(p),
    }),
    shape: () => ({ results: 'Updated' }),
    errorStyle: 'message',
  },
  'MedicationAdmin.PrnSignOff': {
    // `signoffobj` is ONE slot object — `{time, dosage, reason, status,
    // signedOff}` (MedAdmin.vue:5732-5738) — not a collection. The legacy
    // handler read `$signoffobj->prntimes`; this table used to read `.times`,
    // and neither key has ever existed on the wire, so the fallback
    // `[p.signoffobj]` was the only branch that ever ran and it posted the raw
    // slot: no `administered_at`, and a 422 on every rescue dose.
    //
    // `reason` is required and is deliberately not defaulted. "As needed" for
    // what is the clinically load-bearing field on the endpoint; an empty
    // indication has to come back to the nurse as an error, not be filled in.
    //
    // The day is the nurse's **local** day and the moment is the wall-clock
    // `time`, which the API composes in the organization's zone. `dateOnly`
    // took the UTC day of the sign-off stamp, and the naive instant built from
    // it was read as UTC: from 20:00 in New York every rescue dose was sent as
    // tomorrow's, refused (`dose_date_in_future`), and — with the slot already
    // locked on screen — recorded nowhere.
    method: 'POST',
    path: (p) => `/medications/${id(p.medid)}/prn-administrations`,
    body: (p) => {
      const slot = p.signoffobj || {};
      const stamp = (slot.signedOff && slot.signedOff.date) || new Date();
      const on = toLocalYMD(slot.date || stamp);
      const at = stamp instanceof Date ? stamp : new Date(stamp);
      const now = Number.isNaN(at.getTime()) ? new Date() : at;

      return {
        administered_on: on,
        times: [{
          time: wallClockTime(slot.time) || wallClockTime(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`),
          reason: slot.reason,
          dose_amount: slot.dosage,
          // The popup stamps 'taken' before posting; a refusal is charted
          // through the scheduled-dose path, not here.
          status: slot.status === 'refused' ? 'refused' : 'taken',
        }],
      };
    },
    shape: () => okStatus('200 Successfull', { code: '200 Successfull', results: 'Inserted' }),
    errorStyle: 'status',
  },
  'MedicationAdmin.MedSignOff': {
    // MAR scheduled dose sign-off (Trello UHjWAgbK: a patient must never be
    // given a dose twice, so a recorded dose must never be silently lost).
    //
    // keyon's MedSignOff took the pending sign-offs as one `signoffObj` spanning
    // medications and days and updated them in one untransacted call. The port
    // records per medication and day — POST /medications/{m}/administrations,
    // the signer from the token — so this posts one request per group, every
    // group even when an earlier one is refused, and answers what happened to
    // each slot: `medications[]` with `ok` and the server's sentence. The MAR
    // locks a slot only when its result says it was recorded, and tells the
    // nurse about every one that was not; it used to lock everything first and
    // read a refusal as silence.
    //
    //  - the slot is the scheduled time: finalSignOff appends " (taken at
    //    8:05 AM)" to the label it shows, which is cut off here;
    //  - the day is the local day the nurse charted on, never the UTC one;
    //  - a refusal's reason travels as `reason` (required by the server);
    //  - stock is the server's to count: `medication.available` is never sent;
    //  - an item naming no medication is reported as failed, not skipped while
    //    the call answers "Updated".
    local: async (p) => {
      const rawItems = p.signoffObj;
      const items = Array.isArray(rawItems)
        ? rawItems
        : (rawItems && typeof rawItems === 'object' ? Object.values(rawItems) : []);

      const results = [];
      const groups = new Map();

      items.forEach((item, index) => {
        if (!item) return;

        const medication = item.medication || {};
        const medId = routeId(medication.medentryid ?? medication.id ?? medication.medicationid);
        const timeObj = item.timeObj || {};
        const slotTime = String(timeObj.rawTime || timeObj.time || '').split('(')[0].trim().slice(0, 5);
        const status = String(timeObj.temporaryStatus || timeObj.status || 'taken').toLowerCase();
        const day = toLocalYMD(item.dateObj || timeObj.date || p.adminDate || new Date());

        const result = { index, medentryid: medId, slot_time: slotTime, administered_on: day, status, ok: false, message: '' };
        results.push(result);

        if (medId === null) {
          result.message = 'This dose could not be signed off: the medication it belongs to was not identified.';
          return;
        }

        const key = `${medId}|${day}`;
        if (!groups.has(key)) groups.set(key, { medId, day, entries: [], results: [] });

        const reason = status === 'refused'
          ? (timeObj.refusalReason || timeObj.reason || timeObj.note || undefined)
          : undefined;
        const note = timeObj.earlyReason || (status === 'refused' ? undefined : timeObj.note) || undefined;

        groups.get(key).entries.push({
          slot_time: slotTime,
          status,
          ...(reason ? { reason } : {}),
          ...(note ? { note } : {}),
        });
        groups.get(key).results.push(result);
      });

      for (const group of groups.values()) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.post(`/medications/${group.medId}/administrations`, {
            administered_on: group.day,
            entries: group.entries,
          });
          group.results.forEach((result) => { result.ok = true; });
        } catch (error) {
          if (!(error instanceof ApiError)) throw error;

          // One medication's day is one transaction: its slots stand or fall
          // together, and every other medication is still attempted.
          group.results.forEach((result) => {
            result.message = error.message || 'This dose could not be signed off.';
            result.code = error.code;
          });
        }
      }

      const recorded = results.filter((result) => result.ok).length;

      if (results.length > 0 && recorded === results.length) {
        return { results: 'Updated', code: '200 Successfull', medications: results };
      }

      const failed = results.filter((result) => !result.ok);

      return {
        results: recorded > 0 ? 'Partial' : 'Error',
        code: recorded > 0 ? '207-Partial' : '400-Error',
        status: 'Error',
        message: failed.map((result) => result.message).filter(Boolean)[0] || 'Nothing was signed off.',
        medications: results,
      };
    },
    errorStyle: 'message',
  },
  'MedicationAdmin.HoldMedication': {
    // Hold *now*. The legacy `holdobjec.status` says what to do ('hold' or
    // 'discontinue' — MedAdmin.vue:4494 routes both through here) and
    // `holdobjec.type` says how much of the day ('all' | 'specific'). The port
    // reads `type` as hold-versus-discontinue and `times` as the scope, so the
    // two legacy fields swap places; forwarded raw, `type: 'all'` and
    // `status: 'hold'` failed both `Rule::in`s and every hold 422'd.
    method: 'POST',
    path: (p) => `/medications/${id(p.holdobjec && p.holdobjec.medentryid)}/holds`,
    body: (p) => holdBody(p.holdobjec, { queued: false }),
    shape: () => ({ results: 'Updated', message: 'Medication Held Successfully!' }),
    errorStyle: 'message',
  },
  'MedicationAdmin.ScheduleHoldMedication': {
    // Three operations behind one API_Meth.
    //
    // keyon switched on `holdobjec.action` — schedule / update / cancel
    // (tswebhook.php:2763-2810) — and this table ignored it and always POSTed.
    // So releasing a queued hold *added a second one* to the drug the nurse was
    // trying to free: a wrong clinical write, not a rejected one. The port gives
    // the three their own routes, and the queued hold is addressed by its own
    // id — which the legacy payload does not carry, so amend and cancel have to
    // find it first. `local` because that is two round trips; the alternative
    // is a spec that pretends one request can do it.
    local: async (p) => {
      const hold = p.holdobjec || {};
      const medication = routeId(hold.medentryid);
      const action = String(hold.action || 'schedule').toLowerCase();

      if (medication === null) {
        throw new ApiError({
          status: 0,
          code: 'bridge_unresolved_target',
          message: 'This hold could not be sent: the medication it applies to was not identified.',
        });
      }

      if (action !== 'cancel' && action !== 'update') {
        await api.post(`/medications/${medication}/scheduled-holds`, holdBody(hold, { queued: true }));

        return okCode1(null, { code: '1', results: 'Inserted' });
      }

      const queued = (await api.get(`/medications/${medication}/scheduled-holds`, {
        'filter[status]': 'scheduled',
        sort: '-hold_start_on',
      })) || [];
      const target = Array.isArray(queued) ? queued[0] : null;

      if (!target) {
        // Not fabricated as a success: the nurse asked to release a hold and
        // there is none queued, which they need to see.
        throw new ApiError({
          status: 404,
          code: 'scheduled_hold_not_found',
          message: 'There is no queued hold on this medication to change.',
        });
      }

      if (action === 'cancel') {
        await api.delete(`/scheduled-holds/${target.id}`);
      } else {
        await api.patch(`/scheduled-holds/${target.id}`, holdBody(hold, { queued: true, amend: true }));
      }

      return okCode1(null, { code: '1', results: 'Inserted' });
    },
    // keyon's refusal here is {code: "0", error, errorMsg} (tswebhook.php ScheduleHoldMedication), and both callers
    // (MedAdmin scheduleholdMedication, CurrentMed handleHoldScheduleSubmit) branch on code 0: they say `error` and,
    // in Medication Administration, put the row back. The port's 'message' style answered {message, status:'Error'},
    // which no code-0 branch saw, so a refused scheduled hold was silent and its row stayed changed (NUR339-15). The
    // sentence is the API's own, in `error` (and `refusal_code` for a refusal main has a word for).
    errorStyle: 'code',
  },
  'MedicationAdmin.CheckRemoveScheduledHold': {
    // `{hold}` binds a `medication_schedules` row by its own id. The legacy
    // payload carries `medentryid`, a *medication* id, and the old fallback put
    // it in the hold position — so this deleted whichever queued hold happened
    // to share that number, on any medication in the tenant. The hold is looked
    // up under the medication instead, which is the only identifier the caller
    // actually has.
    local: async (p) => {
      const hold = p.holdobjec || {};
      const known = routeId(hold.holdid);
      const medication = routeId(hold.medentryid);

      if (known === null && medication === null) {
        throw new ApiError({
          status: 0,
          code: 'bridge_unresolved_target',
          message: 'This hold could not be cleared: the medication it applies to was not identified.',
        });
      }

      let target = known;

      if (target === null) {
        const queued = (await api.get(`/medications/${medication}/scheduled-holds`, {
          'filter[status]': 'scheduled',
          sort: '-hold_start_on',
        })) || [];
        const first = Array.isArray(queued) ? queued[0] : null;

        // The operation's name is "check *and* remove": nothing queued is the
        // expected outcome half the time, and the call site treats the cleared
        // envelope as "there is no hold now", which is true.
        if (!first) {
          return { results: 'Cleared', message: 'Medication Scheduled Hold have been reoved successfully' };
        }

        target = first.id;
      }

      await api.delete(`/scheduled-holds/${target}`);

      return { results: 'Cleared', message: 'Medication Scheduled Hold have been reoved successfully' };
    },
    errorStyle: 'message',
  },
  'MedicationAdmin.ClearCurrentMedicationHold': {
    method: 'DELETE',
    path: (p) => `/medications/${id(p.medentryid)}/holds/current`,
    shape: () => okStatus('200-Successfull', {
      result: { message: 'Auto Change Completed successfully' },
    }),
    errorStyle: 'status',
  },
  'MedicationAdmin.UpdatePrepourForActiveMedications': {
    method: 'PATCH',
    path: (p) => `/patients/${id(p.pid)}/medications/prepour-setting`,
    body: () => ({ is_prepour: true }),
    shape: () => okSuccessful(),
  },
  'MedicationAdmin.DiscontinueOrder': {
    // `holdobjec.type` here is the legacy hold *scope* — MedAdmin.vue:5391
    // hard-codes 'all' — while the port's `type` is the change type carried
    // onto the successor order, bounded to discontinue / expire / not_approved.
    // Sent raw it 422'd every discontinuation. `status` is not accepted at all:
    // the predecessor becomes `discontinued` and the successor enters at
    // `OrderStatus::atCreation()`, neither of which is a client's decision.
    method: 'POST',
    path: (p) => `/orders/${orderRef(p.holdobjec) ?? orderRef(p)}/discontinuation`,
    body: (p) => ({
      reason: (p.holdobjec && p.holdobjec.reason) || undefined,
      type: discontinuationType(p.holdobjec && p.holdobjec.type),
      archive_previous: archiveFlag(p.holdobjec && p.holdobjec.archiveHLDDC),
      // The day the nurse picked, in their own calendar (PP-22): dateOnly's
      // UTC day is tomorrow from 20:00 in New York.
      discontinued_on: toLocalYMD(p.holdobjec && p.holdobjec.discontinuedDate) || undefined,
    }),
    shape: () => ({ code: '200-Successfull', message: 'Order discontinued successfully.' }),
    errorStyle: 'message',
  },

  // --- Prescriptions and pharmacies -------------------------------------
  'MedicationAdmin.GetAssignedPatPharmacy': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/pharmacies`,
    paginated: true,
    shape: (page) => ({ results: page.items.length ? pharmacyRows(page.items) : '' }),
  },

  // --- Orders ------------------------------------------------------------
  'Order.GetAccountPendingOrders': {
    method: 'GET',
    path: () => '/orders',
    query: (p) => ({
      // Main's queue is by whose signature is missing (lib/PendingOrderFilter):
      // a provider, a registered nurse or a licensed practical nurse sees only
      // the orders waiting on their own signature, so a hold the provider has
      // signed leaves his count, and a provider's hold or discontinue waits on
      // the nurses until one signs. The server derives the half from the
      // token; any other account type is counted by status alone, Pending, as
      // main counts it (NUR339-09). meta.total is the count the tile shows.
      ...pendingOrdersFilter(p.accountType, 'pending'),
      'filter[patient_id]': (p.pids || []).map(id).join(',') || undefined,
      // The grid shows the medication and both signatures; neither is on the
      // order row itself.
      include: 'medications,signatures',
      sort: '-ordered_on',
      per_page: 100,
    }),
    paginated: true,
    shape: (page) => ({ numofpending: (page.meta && page.meta.total) || page.items.length, orders: orderRows(page.items) }),
  },
  'Order.GetAccountPendingOrdersByID': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/orders`,
    // The orders waiting on the person asking, as above; anyone else gets both
    // rungs, as main's GetAccountPendingOrdersByID reads them.
    query: (p) => ({ ...pendingOrdersFilter(p.accountType, 'pending,active_pending'), sort: '-ordered_on', per_page: 100 }),
    paginated: true,
    shape: (page) => ({ numofpending: (page.meta && page.meta.total) || page.items.length, orders: orderRows(page.items) }),
  },
  'OrderFilter.NEW_OrderLookUp': orderWidgetLookup(),
  // Main reserved an order number before the popup opened (keyon
  // GetOrderNumber) and the Orders page's Create New Order, the welcome grid's
  // order button and the Vitals page's note all wait for one, closing with
  // "Error generating order number" without it (MV-26). The port allocates the
  // number per organization when the order is created (02-api-endpoints.md
  // §4.2, `App\Domain\Orders\OrderNumbers`), so there is nothing to reserve:
  // this answers with a placeholder that only says so. No caller sends it back
  // — OrderPopup's InsertNewOrder body is built by clinicalBridge.newOrderBodies,
  // which takes no client order number, and the real one comes back from
  // POST /orders as `order_display_number`.
  'systemData.GetOrderNumber': {
    local: () => okSuccessful({ ordernumber: ORDER_NUMBER_ON_SAVE }),
  },
  'Orders.GetALLPatientOrdersWithMedications': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientID)}/orders`,
    query: () => ({ include: 'medications,diagnoses', per_page: 100 }),
    paginated: true,
    shape: (page) => ({ Count: page.items.length, result: orderRows(page.items) }),
  },
  'Order.ClearPending': {
    method: 'DELETE',
    path: (p) => `/orders/${orderRef(p)}/pending-items`,
    shape: () => ({ recRemoved: 'Pending Medication and Diagnosis Deleted' }),
  },
  'Orders.ChangeOrderStatus': {
    method: 'PATCH',
    path: (p) => `/orders/${orderRef(p)}`,
    body: (p) => ({ status: p.status }),
    shape: () => ({ result: 'Updated' }),
  },
  'OrderTemp.InsertOrder': {
    method: 'POST',
    path: () => '/orders',
    body: (p) => orderBody(p),
    shape: (order) => ({ result: 'Inserted', ordernumber: order && order.id }),
  },
  'OrderTemp.UpdateOrder': {
    // The pending-order popup's Save is a **countersignature**, not an edit.
    //
    // Legacy `updateOrderTemplate` (tswebhook.php:8999) wrote the captured
    // signatures onto the order header, set the status Active and cascaded to
    // the medication rows; it never touched the child collections. This used to
    // be bridged onto `PUT /orders/{order}`, whose documented semantics are "an
    // omitted child collection means this order has none" — so signing a
    // pending order soft-deleted every medication, diagnosis and procedure on
    // it, recorded no signature (`orderBody` carries none), left the order
    // inactive, and reported `result: 'Updated'`.
    //
    // `POST /orders/{order}/signatures` is the operation that was actually
    // being asked for: append-only, credential-checked, and it advances the
    // status and cascades to the medication lines itself when the ladder
    // completes (`OrderSigning`, lines 101-107) — the same three effects, none
    // of the destruction.
    //
    // `role_override` is deliberately not sent. The popup pre-fills both
    // signature boxes from the order, so which half is *new* is not derivable
    // from the payload; letting the server derive the role from the principal
    // means this can only ever record the caller's own half, and an
    // administrator signing for the other half still needs the credential.
    //
    // The header fields the popup also sends (description, order type, the
    // physician block) are dropped: the only non-destructive order write in the
    // port is `PATCH /orders/{order}`, which takes `status` alone. Editing a
    // pending order's header therefore needs the component migrated to the
    // native client — recorded in the report, not papered over here.
    method: 'POST',
    path: (p) => `/orders/${orderRef(p)}/signatures`,
    // The signer's own box and day (api2/orderSignOff.js countersignBody): the popup posts both pre-filled.
    body: (p) => countersignBody(p, signedInAccountType()),
    shape: () => ({ result: 'Updated' }),
    errorStyle: 'message',
  },
  'MedicationAdmin.LookupOrderInfoSigSignOff': {
    // keyon answered ONE row - the order, both signatures and their times, joined to the medication the pop-up was
    // opened for. The signature context alone (what was passed through before) has none of that: the pop-up got no
    // order number, no medication, no signatures and no times, so it routed its sign-off to /orders/null/signatures
    // and never offered a signature pad. Built from the order and its signatures, the signature context and, for an
    // order that carries no line of its own (a hold or a discontinue), the medication (api2/orderSignOff.js). A MAR
    // card names the line's own order, signed by both halves, while its row waits on the order of a hold (or a
    // release) awaiting its second signature: that order is the one opened, and the row answers with its number.
    local: (p) => lookupOrderSignOff(p, orderRows),
    errorStyle: 'status',
  },
  'MedicationAdmin.UpdateOrderWithNurseSignature': {
    method: 'POST',
    path: (p) => `/orders/${orderRef(p)}/signatures`,
    // Per action (api2/orderSignOff.js signOffBody): a provider countersigning after the nurse sends the provider's
    // text, day and half; a nurse's sign-off sends hers. The popup emits only when both signatures are on it, so
    // `nurseSignature` is always set, and the old `nurseSignature || providerSignature` filed the nurse's text and day
    // under a provider. Both days are the signer's own calendar day (toLocalYMD), not the UTC day of the stamp.
    body: (p) => signOffBody(p),
    shape: () => ({ code: '200 Successfull' }),
    errorStyle: 'message',
  },

  // --- Directives --------------------------------------------------------
  'Directives.GetDirectiveCatalog': {
    method: 'GET',
    path: () => '/directive-catalog',
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  'Directives.SearchDirectives': {
    method: 'GET',
    path: () => '/directive-catalog',
    query: (p) => ({ q: p.query }),
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  'Directives.SearchSnomed': {
    method: 'GET',
    path: () => '/vocabularies/snomed',
    query: (p) => ({ q: p.query, 'filter[subset]': 'directive_eligible' }),
    paginated: true,
    shape: (page) => dataEnvelope(page.items, { available: true }),
  },
  'Directives.GetDirectivePatients': {
    // Main's roster is one answer: every patient in the caller's directive
    // read scope, Full Code included, which the list page filters, sorts and
    // pages itself. The API pages it, so every page is read.
    local: async () => dataEnvelope(directivePatientRows(await listAll('/directive-patients', {}))),
  },
  'Directives.SearchDirectivePatients': {
    // Main's "New Patient Record" search: scoped, three characters or
    // nothing, patients with no record included, at most fifty with `capped`
    // saying there were more.
    method: 'GET',
    path: () => '/directive-patients',
    query: (p) => ({ q: p.query || '', per_page: 50 }),
    paginated: true,
    shape: (page) => dataEnvelope(
      directivePatientRows(page.items),
      { capped: !!(page.meta && page.meta.total > page.items.length) },
    ),
  },
  'Directives.GetDirectiveCounts': {
    method: 'GET',
    path: () => '/directive-records/statistics',
    shape: (stats) => directiveCounts(stats),
  },
  'Directives.GetDirectiveRecordsForPatient': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/directive-records`,
    query: () => ({ per_page: 100 }),
    paginated: true,
    shape: (page) => ({ status: '200', results: directiveRecordRows(page.items) }),
  },
  'Directives.GetDirectiveRecords': {
    // A refusal (outside the read scope) arrives as {status: 'Error'}, which
    // main's getDirectiveRecords reads as "not assigned", never as "no
    // directives" — the difference between a denied chart and a Full Code one.
    method: 'GET',
    path: (p) => `/patients/${id(p.patient_pid)}/directive-records`,
    query: () => ({ include: 'directives,events,document', per_page: 100 }),
    paginated: true,
    shape: (page) => dataEnvelope(directiveRecordRows(page.items), {
      patient: (page.meta && page.meta.patient) || null,
    }),
    errorStyle: 'status',
  },
  'Directives.CreateDirectiveRecordManual': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patient_pid)}/directive-records`,
    body: (p) => ({
      ingest_path: 'manual',
      relationship_to_patient: p.relationship_to_patient,
      relationship_other: p.relationship_other,
      is_legal_surrogate: bool(p.is_legal_surrogate),
      authority_type: p.authority_type,
      directives: p.directives,
    }),
    shape: (record) => dataEnvelope(record),
  },
  'Directives.AddDirectivesToRecord': {
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id)}/directives`,
    body: (p) => ({ directives: p.directives }),
    shape: () => okStatus('Successful'),
    errorStyle: 'status',
  },
  'Directives.SignDirectiveRecord': {
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id || p.record_id)}/signature`,
    body: (p) => ({
      assigned_directives: p.assigned_directives,
      attestation_note: p.attestation_note,
    }),
    shape: (record) => ({ status: '200', results: record }),
    errorStyle: 'status',
  },
  'Directives.ReauthorizeDirectiveRecord': {
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id)}/reauthorizations`,
    body: (p) => ({ attestation_note: p.attestation_note || p.note }),
    shape: () => okStatus('Successful'),
    errorStyle: 'status',
  },
  'Directives.ConfirmDirectiveAuthorization': {
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id)}/authorization-confirmation`,
    body: (p) => ({ confirmation_note: p.note }),
    shape: () => okStatus('Successful'),
    errorStyle: 'status',
  },
  'Directives.MarkDirectiveReviewed': {
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id)}/reviews`,
    body: (p) => ({ note: p.note }),
    shape: () => okStatus('Successful'),
    errorStyle: 'status',
  },
  'Directives.FlagDirectiveReviewNeeded': {
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id)}/review-flags`,
    body: (p) => ({ note: p.note, reason: p.reason }),
    shape: () => okStatus('Successful'),
    errorStyle: 'status',
  },
  'Directives.RevokeDirectiveRecord': {
    // Main's "Delete current submission" sends no reason, and none is
    // required; one that is sent becomes the event's note.
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id)}/revocation`,
    body: (p) => ({ reason: p.reason || p.note || undefined }),
    shape: () => okStatus('Successful'),
    errorStyle: 'status',
  },
  'Directives.DeleteDirectiveDocument': {
    // Main's DeleteDirectiveDocument (decision D4(b)): remove a pending
    // submission's document with a reason, which also closes the submission.
    // The file uid is the one the page showed; a record whose document is a
    // different file answers 404, as keyon's does.
    method: 'POST',
    path: (p) => `/directive-records/${id(p.directive_record_id)}/document-removal`,
    body: (p) => ({ reason: p.reason, file_uid: p.file_uid || undefined }),
    shape: (result, p) => okSuccessful({
      file_uid: p.file_uid,
      directive_record_id: id(p.directive_record_id),
    }),
    errorStyle: 'status',
  },
  'DirectivePolicy.GetDirectiveReauthPolicies': {
    method: 'GET',
    path: () => '/directive-reauth-policies',
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  'DirectivePolicy.GetDirectiveReauthPolicyImpact': {
    method: 'GET',
    path: (p) => `/directive-reauth-policies/${p.state_code}/impact`,
    query: (p) => ({ reauth_days: p.reauth_days }),
    shape: (impact) => dataEnvelope(impact),
  },
  'DirectivePolicy.SaveDirectiveReauthPolicy': {
    method: 'PUT',
    path: (p) => `/directive-reauth-policies/${p.state_code}`,
    body: (p) => ({ reauth_days: Number(p.reauth_days) }),
    shape: () => okStatus('Successful'),
  },
  'DirectivePolicy.DeleteDirectiveReauthPolicy': {
    method: 'DELETE',
    path: (p) => `/directive-reauth-policies/${p.state_code}`,
    shape: () => okStatus('Successful'),
  },

  // --- Documents ---------------------------------------------------------
  // Main retired the three path-based credential operations with the blob
  // move (410 endpoint_retired, "This page is out of date. Reload it to
  // continue."): they listed, zipped and unlinked files by a path the client
  // sent. Their replacements name files by uid and check who asks:
  // ListCredentialDocuments, the credentials bundle and DeleteCredentialDocument.
  'PdfList.GetAllNursePdfs': retiredCredentialOperation(),
  'PdfList.DownloadManageAccountPdfs': retiredCredentialOperation(),
  'PdfList.RemoveManageAccountPdfs': retiredCredentialOperation(),

  // Main's ListCredentialDocuments: `own` (the caller's), `org` (everyone's,
  // caregiver_documents:read) or `owner` (one person's). The API lists by page;
  // main answers with every document grouped by owner, so the pages are read
  // through and grouped here, newest first. The order is a total one, the id
  // last (allPages): documents uploaded in the same second tie on
  // `uploaded_at`, tied rows may come back in another order for page 2, and a
  // document on the boundary would be listed twice or not at all - on the page
  // that says whose paperwork is on file.
  'PdfList.ListCredentialDocuments': {
    local: async (p) => {
      const scope = String(p.scope || 'own').toLowerCase();
      const query = { scope, sort: '-uploaded_at,-id' };
      if (scope === 'owner' && p.owner_unique_id) query.owner = String(p.owner_unique_id);
      const items = await allPages('/credential-documents', query);
      return okStatus('Successful', { scope: listedScope(scope, p), groups: credentialGroups(items) });
    },
    errorStyle: 'status',
  },

  // Main's DeleteCredentialDocument: a soft delete that empties every licence
  // or insurance slot naming the file. `unclaimed_only` (a JSON true only) is a
  // form releasing its own unsaved upload. The reason is PHI-capable text and
  // travels in the body, never the URL.
  'PdfList.DeleteCredentialDocument': {
    local: async (p) => {
      const result = await api.delete(`/documents/${fileUidSegment(p.file_uid)}`, {
        data: {
          ...(p.reason !== undefined ? { reason: p.reason } : {}),
          ...(p.unclaimed_only === true ? { unclaimed_only: true } : {}),
        },
      });
      return okStatus('Successful', {
        file_uid: (result && result.file_uid) || p.file_uid,
        cleared_references: (result && result.cleared_references) || 0,
      });
    },
    errorStyle: 'status',
  },

  // --- Notes and note templates -----------------------------------------
  'Note.GetNotes': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/notes`,
    paginated: true,
    shape: (page) => records(page.items),
  },
  // Every page (wholeDirectory), as main answers: SqlClass GetAllNotes has no
  // LIMIT, and AttachNoteVideos then adds each note's videos (#334). The API
  // pages /patients/{id}/notes (25 by default, newest first), and one request
  // dropped every note after the 25th from NotesPage, ProfessionalNotesPage
  // and the note popup - the older notes and their videos never showed.
  // `shape` stays on the entry, so the specs that call it directly still work.
  //
  // Audit: each page whose notes carry videos writes its own
  // note_attachment_list VIEW row, where main writes one per read. That is a
  // finer-grained record of the same read, accepted rather than hidden
  // (P-MEDIA reviewed it): the rows' notes and count add up to main's one row
  // only if the pages split the list cleanly, which a total order
  // (NOTE_WALK) is for.
  'Note.GetAllNotes': wholeDirectory({
    method: 'GET',
    path: (p) => `/patients/${id(p.patientID)}/notes`,
    query: () => NOTE_WALK,
    paginated: true,
    shape: (page) => dataEnvelope(noteRows(page.items)),
  }),
  'Note.GetNoteByID': {
    method: 'GET',
    path: (p) => `/notes/${id(p.noteID)}`,
    // The legacy spellings, and the note's `attachments` (#334), as the list
    // gives them: NoteAttachmentList reads the row as GetAllNotes shapes it.
    shape: (note) => dataEnvelope(note ? noteRows([note])[0] : note),
  },
  'Note.SavePatientNote': {
    // The enveloped spelling of the flat operation below; same body builder, so
    // the type map and the seconds-to-minutes conversion cannot be fixed on one
    // and not the other. Forwarding `p` untouched was a 422 on every field.
    method: 'POST',
    body: (p) => noteBody(p),
    // `local` is the whole operation: runOperation calls it before it would
    // read a path or a shape, so this entry has neither. A note - with videos
    // (#334) or without - answers main's save shape (msg 'Inserted', note_id,
    // and for videos attachments_unbound and attachments), and a refused one
    // keeps its real status, which notesApi.saveNote reads
    // (src/api2/videoBridge.js saveNoteWithVideos).
    local: (p) => saveNoteWithVideos(notePath(p.patientid || p.patientID), noteBody(p), p),
    errorStyle: 'msg',
  },
  'Provider.SendNoteGroupNotifications': {
    // Was UNMAPPED, because `POST /notes/{note}/notifications` is addressed by
    // the note and the legacy operation was note-*less*: it named groups and
    // members for an account (tswebhook.php:8063). NotePopup now threads the id
    // the save returns into the payload (NotePopup.vue:1161, :1401), so the
    // operation has the one thing it was missing.
    //
    // The old mapping built `/notes/null/notifications`, which the bridge's
    // unresolved-path branch short-circuited, and a constant shape then answered
    // `{message: 'NotificationsSent'}` over a request that never left the
    // browser. The path still resolves to `null` when no id reaches it — a
    // write, so `legacyBridge` fails it loudly rather than shaping a success.
    method: 'POST',
    path: (p) => `/notes/${routeId(p.noteId || p.note_id)}/notifications`,
    body: (p) => ({
      group_ids: idList(p.group_ids),
      // The channel is deliberately absent from this wire: each recipient's own
      // communication preference decides it, with SMS falling back to e-mail.
      // `StoreNoteNotificationRequest` refuses a request naming neither a group
      // nor a person, which is the right answer to "notify nobody".
      user_ids: idList(p.member_unique_IDs),
    }),
    // The `202` says the notifications were queued — the rows exist before any
    // transport runs — and `queued: 0` is a correct answer for a resubmitted
    // form, which the send-once dedupe key absorbs.
    shape: (result) => okMessage('NotificationsSent', { code: 1, queued: result && result.queued }),
    errorStyle: 'message',
  },

  // --- Texts and calls (main #317, keyon/lib/TwilioAccess.php) -------------
  //
  // src/api/smsApi.js posts these, and a few older components still post them
  // directly. The API runs main's gate: a number is accepted only when it is on
  // file for the organization, a named patient (`patient_unique_ID`, which is
  // the charted patient's id here) is texted or rung on their own number and
  // the posted one is ignored, a patient needs patients:communicate, and a call
  // is connected to one of the CALLER's own numbers — `from` is only a
  // preference. The answer is what Twilio did: keyon's {status: <sid>,
  // TwilioStatus: 'successful'} only on a send it took, and otherwise keyon's
  // failure body with the API's code and sentence ('twilio' errorStyle), so the
  // compose modals show "That phone number is not on file for your
  // organization..." and stay open rather than claiming "Text sent".
  'Twilio.SendText': {
    method: 'POST',
    path: () => '/sms-messages',
    body: (p) => ({ ...twilioRecipient(p, p.smsnumber), body: p.smsmessage }),
    shape: (result) => twilioSent(result && result.provider_message_id),
    errorStyle: 'twilio',
  },
  'Twilio.SendPhoneCall': {
    method: 'POST',
    path: () => '/phone-calls',
    body: (p) => ({ ...twilioRecipient(p, p.callnumber), preferred_from: p.from ? String(p.from) : undefined }),
    shape: (result) => twilioSent(result && result.provider_call_id),
    errorStyle: 'twilio',
  },
  // Main's conference text is a text to a comma list of numbers through the
  // same gate (at most ten, each on file). Main then texted a hard-coded
  // organiser line (317-750-8432) "You're conference Call and Group has been
  // setup"; that number is nobody's in this system and is not ported. Its
  // callers build their lists from a group roster nothing populates.
  'Twilio.SendConferenceText': {
    method: 'POST',
    path: () => '/sms-messages',
    body: (p) => ({ to: splitNumbers(p.conferencenumbers), body: p.conferencemessage }),
    shape: (result) => twilioSent(result && result.provider_message_id),
    errorStyle: 'twilio',
  },
  'Note.GetNoteTemplates': {
    // A template can be saved for one patient and its body may name them, so
    // the list is a CHART's, as main's GetNoteTemplates answered one named
    // patient's. A chart that does not resolve - no id, or one that is not a
    // number - used to drop the filter (`id(p.patientID) || undefined`) and ask
    // for every template in the organization, which the picker would then
    // offer, and paste into this chart's note. It is no request at all: the
    // picker reads the failure as no templates (NotePopup.loadTemplates).
    method: 'GET',
    path: () => '/note-templates',
    query: (p) => {
      const patient = routeId(p.patientID);

      if (patient === null) {
        throw new ApiError({
          status: 0,
          code: 'bridge_unresolved_target',
          message: 'The templates could not be loaded: the chart they belong to was not identified.',
        });
      }

      return { 'filter[patient_id]': patient, per_page: 100 };
    },
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  'Note.CreateNoteTemplate': {
    // NotePopup.vue picks this or UpdateNoteTemplate from an `isEdit` ternary,
    // so both spellings have to resolve.
    method: 'POST',
    path: () => '/note-templates',
    body: (p) => ({ name: p.name, body_html: p.bodyHtml, patient_id: id(p.patientID) }),
    shape: (template) => okStatus('success', { id: template && template.id }),
    errorStyle: 'message',
  },
  'Note.UpdateNoteTemplate': {
    method: 'PATCH',
    path: (p) => `/note-templates/${id(p.templateID)}`,
    body: (p) => ({ name: p.name, body_html: p.bodyHtml }),
    shape: () => okStatus('success'),
    errorStyle: 'message',
  },
  'Note.DeleteNoteTemplate': {
    method: 'DELETE',
    path: (p) => `/note-templates/${id(p.templateID)}`,
    shape: () => okStatus('success'),
    errorStyle: 'message',
  },

  // --- Messaging ---------------------------------------------------------
  // The widgets' inbox: keyon's flat message rows, one per message per
  // recipient, rebuilt from the API's threads (api2/messageInbox.js says
  // how). Thread rows carried none of the fields the widgets thread on, so
  // the inbox was empty. The signed-in person is the token's subject, never
  // the ids the payload names.
  'Messages.FetchAllMessagesByIDS': {
    local: () => inboxRecords(),
  },
  'Messages.FetchMessages': {
    local: () => inboxRecords(),
  },
  'Messages.SearchAllMessagesByIDS': {
    method: 'GET',
    path: () => '/message-threads',
    query: (p) => ({ q: p.message, sort: '-last_message_at', per_page: 100 }),
    paginated: true,
    shape: (page) => okStatus('Successfull', {
      records: page.items.map((row) => withMessageTimes(row, row && row.last_message_at)),
    }),
  },
  'Messages.FetchMessagesByThreadID': {
    method: 'GET',
    path: (p) => `/message-threads/${id(p.thread_id)}/messages`,
    query: () => ({ sort: 'sent_at' }),
    paginated: true,
    // Each message with main's `thread_attachments` items, which the widgets
    // preview and open by uid (messageAttachments.normalizeMessageAttachments).
    shape: (page) => records(page.items.map((row) => withMessageTimes(withThreadAttachments(row), row && row.sent_at))),
  },
  // Main's DeleteMessageAttachment: the sender takes a file back, with a
  // reason, from every message it went out with. The reason travels in the
  // body. keyonFiles reads a refusal's code and sentence (reason_required,
  // forbidden, not_found, already_deleted).
  'Messages.DeleteMessageAttachment': {
    local: async (p) => {
      const result = await api.delete(`/message-attachments/${fileUidSegment(p.file_uid)}`, {
        data: { reason: p.reason },
      });
      return okStatus('Successful', {
        file_uid: (result && result.file_uid) || p.file_uid,
        cleared_references: (result && result.cleared_references) || 0,
      });
    },
    errorStyle: 'status',
  },
  'Messages.SearchContacts': {
    method: 'GET',
    path: () => '/message-contacts',
    query: (p) => ({ q: p.searchterm, 'filter[kind]': p.searchtype || undefined }),
    paginated: true,
    // The two callers disagree: one reads a bare array, the other `records`.
    shape: (page) => Object.assign(page.items.slice(), { status: 'Successfull', records: page.items }),
  },
  'Messages.SearchAllContactsNurseProviders': {
    method: 'GET',
    path: () => '/message-contacts',
    query: (p) => ({ q: p.searchterm, 'filter[kind]': 'staff' }),
    paginated: true,
    shape: (page) => okStatus('Successfull', { records: page.items }),
  },
  'Messages.SearchGroups': {
    method: 'GET',
    path: () => '/message-contact-groups',
    query: (p) => ({ q: p.searchterm }),
    paginated: true,
    shape: (page) => okStatus('Successfull', { records: page.items }),
  },
  'Messages.GetMessageContactsForUser': {
    method: 'GET',
    path: () => '/message-contacts',
    query: () => ({ per_page: 100 }),
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  'Messages.GetSharedPatientsForMessaging': {
    method: 'GET',
    path: (p) => `/users/${id(p.requester_unique_ID)}/shared-patients`,
    query: (p) => ({ 'filter[other_user_ids]': (p.other_unique_IDs || []).map(id).join(',') || undefined }),
    paginated: true,
    shape: (page) => dataEnvelope(page.items, { status: 'successful' }),
  },
  'Messages.CreatePatientOwnedSubgroupWithMembers': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patient_unique_ID)}/message-groups`,
    body: (p) => ({
      name: p.group_name,
      member_user_ids: (p.member_unique_IDs || []).map(id),
    }),
    shape: () => okStatus('Successful'),
    errorStyle: 'message',
  },
  // The compose modal's "Select channels" defaults (main #322's
  // GetAccountUsersContactInfoByUniqueIds; NT-10). Main answered with every
  // listed member's e-mail address and phone number; the API answers each
  // person's channel only, and only for people the caller may message, so
  // user_email and user_phone are not in this answer at all. The modal reads
  // communication_preference, in main's words.
  'Messages.GetAccountUsersContactInfoByUniqueIds': {
    method: 'GET',
    path: () => '/message-contacts/notify-defaults',
    query: (p) => ({ ids: idList(p.unique_IDs) }),
    shape: (rows) => okStatus('Successful', {
      data: (Array.isArray(rows) ? rows : []).map((row) => ({
        unique_ID: String(row.id),
        communication_preference: NOTIFY_CHANNEL_LABELS[row.notify_channel] || 'Email',
      })),
    }),
  },

  // --- Template notifications (main #283; NT-07) ---------------------------
  // Main's two tswebhook branches were an open relay: the address, the words
  // and the account came from the body, with no session at all. The API takes
  // a recipient by id and the record a template is about by id, and fills the
  // rest itself; `recipient.email`, `recipient.phone`, `recipient.accountnumber`
  // and `accountnumber` are not forwarded. Main's `phone` channel is the API's
  // `voice`. No screen of main's calls either; they are mapped so a caller that
  // does gets the closed version, not an unmapped operation.
  'Notification.SendNotification': {
    method: 'POST',
    path: () => '/notifications',
    body: (p) => ({
      template_key: p.templateKey,
      recipient_user_id: routeId(p.recipient && p.recipient.recipient_unique_id) ?? routeId(p.recipient_unique_id) ?? undefined,
      channels: templateChannels(p.channels),
      patient_id: routeId(p.patient_id) ?? undefined,
      appointment_id: routeId(p.appointment_id) ?? undefined,
      medication_id: routeId(p.medication_id) ?? undefined,
      // Forwarded so a field the server fills itself is refused out loud
      // (422) rather than dropped while the caller believes it was used.
      fields: p.fields && typeof p.fields === 'object' && Object.keys(p.fields).length ? p.fields : undefined,
    }),
    shape: (result) => ({
      code: 'Successful',
      message_uuid: result && result.message_group,
      results: Object.fromEntries(((result && result.rows) || []).map((row) => [
        legacyNotifyChannel(row.channel),
        { sent: row.status === 'pending' || row.status === 'sent', error: row.status === 'pending' || row.status === 'sent' ? null : row.status },
      ])),
    }),
  },
  'Notification.GetNotificationStatus': {
    method: 'GET',
    path: () => '/notifications',
    query: (p) => ({ 'filter[message_group]': String(p.message_uuid || ''), per_page: 10 }),
    paginated: true,
    shape: (page) => (page.items.length === 0
      ? { code: 'NotFound' }
      : {
        code: 'Successful',
        confirmed_at: (page.items.find((row) => row.confirmed_at) || {}).confirmed_at || null,
        channels: Object.fromEntries(page.items.map((row) => [
          legacyNotifyChannel(row.channel),
          { send_status: LEGACY_SEND_STATUS[row.status] || 'failed', delivery_status: row.delivery_status, sent_at: row.sent_at },
        ])),
      }),
  },

  // --- Appointments ------------------------------------------------------
  // The booked times the slot pickers subtract from a caregiver's hours
  // (getAvailability): main's SELECT is `status IN ('pending','approved')`
  // for the provider on the date, unpaged (SqlClass). A visit that was
  // cancelled or is done no longer holds its slot, so those are left out -
  // counted, a slot freed by a cancellation was never offered again - and
  // every page is read (allPages), since the API pages at 25 by default and a
  // booked time on a later page was offered as free and came back 409. The id
  // breaks ties between visits that share a start time, so none on a page
  // boundary is read twice or missed. No provider or no day names no visits,
  // as main's `provider_id = ''` matched none: without the provider the
  // filter is gone, and the read would be every visit of the day, page after
  // page, to be subtracted from the wrong caregiver's hours.
  'Appointment.GetAppointmentsForProviderDate': {
    method: 'GET',
    path: () => '/appointments',
    query: providerDayQuery,
    paginated: true,
    shape: providerDayShape,
    local: async (p) => providerDayShape({
      items: id(p.providerId) === null || dateOnly(p.date) === null
        ? []
        : await allPages('/appointments', providerDayQuery(p)),
      meta: null,
      links: null,
    }),
  },
  // Every page, without the cancelled: main's GetAppointmentsForPatients is
  // one unpaged SELECT of the patients' visits with status != 'cancelled'
  // (SqlClass). The calendars build their diary, their conflict and viability
  // previews and a series' "this and following" from what this returns, and
  // the API sorts oldest first and pages at 100 — so one page showed a chart's
  // oldest hundred visits and none of its upcoming ones, and a bulk cancel
  // left the unread occurrences booked. Read in groups of patients, each to
  // its last page (diaryOfPatients), then answered in main's order.
  'Appointment.GetAppointmentsForPatients': {
    method: 'GET',
    path: () => '/appointments',
    query: (p) => diaryQuery(p.patientIds),
    paginated: true,
    shape: (page) => records(appointmentRows(page.items)),
    local: async (p) => records(appointmentRows(await diaryOfPatients(p.patientIds))),
  },
  'Appointment.CreateAppointment': {
    method: 'POST',
    path: () => '/appointments',
    body: (p) => ({
      patient_id: id(p.patientId),
      provider_id: id(p.providerId),
      scheduled_on: dateOnly(p.appointmentDate),
      scheduled_at: p.appointmentTime,
      duration_minutes: p.durationMinutes,
      meeting_type: p.meetingType,
      reason: p.reason,
      series_id: id(p.seriesId),
      notify: notifyBody(p),
      // Only when the person booking saw a conditional verdict and confirmed
      // it — never a default, or every conditional visit would book unseen.
      confirm_conditional: p.confirmConditional === true ? true : undefined,
      // The discipline the screen evaluated (apiCaregiverType).
      caregiver_type: apiCaregiverType(p.caregiverType),
    }),
    shape: (appointment) => okCode1(appointment && appointment.id, { code: 1 }),
    refusals: { visit_conditional: CONDITIONAL_VISIT_REFUSAL },
    // keyon's appointment failure body is {code: 0, error}; the call sites
    // read `error` and show it, so the API's sentence goes there.
    errorStyle: 'code',
  },
  // The recurrence rule AND its visits, in one request. Main stored only the
  // rule here (no time of day) and the calendar then posted each occurrence
  // through CreateAppointment. The API materialises the occurrences itself,
  // checking each against the caregiver's hours and diary, the patient's cap
  // and the rubric, and skipping and reporting the dates it cannot book — so
  // the caller sends the time (`appointmentTime`) and creates nothing more
  // (SchedulingCalendar.confirmSeriesBooking). `recurrenceDays` arrives as
  // main's comma list and goes out as weekday indices.
  'Appointment.CreateAppointmentSeries': {
    method: 'POST',
    path: () => '/appointment-series',
    body: (p) => ({
      patient_id: id(p.patientId),
      provider_id: id(p.providerId),
      start_date: dateOnly(p.startDate),
      scheduled_at: p.appointmentTime,
      duration_minutes: p.durationMinutes,
      meeting_type: p.meetingType,
      reason: p.reason,
      recurrence_type: p.recurrenceType,
      recurrence_days: p.recurrenceType === 'weekly' ? seriesWeekdays(p.recurrenceDays) : undefined,
      end_type: p.endType,
      end_count: p.endType === 'count' ? Number(p.endCount) : undefined,
      end_date: p.endType === 'date' ? dateOnly(p.endDate) : undefined,
      notify: notifyBody(p),
      // The calendar's preview showed conditional dates and the person booked
      // anyway. Without it the API skips those dates as `visit_conditional`
      // (an in-home physician series is every date).
      confirm_conditional: p.confirmConditional === true ? true : undefined,
      // The discipline the preview evaluated (apiCaregiverType).
      caregiver_type: apiCaregiverType(p.caregiverType),
    }),
    shape: (plan) => okCode1(plan && plan.series ? plan.series.id : null, {
      occurrences: ((plan && plan.occurrences) || []).map((a) => ({
        id: a.id,
        date: a.local_date,
        time: hhmm(a.local_start_time),
      })),
      skipped: ((plan && plan.skipped) || []).map((s) => ({ date: s.local_date, reason: s.reason })),
    }),
    errorStyle: 'code',
  },
  // No patient_id (MX-17): a visit never moves to another patient. Main's
  // UpdateAppointment passes no patient to SQL, and the API refuses a different
  // one with 422 patient_change_not_allowed. The call site still sends
  // `patientId` (patientCardsStore.updateAppointment resolves it for the
  // notification), so it is dropped here rather than forwarded.
  'Appointment.UpdateAppointment': {
    method: 'PATCH',
    path: (p) => `/appointments/${id(p.id)}`,
    body: (p) => ({
      scheduled_on: dateOnly(p.appointmentDate),
      scheduled_at: p.appointmentTime,
      duration_minutes: p.durationMinutes,
      meeting_type: p.meetingType,
      reason: p.reason,
      provider_id: id(p.providerId),
      notify: notifyBody(p),
      // Moving a visit asks the rubric again (a status change does not).
      confirm_conditional: p.confirmConditional === true ? true : undefined,
      // Sent with a new caregiver only (patientCardsStore.updateAppointment):
      // the API re-derives the discipline then, and for an allied
      // professional it has to be told.
      caregiver_type: apiCaregiverType(p.caregiverType),
    }),
    shape: () => okCode1(null, { code: 1 }),
    refusals: { visit_conditional: CONDITIONAL_VISIT_REFUSAL },
    errorStyle: 'code',
  },
  'Appointment.SetAppointmentStatus': {
    method: 'PATCH',
    path: (p) => `/appointments/${id(p.id)}`,
    body: (p) => ({ status: p.status, notify: notifyBody(p) }),
    shape: () => okCode1(null, { code: 1 }),
    errorStyle: 'code',
  },
  'Appointment.DeleteAppointment': {
    method: 'DELETE',
    path: (p) => `/appointments/${id(p.id)}`,
    shape: () => okCode1(null, { code: 1 }),
    errorStyle: 'code',
  },

  // --- Scheduling: availability, time off, settings ----------------------
  'Schedule.GetProviderAvailabilityById': {
    // The **rules**, not the computed slots.
    //
    // Legacy `GetProviderAvailabilityById` returned `provider_availability` rows
    // and the browser computed the bookable slots from them, which is still
    // what the call site does: it reads `day_of_week`, `start_time`, `end_time`,
    // `slot_minutes` and `provider_id` off each row and runs
    // `computeAvailableSlots` (appointmentApiService.ts:780-798). This was
    // mapped to `/users/{id}/availability`, whose items are already-computed
    // `start_at`/`end_at`/`is_available` — none of the five fields — so
    // `dayOfWeek` and `slotMinutes` came out `NaN`, the rules array filtered to
    // empty, and every provider showed no bookable times on the calendar.
    //
    // `provider_id` echoes back the caller's own value rather than the
    // resource's `caregiver_user_id`: the call site filters with `===` against
    // the `providerId` string it passed in, so an integer from the API drops
    // every row even once the rule fields are right.
    method: 'GET',
    path: (p) => `/users/${id(p.providerid)}/availability-rules`,
    paginated: true,
    shape: (page, p) => records((page.items || []).map((rule) => ({
      ...rule,
      provider_id: p.providerid,
    }))),
  },
  'Schedule.GetProviderAvailabilityRules': {
    method: 'GET',
    path: (p) => `/users/${id(p.providerid)}/availability-rules`,
    paginated: true,
    shape: (page) => records(page.items),
  },
  'Schedule.InsertProviderAvailabilityById': {
    method: 'POST',
    path: (p) => `/users/${id(p.provider_id)}/availability-rules`,
    body: (p) => ({
      day_of_week: Number(p.dayOfWeek),
      start_time: p.startTime,
      end_time: p.endTime,
      slot_minutes: Number(p.slotMinutes),
    }),
    shape: (rule) => okCode1(rule && rule.id, { code: 1 }),
  },
  'Schedule.UpdateProviderAvailabilityById': {
    method: 'PATCH',
    path: (p) => `/availability-rules/${id(p.id)}`,
    body: (p) => ({
      day_of_week: Number(p.dayOfWeek),
      start_time: p.startTime,
      end_time: p.endTime,
      slot_minutes: Number(p.slotMinutes),
    }),
    shape: (rule) => okCode1(rule && rule.id, { code: 1 }),
  },
  'Schedule.DeleteProviderAvailabilityById': {
    method: 'DELETE',
    path: (p) => `/availability-rules/${id(p.id)}`,
    shape: () => okCode1(null, { code: 1 }),
  },
  // Every page (wholeDirectory): main answered every entry of the caregiver's
  // time off. The API pages at 25 by default and sorts oldest first, so one
  // page held the caregiver's oldest 25 absences and none of the current ones
  // (a year of sick days and holidays is more than that), and the slot pickers
  // offered days they are away; the booking then came back 409. The id breaks
  // ties between entries that share a date and a start, so none on a page
  // boundary is read twice or missed.
  'Schedule.GetProviderTimeOff': wholeDirectory({
    method: 'GET',
    path: (p) => `/users/${id(p.providerid)}/time-off`,
    query: () => ({ sort: 'off_date,start_time,id', per_page: 100 }),
    paginated: true,
    shape: (page) => records(page.items),
  }),
  'Schedule.SaveTimeOff': {
    method: 'POST',
    path: (p) => `/users/${id(p.providerid)}/time-off`,
    body: (p) => ({
      off_date: dateOnly(p.offDate),
      start_time: p.startTime,
      end_time: p.endTime,
      reason: p.reason,
    }),
    shape: (entry) => okCode1(entry && entry.id, { code: 1 }),
  },
  'Schedule.UpdateTimeOff': {
    method: 'PATCH',
    path: (p) => `/time-off/${id(p.id)}`,
    body: (p) => ({
      off_date: dateOnly(p.offDate),
      start_time: p.startTime,
      end_time: p.endTime,
      reason: p.reason,
    }),
    shape: (entry) => okCode1(entry && entry.id, { code: 1 }),
  },
  'Schedule.DeleteTimeOff': {
    method: 'DELETE',
    path: (p) => `/time-off/${id(p.id)}`,
    shape: () => okCode1(null, { code: 1 }),
  },
  'NurseSchedule.GetNurseStaff': rosterOrDirectory({
    // keyon's GetAllNurseStaff columns (nurses: providerID, firstname,
    // lastname, caregivetype, email, primaryphone), which the scheduling
    // reader (appointmentApiService getSchedulingNurseStaff) reads by name.
    // Passing the user rows through gave every nurse and aide the id
    // 'undefined' and no name, so a nurse locked to her own calendar had an
    // empty picker; and `caregivetype` must be main's words ('Home Health
    // Aid'), which is how the reader tells an aide from a nurse.
    //
    // Read for everyone (rosterOrDirectory): `GET /users` is users:read, which
    // a nurse, an aide or a care manager does not hold, so their pickers came
    // back empty and a nurse locked to her own calendar found no one - not
    // even herself - to book. They read the staff directory: the same people
    // by name, role and work phone, and no e-mail or username.
    //
    // Active staff only, as main's GetAllNurseStaff listed (an Active
    // account_user) and the directory below lists: `GET /users` has no default
    // status, and this is the scheduling picker, where the API books whoever it
    // is handed - a deactivated, invited or suspended caregiver included.
    method: 'GET',
    path: () => '/users',
    query: () => ({
      'filter[role]': 'registered_nurse,licensed_practical_nurse,home_health_aid',
      'filter[status]': 'active',
      sort: ROSTER_SORT,
      per_page: 100,
    }),
    paginated: true,
    shape: (page) => nurseStaffRows(page.items),
  }, {
    path: () => '/staff-directory',
    query: () => ({
      'filter[role]': 'registered_nurse,licensed_practical_nurse,home_health_aid',
      sort: ROSTER_SORT,
      per_page: 100,
    }),
    shape: (page) => nurseStaffRows(page.items),
  }),
  'NurseSchedule.GetShiftSettings': {
    method: 'GET',
    path: (p) => `/shift-settings/${p.groupKey}`,
    shape: (settings) => records(settings ? [settings] : []),
  },
  'NurseSchedule.SaveShiftSettings': {
    method: 'PUT',
    path: (p) => `/shift-settings/${p.groupKey}`,
    body: (p) => ({
      min_minutes: Number(p.minMinutes),
      max_minutes: Number(p.maxMinutes),
      step_minutes: Number(p.stepMinutes),
      max_consecutive_hours: Number(p.maxConsecutiveHours),
    }),
    shape: () => okCode1(null, { code: 1 }),
  },
  'SchedulingSettings.GetSchedulingUiSettings': {
    method: 'GET',
    path: () => '/scheduling-settings',
    shape: (settings) => records(settings ? [{
      enable_environment_tabs: settings.enable_environment_tabs ? 1 : 0,
      max_concurrent_caregivers: settings.max_concurrent_caregivers,
      show_calendar_supporting_cards: settings.show_supporting_cards ? 1 : 0,
    }] : []),
  },
  'SchedulingSettings.SaveSchedulingUiSettings': {
    method: 'PUT',
    path: () => '/scheduling-settings',
    body: (p) => ({
      enable_environment_tabs: bool(p.enableEnvironmentTabs),
      max_concurrent_caregivers: Number(p.maxConcurrentCaregivers),
      show_supporting_cards: bool(p.showSupportingCards),
    }),
    shape: () => okCode1(null, { code: 1 }),
  },
  'SchedulingCustomization.GetCustomSchedulingContexts': {
    method: 'GET',
    path: () => '/scheduling-contexts',
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  'SchedulingCustomization.AddCustomSchedulingContext': {
    method: 'POST',
    path: () => '/scheduling-contexts',
    body: (p) => ({ label: p.label }),
    shape: (context) => okCode1(context && context.id, { code: 1 }),
    errorStyle: 'message',
  },
  'SchedulingCustomization.DeleteCustomSchedulingContext': {
    method: 'DELETE',
    path: (p) => `/scheduling-contexts/${id(p.id)}`,
    shape: () => okCode1(null, { code: 1 }),
    errorStyle: 'message',
  },

  // --- Patient availability ---------------------------------------------
  // Every page (wholeDirectory): the blocks are the patient's whole routine,
  // read by the calendars' conflict previews, and the API pages at 25 by
  // default. The id breaks ties between blocks that share a start time.
  'PatientAvailability.GetBlocks': wholeDirectory({
    method: 'GET',
    path: (p) => `/patients/${id(p.patientId)}/availability-blocks`,
    query: (p) => ({ 'filter[service_mode]': p.serviceMode || undefined, sort: 'start_time,id', per_page: 100 }),
    paginated: true,
    shape: (page) => records(page.items),
  }),
  'PatientAvailability.AddBlock': {
    method: 'POST',
    path: (p) => `/patients/${id(p.patientId)}/availability-blocks`,
    body: (p) => blockBody(p),
    shape: (block) => okCode1(block && block.id, { code: 1 }),
  },
  'PatientAvailability.UpdateBlock': {
    method: 'PATCH',
    path: (p) => `/availability-blocks/${id(p.id)}`,
    body: (p) => blockBody(p),
    shape: () => okCode1(null, { code: 1 }),
  },
  'PatientAvailability.DeleteBlock': {
    method: 'DELETE',
    path: (p) => `/availability-blocks/${id(p.id)}`,
    shape: () => okCode1(null, { code: 1 }),
  },
  'PatientAvailability.GetServiceMode': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientId)}/service-mode`,
    shape: (mode) => ({ service_mode: (mode && mode.service_mode) || 'in_home' }),
  },
  'PatientAvailability.SetServiceMode': {
    method: 'PUT',
    path: (p) => `/patients/${id(p.patientId)}/service-mode`,
    body: (p) => ({ service_mode: p.serviceMode }),
    shape: () => okCode1(null, { code: 1 }),
  },

  // --- Vitals, measurements and CPT billing -----------------------------
  // The device readings behind the CPT page's cycle calendar: main's
  // `SELECT * FROM foradata WHERE patientId = :pid` (and `AND MDeviceID =
  // :meterid` for one meter), every reading the chart's Fora meters uploaded.
  // See cptDeviceReadings for what is read and why it is bounded: the cycle on
  // screen (`firstDate`, `lastDate`), not the chart. Both answer 'Success' as
  // main's two did.
  'PatientCpt.GetMeasurements': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/measurements`,
    paginated: true,
    local: async (p) => okStatus('Success', { data: await cptDeviceReadings(p, { onlyMeter: true }) }),
  },
  'PatientCpt.GetMeasurements2': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/measurements`,
    paginated: true,
    local: async (p) => okStatus('Success', { data: await cptDeviceReadings(p) }),
  },
  // Every page of the chart's billing cycles (wholeDirectory): main's SqlClass
  // answered with all of them, the API pages /cpt-entries (25 by default), and
  // one request filed a chart's first 25 cycles and silently dropped the rest
  // from CptPage - the billing history of a patient on several meters, which
  // passes 25 cycles within a year or two.
  //
  // In a total order (CPT_CYCLE_WALK), then filed as main's tables were
  // (cptPageTables): GetPatientCpt2 by code for CptPage, GetPatientCpt by meter
  // and code for the device page. The API sends a flat list, and a cycle names
  // its meter in `device_model` (there is no `device_id`), so filing by a
  // `device_id` put every cycle under '' and left each code its oldest cycle
  // only.
  'PatientCpt.GetPatientCpt': wholeDirectory({
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/cpt-entries`,
    query: () => CPT_CYCLE_WALK,
    paginated: true,
    shape: (page, p) => cptPageTables(page.items, p.pid, { byMeter: true }),
  }),
  'PatientCpt.GetPatientCpt2': wholeDirectory({
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/cpt-entries`,
    query: () => CPT_CYCLE_WALK,
    paginated: true,
    shape: (page, p) => cptPageTables(page.items, p.pid),
  }),
  // Meter enrolment under a CPT code (MX-06): main's GetMeterCpt read
  // `cpt_meters` (meterCptRows), not the billing cycles this used to read. The
  // chart is the path's: the `pid` the page sends is only the address,
  // resolved under the tenant scope. The API pages the list, as it does every
  // list of patient data, and main answered with every row, so every page is
  // read (allPages). With no chart in view the answer is no meters, and no
  // request is sent, as for any read.
  'PatientCpt.GetMeterCpt': {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/cpt-meters`,
    paginated: true,
    shape: (page) => meterCptRows(page.items),
    local: async (p) => {
      const patient = routeId(p.pid);

      return meterCptRows(patient === null ? [] : await allPages(`/patients/${patient}/cpt-meters`, {}));
    },
  },
  // Enrolment, not accrual: AddMeterCpt (the CPT page's Add Meter, once per
  // code in a paired set) and AddPatientCpt (the device page's code picker)
  // map a meter to a code - `cpt_meters` plus the `cpt_assignments` row the
  // ledger bills under - and open no billing cycle. They used to be sent to the
  // accrual endpoint, which invented a billable unit dated today. Idempotent:
  // the paired-set click and a re-provisioned device enrol nothing twice. No
  // ICD from the CPT page (main #326 made it nullable); AddPatientCpt's
  // `cost`, `status` and `cycleCount` are a cycle's, and there is none.
  'PatientCpt.AddMeterCpt': {
    method: 'POST',
    path: (p) => `/patients/${id(p.pid)}/cpt-meters`,
    body: (p) => ({
      device_model: p.meter,
      device_label: p.meterName || undefined,
      cpt_code: p.cpt,
      icd10_code: p.icd || undefined,
    }),
    shape: () => okStatus('Success'),
  },
  'PatientCpt.AddPatientCpt': {
    method: 'POST',
    path: (p) => `/patients/${id(p.pid)}/cpt-meters`,
    body: (p) => ({ device_model: p.meterid, cpt_code: p.cpt }),
    shape: () => okStatus('Success'),
  },
  'PatientCpt.MarkBilled': {
    method: 'PATCH',
    path: (p) => `/cpt-entries/${id(p.fid)}`,
    body: (p) => ({ is_billed: true, billed_on: dateOnly(new Date()), claim_reference: p.icd }),
    shape: () => okStatus('Success'),
    errorStyle: 'status',
  },
  // The org-wide CPT billing dashboard sends the whole caseload's ids and binds
  // a map keyed by patient (CptBilling.vue:217-245). The legacy looped every one
  // of them server-side (processClass.php:3191-3230); this table used to read
  // `(p.pids || [])[0]` and key the answer by that single id, so every patient
  // after the first vanished from a revenue screen with no error at all.
  //
  // `local` because "one call per patient" is exactly what the legacy did and
  // there is no collection endpoint that crosses charts — `cpt-entries` is
  // nested under `/patients/{patient}` because a billing cycle belongs to one.
  'PatientCpt.GetPatientCptBilling': {
    local: (p) => cptBillingForPatients(p.pids),
  },
  'PatientCpt.GetPatientCptBilling2': {
    // Same read. `GetPatCpt`/`GetPatCpt2` differed only in which pair of legacy
    // tables they hit (`cpt_billing` versus `cpt_billing2`), a live fork the
    // port collapsed into one `billing_entries` table.
    local: (p) => cptBillingForPatients(p.pids),
  },

  // `Insurance.SaveNewInsurance` is in UNMAPPED: main's save wrote a NURSE's
  // malpractice cover, and this entry filed it as a PATIENT's insurance policy.

  // --- Notifications -----------------------------------------------------
  'Provider.SendProviderEmail': {
    method: 'POST',
    path: (p) => `/users/${id(p.provider)}/emails`,
    body: (p) => ({ subject: p.subject || 'A message from your care team', body: p.message }),
    shape: () => okMessage('EmailSent'),
    errorStyle: 'message',
  },
  // Main's SendNewSubAccountEmail: the organization sending a Pending or
  // Expired person their invitation again. It revives an expired one, restores
  // what expiry took and has pacmny-auth mail a fresh single-use link. The
  // Add User form sends it straight after the create, which has already
  // invited; the AS keeps that link rather than killing it (`already_sent`).
  // With no AS configured nothing went out, which is not "Successfull". An
  // account with no invitation awaiting it is a 409 `not_pending` whose
  // sentence reaches the modal (main answered a nurse `NotPending`).
  //
  // `usertoemail` rides along as a tripwire, never as the address: the API
  // refuses (422) when it is not the person's own, so an id that names the
  // wrong person revives and mails nobody.
  //
  // The Home grid's patient resend names the patient by CHART id
  // (`patient_id`, which it sends only for that reason) — as a user id that
  // number is some other person. It goes to the patient's portal invitation,
  // which does the same for a patient: a pending or expired one is sent the
  // invitation again (revived with a fresh clock), and one already using the
  // portal is sent a password reset rather than demoted.
  'Account.SendNewSubAccountEmail': {
    method: 'POST',
    path: (p) => (p.patient_id
      ? `/patients/${id(p.patient_id)}/invitation`
      : `/users/${id(p.uniquenine)}/invitations`),
    body: (p) => (p.patient_id
      ? { send_email: true }
      : { kind: 'sub_account', email: p.usertoemail }),
    shape: (result) => (result && (result.status === 'undelivered' || result.delivered === false)
      ? { status: 'Fail', message: 'The invitation could not be sent: the sign-in service is not configured.' }
      : okStatus('Successfull')),
    errorStyle: 'message',
  },
  'Account.SendDualRoleEmail': {
    method: 'POST',
    path: (p) => `/users/${id(p.uniquenine || p.unique_ID)}/invitations`,
    body: (p) => ({ kind: 'dual_role', email: p.usertoemail }),
    shape: () => okStatus('Successfull'),
    errorStyle: 'message',
  },
  // Main's medication-order provider invitation (decision 15): the patient
  // themself or privilege 5 invites a prescriber who has no account. The API
  // creates the provider (Providers group only) and queues the link to this
  // chart until they accept; pacmny-auth mails the single-use link. The chart
  // is the one in view — `patientUniqueId` is the SPA's patient id — and the
  // sender, the organization and the group are the token's, never the body's.
  // Main's answer: code '1' when the mail went out, '0' when it did not.
  'MedicationAdmin.SendAccountCreationEmail': {
    method: 'POST',
    path: (p) => `/patients/${currentPatientId({ patientId: p.patientUniqueId })}/provider-invitations`,
    body: (p) => {
      const [first, ...rest] = String(p.verifyprovName || '').trim().split(/\s+/);

      return {
        first_name: first || '',
        last_name: rest.join(' ') || null,
        email: String(p.verifyEmail || '').trim(),
        npi: String(p.verifyprovNPI || '').trim() || null,
      };
    },
    shape: (result) => (result && result.delivered
      ? { status: '200-Successfull', code: '1', message: 'Account creation email sent.' }
      : { status: '200-Successfull', code: '0', message: 'The provider was invited, but the email could not be sent.' }),
    errorStyle: 'message',
  },

  // --- Help content ------------------------------------------------------
  'HelpContent.GetAllHelpContent': {
    method: 'GET',
    path: () => '/help-articles',
    query: () => ({ include: 'role_sections,org_section', per_page: 100 }),
    paginated: true,
    // Legacy shipped three parallel tables; the resource nests them, so unnest.
    shape: (page) => okStatus('success', {
      articles: page.items,
      // `role_name`, main's column, which the Role tab and the editor match on.
      roleSections: helpRoleSectionRows(page.items),
      orgSections: page.items
        .filter((a) => a.org_section_html)
        .map((a) => ({ help_id: a.help_id, body_html: a.org_section_html })),
    }),
  },
  'HelpContent.UpsertHelpArticle': {
    method: 'PUT',
    path: (p) => `/help-articles/${p.help_id}`,
    body: (p) => ({
      title: p.title,
      overview_html: p.overview_html,
      details_html: p.details_html,
      how_to_html: p.how_to_html,
      // The modal's `[{role_name, body_html}]` as the API's `{role: html}`; a
      // list went through unchanged and was refused (422) or, empty, deleted
      // every role section. Left out when the caller sent none.
      role_sections: helpRoleSectionMap(p.role_sections),
    }),
    shape: () => okStatus('success'),
    errorStyle: 'message',
  },
  'HelpContent.UpsertHelpOrgSection': {
    method: 'PUT',
    path: (p) => `/organization/help-sections/${p.help_id}`,
    body: (p) => ({ body_html: p.body_html }),
    shape: () => okStatus('success'),
    errorStyle: 'message',
  },

  // --- Audit -------------------------------------------------------------
  'AuditLog.GetAuditLogs': {
    // The page's keyon vocabulary (action words, FILE_<DOMAIN> types, API
    // names, `order_by: timestamp`, up to 10 000 rows from an offset) as the
    // compliance log's filters, read 100 rows a page, and each row in the
    // columns the page renders (api2/auditBridge.js). Passed through, the
    // page's own defaults were refused and every search showed nothing. The
    // Nursys names still translate to their paths (NURSYS_AUDIT_FILTERS).
    local: (p) => readAuditLogs(p || {}),
    errorStyle: 'status',
  },
  'AuditLog.GetAuditLogMethods': {
    method: 'GET',
    path: () => '/audit-events/facets',
    query: () => ({ facet: 'endpoint' }),
    shape: (facets) => okStatus('success', { records: (facets && facets.endpoint) || [] }),
  },
};

/**
 * Calls that carried `API_Meth` at the top level with no envelope — the flat
 * JSON posts, every multipart post (FormData has no envelope field), and the
 * few operations whose envelope name was spelled two ways.
 */
export const KEYON_FLAT_OPERATIONS = {
  // --- Orders (flat JSON) ------------------------------------------------
  GetOrders: {
    // Raw `OrderResource` rows used to come back, which OrdersPage and
    // HomeGrid read by legacy keys (`orderid`, `ordernumber`,
    // `orderdescription`, `med_medname`, the two signatures) and rendered
    // blank. Same shaper as the other order lists.
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/orders`,
    query: () => ({ include: 'medications,procedures,diagnoses', per_page: 100 }),
    paginated: true,
    shape: (page) => orderRows(page.items),
  },
  GetOrderDetails: {
    // keyon answered one line of the order in its legacy spellings — the
    // medication, or the procedure with the order's signatures — and the
    // pending-order popups read those keys (`medName`, `dosage`,
    // `procShorthand`, `diagnosis_code`, …), not the resource's.
    method: 'GET',
    path: (p) => `/orders/${orderRef(p)}`,
    query: (p) => ({ include: p.type === 'procedure' ? 'procedures' : 'medications' }),
    shape: (order, p) => orderDetailRow(order, p),
  },
  InsertNewOrder: {
    // OrderPopup's save: one `POST /orders` per ordered medication and per
    // ordered procedure, as main writes them, then the nurse's (or provider's)
    // signature the form captured when the person at the keyboard may fill
    // that half, each medication's administration times, and its pharmacy.
    // See `insertNewOrder` in clinicalBridge.js for the translation — order
    // type slug, one instant from date + time, the NPI-named prescriber, the
    // line keys, the staged diagnoses as `chart_diagnoses` — every one of which
    // the old pass-through got wrong, so this 422'd on every save.
    //
    // `code: '1'` only when every order exists; a refusal is `{code: 0,
    // error}` (with `saved_lines` when earlier lines were written), which
    // OrderPopup now tests (it used to test the HTTP status, which the bridge
    // always answers 200).
    local: (p) => insertNewOrder(p),
    errorStyle: 'code',
  },

  // --- Medications (flat JSON) -------------------------------------------
  GetPrePourMeds: {
    // The older pre-pour page's read, shaped and narrowed like the
    // `MedicationAdmin.` one: raw items listed a superseded line beside its
    // successor (MED-OM-02), and the page reads the legacy spellings
    // (`medName`, the capitalised status words) that only `medicationRows`
    // provides.
    method: 'GET',
    path: (p) => `/patients/${id(p.pid)}/medications`,
    query: () => ({ 'filter[is_prepour]': 1, 'filter[current]': 1, per_page: 100 }),
    paginated: true,
    shape: (page) => medicationRows(currentCourseRows(page.items, []).rows),
  },
  UpdatePrepourForActiveMedications: {
    method: 'PATCH',
    path: (p) => `/patients/${id(p.pid)}/medications/prepour-setting`,
    body: () => ({ is_prepour: true }),
    shape: () => okSuccessful(),
  },
  GetPatientMeds: {
    method: 'GET',
    path: (p) => `/patients/${id(p.pid || p.patientid)}/medications`,
    query: () => ({ include: MEDICATION_INCLUDES, per_page: 100 }),
    paginated: true,
    shape: (page) => records(medicationRows(page.items)),
  },

  // --- Notes (flat JSON) --------------------------------------------------
  SavePatientNote: {
    method: 'POST',
    body: (p) => noteBody(p),
    // As 'Note.SavePatientNote': `local` is the whole operation, so there is
    // no path or shape to read.
    local: (p) => saveNoteWithVideos(notePath(p.patientID), noteBody(p), p),
    errorStyle: 'msg',
  },

  // --- Provider account verification --------------------------------------
  //
  // Flat, because both live call sites post it under the `MedicationAdmin`
  // envelope (OrderPopup.vue:2877, AddMedicationForm.vue:6707) — which is what
  // keyon dispatched on (tswebhook.php:2366). The table used to key it as
  // `Provider.ProviderNPIAccountVerification` only, an envelope nothing sends,
  // so both buttons died with `BridgeUnmappedOperation` and an unhandled
  // rejection. The envelope-agnostic tier is where an operation belongs when
  // the spelling of its envelope is not the identifier.
  //
  // It is a **read**, and it was mapped to a write. The legacy branch asked
  // "does this NPI belong to somebody with an account here, and does the name
  // match?" and answered inline (tswebhook.php:2382-2425): no mail, no record.
  // `POST /providers/{provider}/verification-requests` is a different act - it
  // e-mails the provider and waits - it needs an address neither call site
  // sends, and it binds `providers.id` where the mapping put the 10-digit NPI,
  // so it could never have resolved either.
  //
  // `GET /provider-account-verification` is the question that was actually being
  // asked, for the people who ask it: whoever writes an order or a medication.
  // It replaced `GET /users?filter[npi]=`, which is users:read - administrators
  // only - so a provider or nurse writing an order was always refused, the
  // form's `.then` never got a verdict, and the "Account Not Verified" step
  // that leads to the provider invitation (SendAccountCreationEmail) could not
  // be reached. It answers the verdict and the matched account's id and
  // nothing else about the person; the name is compared by the API.
  ProviderNPIAccountVerification: {
    method: 'GET',
    path: () => '/provider-account-verification',
    query: (p) => ({ npi: p.verifyprovNPI, name: p.verifyprovName || undefined }),
    // The three legacy verdicts, preserved: verified, partially verified (the
    // account exists but the name on the order is missing or does not match
    // it - main answered 0.5-Partial for an empty name too), and no account (or
    // more than one holding the NPI). `missing` was the account/subaccount pair
    // the legacy handed back for the invitation that follows; the id is what
    // that flow needs now.
    shape: (verification) => {
      const verdict = verification && verification.verdict;

      if (verdict === 'verified') return okStatus('200-Successfull', { code: '1', message: 'Account Verified' });

      if (verdict === 'partial') {
        return okStatus('200-Successfull', {
          code: '0.5-Partial',
          message: 'Partial Verification \u2014 Please confirm and update account and try to cofirm again.',
          missing: { subaccountnumber: verification.user_id },
        });
      }

      return okStatus('200-Successfull', {
        code: '0',
        message: 'Account Not Verified',
        missing: [],
      });
    },
    errorStyle: 'status',
  },

  // --- Nursys (envelope spelled two ways) ---------------------------------
  //
  // `NursyInfo`/`NursysInfo` and `ManageNurseList`/`ManageNurseLList` are the
  // same envelope typed two ways across call sites, so these live here where
  // the lookup is envelope-agnostic rather than being duplicated above.
  //
  // Both answer in keyon's shapes, which main's nursysApi classifies: the bare
  // array of licence records or 'Result Not Found' for a lookup, 'Nurse
  // Added' / 'Nurse Not Added' for an enrolment, and 'No Server Response',
  // 'Rate Limited' or `{status:'Error', code:'not_configured'}` when Nursys
  // gave no answer. `{status:'Successful', data}` — what this used to answer —
  // is UNAVAILABLE to that classifier, so every lookup read as an outage. A
  // refusal by the gate rejects with keyon's sentence (nursysBridge.js).
  LookupNursesInfo: {
    local: lookupNursesInfo,
  },
  Add_Nurse: {
    local: addNurse,
  },

  // --- Documents (multipart) ---------------------------------------------
  AddNewPdf: {
    // Main's Documents-page upload of a person's credential - `subid` is the
    // org number, `externid` the person, `pdfType` one of main's four
    // categories (license, insurance, misc, other). `latest` makes a licence
    // or insurance the one the person's slot names. keyonFiles reads success
    // as a well-formed `file_uid` in the answer.
    //
    // No owner is no request: main answers an empty `externid` 403
    // (tswebhook.php AddNewPdf), and `/users/null/documents` is the bridge's
    // refusal. Never the session user or another id from the body in its place:
    // an upload filed under the wrong person becomes that person's licence.
    method: 'POST',
    path: (p) => `/users/${id(p.externid)}/documents`,
    upload: true,
    body: (p) => ({ type: p.pdfType, file: p.file, is_current: bool(p.latest) ? '1' : '0' }),
    shape: (document, p) => okStatus('Successful', uploadedFile(document, { latest: bool(p.latest) })),
    errorStyle: 'status',
  },
  UploadNurseFile: {
    // Main's immediate upload: the file is stored the moment it is picked,
    // ahead of the create or edit that will send its uid. `subaccnt` names the
    // person - for one the form is still creating, an id that is nobody's
    // yet, and the file waits for the create that binds it.
    method: 'POST',
    path: () => '/credential-documents',
    upload: true,
    body: (p) => ({ file: p.files, category: p.pdftype || undefined, subject_user_id: routeId(p.subaccnt) ?? undefined }),
    shape: (document) => okStatus('Successfull', { error: '', ...uploadedFile(document) }),
    errorStyle: 'status',
  },
  // The verify-nurse re-uploads point the pending nurse's slot at the file at
  // once (main's SetPendingNurseCredential), so they are current uploads.
  UploadNurseLicenseFile: {
    method: 'POST',
    path: (p) => `/users/${id(p.externId)}/documents`,
    upload: true,
    body: (p) => ({ type: 'license', file: p.nurseLicenseFile, is_current: '1' }),
    shape: (document) => okMessage('Updated', { status: '200 Successfull', code: 1, ...uploadedFile(document) }),
    errorStyle: 'message',
  },
  UploadNurseInsuranceFile: {
    method: 'POST',
    path: (p) => `/users/${id(p.externId)}/documents`,
    upload: true,
    body: (p) => ({ type: 'insurance', file: p.nurseInsuranceFile, is_current: '1' }),
    shape: (document) => okMessage('Updated', { status: '200 Successfull', code: 1, ...uploadedFile(document) }),
    errorStyle: 'message',
  },

  // --- Users (multipart) --------------------------------------------------
  CreateSubAccount: {
    // 464 lines of keyon branching on role; one `POST /users`. But the forms
    // that send this have already created the person through register() -
    // main's two stores, the port's one - so naming that person it completes
    // rather than creates (accountBridge.js says what is left to do); naming
    // nobody it creates, with every chosen group as `group_ids` parsed from
    // `additional_group_ids` and never `primary_group_name` (xltSi5PP), the
    // credential slots (a picked file or an immediate upload's uid, never
    // both) and the patients queued for a new caregiver. Who queued them is
    // the token's subject, so `assigned_by_unique_ID` is not forwarded.
    local: createSubAccount,
    errorStyle: 'message',
  },

  // --- Directives (multipart) ---------------------------------------------
  CreateDirectiveRecordUpload: {
    method: 'POST',
    path: (p) => `/patients/${id(p.patient_pid)}/directive-records`,
    upload: true,
    body: (p) => ({
      ingest_path: 'upload',
      relationship_to_patient: p.relationship_to_patient,
      relationship_other: p.relationship_other,
      is_legal_surrogate: p.is_legal_surrogate,
      authority_type: p.authority_type,
      document_authorized: p.document_authorized,
      form_label: p.form_label,
      document: p.file,
    }),
    shape: (record) => dataEnvelope(record),
    errorStyle: 'status',
  },

  // --- Messaging (multipart) ----------------------------------------------
  //
  // The endpoints take `multipart/form-data` with the structured payload in one
  // **JSON part** — `thread` for a new conversation, `message` for a reply —
  // alongside `files[]`. That is `MessagingRequest::mergeJsonPart`, and it is
  // the only way to send a list and a binary in one request without base64.
  //
  // The old mapping put the fields at the top level of the form instead, so
  // `participant_ids` reached PHP as the string `'12,13'` and `files` as a
  // single part (or, with no attachment chosen, the literal `"undefined"`).
  // Both are `array` rules, so every new thread and every reply carrying a file
  // answered 422 — and the call sites branch on `res.data.message == 'Inserted'`,
  // so most of them said nothing at all and the message was simply never sent.
  SendGroupMessage: {
    // A new thread and a reply to an existing one differ only by `group_id`.
    method: 'POST',
    path: (p) => (p.group_id
      ? `/message-threads/${id(p.group_id)}/messages`
      : '/message-threads'),
    upload: true,
    body: refusingUnsupportedNotify((p) => (p.group_id
      ? {
        // A reply carries no participant list: `participants` on this branch is
        // the thread's existing membership minus the sender
        // (FloatingMessageWidget.vue:1541), so re-sending it as
        // `additional_contact_ids` would re-add people who are already on the
        // conversation. Widening a thread is a separate act from replying to it.
        message: JSON.stringify({
          body: p.message,
          patient_focus_id: routeId(p.patient_focus_id) ?? undefined,
          // A finished video upload the message names (#334): main's
          // multipart field, a JSON list, carried in the JSON part.
          video_file_uids: videoFileUids(p.video_file_uids),
          // "Notify recipients" (#322): the thread's other participants.
          notify: messageNotify(p),
        }),
        files: fileList(p.files),
      }
      : {
        thread: JSON.stringify({
          subject: p.subject || undefined,
          body: p.message,
          participant_ids: idList(p.participants),
          patient_focus_id: routeId(p.patient_focus_id) ?? undefined,
          video_file_uids: videoFileUids(p.video_file_uids),
          notify: messageNotify(p),
        }),
        files: fileList(p.files),
      })),
    shape: async (result, p) => {
      const skipped = skippedRecipients(result);

      return okMessage('Inserted', {
        // Main counts the recipients it inserted for, and its group send
        // skips one who is not Active (the sender's own picks included), so
        // the count is who got the message and the widgets say "created with
        // N recipients" from it: a send that left someone out reads as the
        // partial send it is, as the send-separately mode's k of N does.
        participants_notified: Math.max(idList(p.participants).length - skipped.length, 0),
        ...(skipped.length ? { participants_skipped: skipped } : {}),
        // Main answers a send with the message's attachments as uid items, and
        // the widgets read them to know a video went on the message (#334,
        // unattachedUids). A reply's answer carries them; a new thread's
        // answer is the thread, so its first message's are read back.
        ...(result && Array.isArray(result.attachments)
          ? { thread_attachments: threadAttachmentItems(result.attachments) }
          : await postedThreadAttachments(result, p)),
      });
    },
    errorStyle: 'message',
  },
  SendInternalMessages: {
    // `internalcontacts` folds into `participant_ids`: it is a list of
    // subaccount numbers (InternalMessage.vue:1521) and the new-thread payload
    // has no `additional_contact_ids` field, while on the reply endpoint that
    // field's documented effect is "they become real participants" — so at
    // thread creation the two legacy lists are one concept.
    //
    // `additionalcontacts` is an e-mail **address** — the recipient's own, one
    // per request, from the widgets (FloatingMessageWidget.vue,
    // MobileMessageWidget.vue) — and a participant here is a user id resolved
    // under the tenant. Non-numeric entries drop out of `idList`.
    //
    // That is also what main does with it. keyon e-mails an address only when
    // `additionalcontacts` is an ARRAY (tswebhook.php SendInternalMessages,
    // `is_array`), and the live callers send one string, so no notice is sent;
    // and the notice it would send (SendInternalMessagingNotication) calls
    // EmailTemplates::CustomerEmail with four of its nine required arguments,
    // an ArgumentCountError on PHP 8 that would end the request before the
    // message was stored. There is no working capability to carry, so the port
    // e-mails no outside address about a conversation — which would also tell
    // an outside inbox that a clinical conversation exists.
    method: 'POST',
    path: () => '/message-threads',
    upload: true,
    body: refusingUnsupportedNotify((p) => ({
      thread: JSON.stringify({
        subject: p.subject || undefined,
        body: p.message,
        participant_ids: [...new Set([
          ...idList(p.recieversubaccountnumber),
          ...idList(p.additionalcontacts),
          ...idList(p.internalcontacts),
        ])],
        patient_focus_id: routeId(p.patient_focus_id) ?? undefined,
        // A finished video upload the message names (#334).
        video_file_uids: videoFileUids(p.video_file_uids),
        // "Notify recipients" (#322).
        notify: messageNotify(p),
      }),
      files: fileList(p.files),
    })),
    // The new thread's first message's attachments, as main answers a send
    // (#334): the widgets check a video they named is among them.
    shape: async (result, p) => okMessage('Inserted', {
      status: '200-Successfull',
      ...(await postedThreadAttachments(result, p)),
    }),
    errorStyle: 'message',
  },
  // Main's patient e-mail. The FormData names the recipient by address, not by
  // chart, so the chart is the one in view (or one the call names); the API
  // mails only an address on that patient's own record, so a mismatch is
  // refused rather than sent anywhere. The attachment is judged like any
  // upload and never stored (EmailAttachment). `fromEmail` and `providerName`
  // are not forwarded: the sender is the token.
  SendPatientEmail: {
    method: 'POST',
    path: (p) => `/patients/${currentPatientId(p)}/emails`,
    upload: true,
    body: (p) => ({
      email: JSON.stringify({ to: p.to || undefined, subject: p.subject, body: p.body }),
      file: p.file instanceof Blob ? p.file : undefined,
    }),
    shape: () => okMessage('EmailSent', { code: 1 }),
    errorStyle: 'message',
  },
  MarkMessagesAsRead: {
    method: 'PATCH',
    path: () => '/messages/read-state',
    body: (p) => ({
      message_ids: String(p.message_ids || '').split(',').filter(Boolean).map(Number),
      is_read: true,
    }),
    shape: (result, p) => okStatus('success', {
      updated_count: String(p.message_ids || '').split(',').filter(Boolean).length,
    }),
    errorStyle: 'message',
  },
  GetPatientGroups: {
    // Posted both as JSON under `PatientGroups` and as FormData; one entry
    // here answers both.
    method: 'GET',
    path: (p) => `/patients/${id(p.patient_id)}/message-groups`,
    query: (p) => ({ 'filter[owner_role]': bool(p.is_provider) ? 'provider' : 'patient' }),
    paginated: true,
    shape: (page) => okStatus('success', { groups: page.items }),
  },
};

/**
 * The retired Yii2 backend. Keyed `<route>#<action>`; a bare `#<action>` entry
 * matches the action on any route, which is what every entry here uses because
 * the action name was already unique across the 55-case dispatcher.
 */
/**
 * The chart currently in view, and the signed-in user.
 *
 * Several retired Yii2 routes took no patient at all — `/session/invitePatient`
 * and `/contacts/index` are the clearest — because the Yii2 session held one.
 * There is no server session any more: the access token identifies the user and
 * nothing else, which is the point. The chart lives in the SPA, so the bridge
 * reads it from there when a legacy call omits it.
 *
 * **The chart in view, and nothing else.** Twenty operations resolve their
 * chart here, writes among them (contacts, notes, devices, invitations,
 * e-mails, the report save), so with no chart in view the answer is null: the
 * path comes out as `/patients/null/...` and `legacyBridge` fails the write
 * loudly (`BridgeUnresolvedTarget`). The chart PDF and C-CDA uploads are the
 * exception: their path names no chart, they name it in the body through
 * `uploadPatientId` below, and with none to name the API refuses them (422),
 * not the bridge. No fallback
 * may stand in for the chart. The tab's remembered id outlives a refused chart
 * switch, so falling back to it sent a write to the chart the clinician had just
 * left; and the signed-in person's own record is a decision for the one call
 * site where main makes it (`uploadPatientId`), not for every caller.
 *
 * A chart the legacy payload names explicitly wins — the API re-authorizes it
 * like any other. pacmny-be's uploads arrive as FormData, whose fields are not
 * properties, so those are read with `get()`.
 *
 * Imported inside the function on purpose. `bridgeMap` is loaded while the
 * bridge installs itself, which happens before Pinia is created; a top-level
 * import would either capture an uninitialised store or make the cycle real.
 */
function currentPatientId(p) {
  const named = (field) => ((typeof FormData !== 'undefined' && p instanceof FormData)
    ? p.get(field)
    : p && p[field]);
  const explicit = id(named('patientId') || named('patientid') || named('pid') || named('patient_id'));

  if (explicit !== null) return explicit;

  // Synchronous by necessity — the callers want an id back, not a promise —
  // so this one stays a deferred `require` rather than a dynamic `import()`.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { usePatientContextStore } = require('@/stores/patientContext');

  return id(usePatientContextStore().patient && usePatientContextStore().patient.id);
}

/**
 * The chart a chart PDF or C-CDA upload is filed under: the chart in view or,
 * with none open, the signed-in person's own record.
 *
 * Main's rule, kept at the upload rather than in `currentPatientId`: pacmny-be's
 * session chart outside a chart is the signed-in person's own (`LoginAction`,
 * `ExitPatientChartAction` set it back to the agent), so `uploadPdf` and
 * `uploadCcda` file a no-chart upload there, and refuse only when there is no
 * record at all. In the port only a patient has a record of their own
 * (`me.patient_id`); a member of staff with no chart open names none, and the
 * API refuses the upload (422) instead of filing it anywhere.
 */
function uploadPatientId(p) {
  const inView = currentPatientId(p);

  if (inView !== null) return inView;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useSessionStore } = require('@/stores/session');
  const me = useSessionStore().me;

  return id(me && me.patient_id);
}

/** The signed-in account's type ('provider', 'registered_nurse', ...), or '' before the session has one. */
function signedInAccountType() {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useSessionStore } = require('@/stores/session');
  const session = useSessionStore();

  return String((session.me && session.me.account_type) || session.accountType || '');
}

/** The one file part FileUpload.vue sends, `file-field` (pacmny-be's name for it). */
function fileUploadPart(p) {
  return (typeof FormData !== 'undefined' && p instanceof FormData) ? p.get('file-field') : p && p['file-field'];
}

/**
 * A contact form, minus the SPA's own bookkeeping.
 *
 * No renaming: `StorePatientContactRequest` already aliases the legacy names
 * (`dob`, `relation_type`, `phone_primary`), so the mapping lives on the server
 * where the validation rules can see it. `isNew` and `id` are the SPA's list
 * bookkeeping and are not fields.
 */
/**
 * Enter a patient's chart — main's `/session/enterPatientChart`.
 *
 * **Viewing a chart, not becoming the patient** (decision D4, main's PR #249).
 * Main parks the signed-in clinician in the session's `agentid` and resolves
 * every authorization, attribution and rate limit through them, so a nurse in
 * a chart keeps her own privileges and texts as herself. This entry used to
 * run an RFC 8693 act-as exchange instead, which swapped the SPA's token for
 * one whose subject was the patient: the nurse was then bounded by the
 * patient's permissions (no vitals, no medication administration) and every
 * message she sent was from the patient. The token never changes now. The
 * chart is selected in the patient context, which asks the API to open it
 * (`POST /patients/{id}/chart-view`): the chart-entry rule is checked there
 * and the opening is audited as the clinician.
 *
 * Main refuses to nest: a second enter while a chart is open answers
 * "Already viewing a patient chart" until the first is exited. Re-entering
 * the chart already open is not nesting, and succeeds.
 */
async function enterChart(p) {
  const patientId = id(p && (p.id ?? p.patientId ?? p.patient_id ?? p.mid ?? p.pid));

  if (patientId === null || Number.isNaN(patientId)) {
    return { status: 'Error', message: 'No patient was named.' };
  }

  const { usePatientContextStore } = await import('@/stores/patientContext');
  const chart = usePatientContextStore();

  if (chart.viewing && String(chart.patientId) !== String(patientId)) {
    return { status: 'Error', message: 'Already viewing a patient chart' };
  }

  try {
    await chart.select(patientId);
  } catch (e) {
    // One sentence for every refusal, as the API gives one: "not your
    // patient" and "no such chart" must not be told apart by the message.
    return { status: 'Error', message: 'You may not view that patient\'s chart.' };
  }

  return { status: 'OK' };
}

/**
 * Leave the chart — main's `/session/exitPatientChart`, which answers
 * "Not viewing a patient chart" when there is none.
 *
 * An act-as token can only exist if somebody switched the exchange on at the
 * provider; if one is held, leaving also hands it back, so nobody stays the
 * patient after "exit" has been pressed.
 */
async function exitChart() {
  const [{ usePatientContextStore }, { useSessionStore }] = await Promise.all([
    import('@/stores/patientContext'),
    import('@/stores/session'),
  ]);

  const chart = usePatientContextStore();

  if (useSessionStore().isImpersonating) {
    const { default: oidc } = await import('@/auth/oidcClient');

    oidc.revertToSelf();
    await reloadSessionAfterSubjectChange();

    return { status: 'OK' };
  }

  if (!chart.viewing) {
    return { status: 'Error', message: 'Not viewing a patient chart' };
  }

  await chart.leave();

  return { status: 'OK' };
}

/** The providers `provAssignmentById` adds and removes, one call each. */
async function assignProvidersById(p) {
  const patientId = id(p && p.patientId);

  if (patientId === null || Number.isNaN(patientId)) {
    return { status: 'Error', message: 'No patient was named.' };
  }

  const ids = (list) => (Array.isArray(list) ? list : []).map(id).filter((v) => v !== null && !Number.isNaN(v));

  for (const userId of ids(p.assigned)) {
    await api.post(`/patients/${patientId}/care-team/providers`, { user_id: userId });
  }

  for (const userId of ids(p.unassigned)) {
    await api.delete(`/patients/${patientId}/care-team/providers/${userId}`);
  }

  return okStatus('OK');
}

/**
 * `getAllData.manager`: the signed-in staff member, in the legacy field names.
 *
 * pacmny-be filled this from the session's agent (`LA_user` of the person at
 * the keyboard): first and last name, `externid`, `externid2` and both phones.
 * `externid` is the user id the bridge addresses people by (`/users/{id}`), so
 * a signature check or a directives actor names this person; `externid2` stays
 * the organization's account number, as the legacy value was.
 */
function staffManager(user, org) {
  const first = user.first_name || '';
  const last = user.last_name || '';
  const name = (user.full_name || `${first} ${last}`).trim();

  return {
    id: user.id,
    externid: user.id,
    externId: user.id,
    externid2: org.number,
    externId2: org.number,
    name,
    providername: name,
    firstName: first,
    lastName: last,
    // pacmny-be's agent carried its account type in main's words, and
    // NotesPage opens the note popup on it (noteTypeForAccountType): without
    // it every staff member's note defaulted to Nursing, a Provider's
    // included (MX-07). The slug stays beside it.
    accounttype: legacyAccountType(user.account_type),
    account_type: user.account_type || '',
    phone1: user.phone || '',
    phone2: user.phone_secondary || '',
    email: user.email || '',
  };
}

/**
 * Rebuild everything keyed to who the session is.
 *
 * The token changed, so the profile, the permission set and the open chart all
 * belong to somebody else now. Leaving any of them stale would show one
 * person's chart under another's authority, which is worse than either alone.
 */
async function reloadSessionAfterSubjectChange() {
  const [{ useSessionStore }, { usePatientContextStore }] = await Promise.all([
    import('@/stores/session'),
    import('@/stores/patientContext'),
  ]);

  const chart = usePatientContextStore();

  chart.clear();
  chart.initialised = false;

  await useSessionStore().hydrate();
  await chart.initialise();
}

/**
 * Edit User's one licence field (main's `proffesionalLicense`) as the field
 * PATCH /users reads: the NPI for a Provider or Administrator — main writes it
 * to `providers.npinumber` for exactly those — an aide's HHA certificate for a
 * Home Health Aid (Edit User prefills the field with `certificatenumber`, and
 * main keeps it in `nurses.certificatenumber`), and the state professional
 * licence for everyone else. Sent as a state licence, an aide's certificate
 * became a state_professional licence row carrying her certificate number.
 * Empty is no change, never a clear: the form sends the field whether or not
 * it was touched.
 */
function editedLicence(p) {
  const value = String(p.proffesionalLicense ?? '').trim();

  if (value === '') return {};

  const type = String(p.accountType || '').trim();

  if ((type === 'Provider' || type === 'Administrator') && /^\d{10}$/.test(value)) {
    return { npi: value };
  }

  if (isAideAccountType(type)) {
    return { certificate_number: value };
  }

  return { professional_license: value };
}

/**
 * A patient's gender as the API's enum (male, female, other, unknown) from
 * main's form codes (`@/data/genders`: 1 Male, 2 Female) or words, or
 * undefined - no change - for a blank or unrecognised one.
 */
function patientGender(value) {
  const v = String(value ?? '').trim().toLowerCase();
  const byCode = { 1: 'male', 2: 'female', m: 'male', f: 'female' };

  if (byCode[v]) return byCode[v];

  return ['male', 'female', 'other', 'unknown'].includes(v) ? v : undefined;
}

function contactBody(p) {
  const body = { ...(p || {}) };

  delete body.isNew;
  delete body.id;
  delete body.contactId;

  return body;
}

/**
 * The signed-in user (`GET /me`), and never an id out of a legacy payload.
 *
 * A body's `userId` / `user_id` / `id` names whatever record the form was
 * built around — the chart's patient, a roster row — not the person at the
 * keyboard (decision D4), so none of them may stand in for the session. An
 * operation that acts on another person names that person itself (AddNewPdf's
 * `externid`) and is refused when it does not.
 */
function currentUserId() {
  // Synchronous by necessity, as in `currentPatientId` above.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useSessionStore } = require('@/stores/session');

  return id(useSessionStore().userId);
}

// --------------------------------------------------------------------------
// Helpers for the locally-resolved operations
//
// These make their own calls, so they live with `api` rather than in
// `bridgeShapes`, which is pure. The shaping each of them does is in
// `bridgeShapes`; what is here is the round trips.

/**
 * Every page of a paginated collection.
 *
 * `ResourceQuery::MAX_PER_PAGE` is 100, and several legacy operations answered
 * one payload covering a whole date range — the MAR grid is the clear case: a
 * fortnight of a real chart is thousands of cells, and reading only the first
 * page would render an incomplete MAR that looks complete.
 *
 * `maxPages` is a stop, not a limit: a caller that hits it has asked for
 * something no legacy screen ever displayed, and looping forever against a
 * miscounted `last_page` is worse than a truncated answer.
 */
async function listAll(path, params = {}, maxPages = 25) {
  const rows = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await api.list(path, { ...params, page, per_page: 100 });

    rows.push(...(result.items || []));
    lastPage = Number(result.meta && result.meta.last_page) || 1;
    page += 1;
  } while (page <= lastPage && page <= maxPages);

  return rows;
}

/**
 * Main's GetMeterCpt answer from the chart's meter enrolments (MX-06): one
 * `{cpt, meter, meterName, icd}` row each, which CptPage files under
 * `assignedMeters[m.cpt]` and matches on `m.meter`.
 */
function meterCptRows(meters) {
  const rows = (meters || []).map((m) => ({
    id: m.id,
    cpt: m.cpt_code,
    meter: m.device_model,
    meterName: m.device_label || '',
    icd: m.icd10_code,
  }));

  return okStatus('Success', { count: rows.length, result: rows });
}

/**
 * The order a chart's billing cycles are walked in: a total one, the id last
 * (allPages, and listAll for the billing dashboard). Cycles of different codes
 * share a start date - the rollover opens a patient's cycles together - so a
 * tie may come back in another order on page 2, and a cycle on the boundary
 * would be read twice or not at all.
 */
const CPT_CYCLE_WALK = { sort: 'cycle_start_on,id' };

/**
 * The device readings the CPT page's cycle calendar draws (GetMeasurements for
 * one meter, GetMeasurements2 for the chart), as rows of main's `foradata`, for
 * ONE cycle: the one the page has on screen, named by `firstDate` and
 * `lastDate` ('YYYY-MM-DD', the dates CptDesc prints as its Date Range).
 *
 * Main read every row of the chart in one request. The API's measurement list
 * is the chart's whole record (every source, newest first, a cursor) at 100 a
 * request, so the same read there is a request per 100 readings of years of
 * them: the request-volume detector counts them like any others (60 a minute),
 * and each page writes its own `measurement` audit row where main wrote one per
 * read. The calendar draws a cycle's days and no others, so this reads those:
 *
 *  - only the days of that cycle, from the day before its first to the second
 *    day after its last (cptCycleSpan), every page of them (listAllMeasurements)
 *    because a calendar that has most of a cycle's days reads as a patient who
 *    did not measure; a page that fails fails the read. No cycle named, or one
 *    that is not a run of days, asks for nothing: the chart's readings are not
 *    the fallback for a page that did not say which cycle it is showing;
 *  - only Fora device readings (`filter[source]=fora`, which is both stored
 *    spellings of it): `foradata` held nothing else, so the chart's manual
 *    vitals, C-CDA results and report extractions are not pulled for a
 *    calendar that cannot draw them. The rows carry the `MDeviceID` CptPage
 *    matches to a meter, which the API's rows do not.
 *
 * One meter (GetMeasurements) is the device its key names - the serial number,
 * else the row id, which is how the pages key a meter - and the API filters on
 * the device's row id, not on that key. A meter the chart does not have, or
 * none named, matches nothing, as `MDeviceID = :meterid` did.
 *
 * What it costs, whatever else the chart holds: the devices (a page in
 * practice) and a request per 100 readings of the cycle - two requests for a
 * cycle whose readings fit one page, never a read of the chart's other cycles.
 * The page asks once per cycle it shows, not once per visit to it.
 */
async function cptDeviceReadings(p, { onlyMeter = false } = {}) {
  const patient = routeId(p.pid);
  const meter = onlyMeter ? String(p.meterid ?? '') : '';
  // Both ends, or no cycle: a start alone would name one day of a month.
  const span = p.firstDate && p.lastDate
    ? cptCycleSpan([{ cycle_start_on: p.firstDate, cycle_end_on: p.lastDate }])
    : null;

  if (patient === null || span === null || span.to <= span.from || (onlyMeter && meter === '')) return [];

  const { items: devices } = await listAllPatientDevices(patient);
  const meterKeys = new Map(devices.map((device) => [Number(device.id), device.serial_number || String(device.id)]));
  const query = { 'filter[source]': 'fora', from: span.from, to: span.to };

  if (onlyMeter) {
    const device = devices.find((candidate) => meterKeys.get(Number(candidate.id)) === meter);

    if (!device) return [];

    query['filter[device_id]'] = device.id;
  }

  return (await listAllMeasurements(patient, query)).map((reading) => foradataRow(reading, meterKeys));
}

/**
 * The CPT billing dashboard's per-patient map, over the whole caseload.
 *
 * One request per patient, as the legacy did server-side. A chart the caller
 * cannot see is omitted rather than failing the screen — that is also what the
 * legacy did with a patient who had no cycles (`GetPatientCptBilling2` only
 * added a key when there was something in it, which is what the template's
 * `patient.externId in cptData` guard is written against). Any other failure
 * propagates, so a broken read is not silently rendered as an empty dashboard.
 */
async function cptBillingForPatients(pids) {
  // Keyed by the id the caller sent, not by the normalised one: the grid looks
  // rows up with `patient.externId in cptData`, so a key that has been through
  // `Number()` and back would miss on any spelling that does not survive the
  // round trip.
  const wanted = [...new Set((pids || []).map((pid) => String(pid ?? '')).filter(Boolean))];
  const map = {};

  // Six at a time, which is the browser's own per-host connection limit — a
  // whole caseload dispatched at once would queue behind that anyway while
  // holding every request open. The legacy ran the same loop serially inside
  // one PHP request.
  for (let offset = 0; offset < wanted.length; offset += 6) {
    const answered = await Promise.all(wanted.slice(offset, offset + 6).map(async (key) => {
      const patient = routeId(key);

      if (patient === null) return null;

      try {
        // No `view=billing`: that view selects only cycles a biller can submit,
        // and this grid renders all three of Not Billable / Billable / Billed —
        // the legacy read both the open and the history table.
        //
        // In a total order (CPT_CYCLE_WALK), as the CPT page reads the same
        // cycles: the endpoint's own order is the start date alone, so on a
        // chart with more than 100 cycles the ones that tie on the page
        // boundary may be on both pages or on neither, and a cycle read on
        // neither is a billable one missing from a revenue screen.
        return [key, await listAll(`/patients/${patient}/cpt-entries`, CPT_CYCLE_WALK)];
      } catch (error) {
        if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return null;

        throw error;
      }
    }));

    answered.forEach((entry) => {
      if (entry === null) return;

      const [key, rows] = entry;
      const buckets = cptBillingBuckets(rows, key);

      // Empty patients are left out, so the accordion does not render a tab per
      // patient with nothing under it.
      if (Object.keys(buckets.cptList).length > 0) map[key] = buckets;
    });
  }

  return map;
}

/**
 * Whether a licence search names no state. The contact card's state select
 * rests on "Select one", whose value is `0`, and a cleared field is '' or
 * null; a search by name carries one of those. None of them is a state that
 * was chosen. Anything else is.
 */
const noStateNamed = (value) => value === undefined
  || value === null
  || ['', '0'].includes(String(value).trim());

/** The state a search names: the first of the two spellings callers use that is not "none". */
const namedState = (p) => [p.state, p.licenseState].find((value) => !noStateNamed(value));

/**
 * A chosen state as the two-letter code `license_state` holds, or undefined
 * when it is not one. Any case and stray spaces are read; an id, a name or a
 * code of another length is not.
 *
 * The contacts screens bind their state select to `allData.states`, which is
 * `STATES` (data/referenceLists.js, through the `getAllData` getter in
 * store/modules/login.js): each entry's `id` is that code, and so is its
 * `abbr`. UserContact `queryContact` sends the chosen entry's `abbr`, so a
 * code is what arrives. Main's list bound a numeric id with the code beside it
 * (Utility::getStates: New York is 33, and DbUtility::findUsersByLicenseAndStateOrName
 * compared that id); an id that reaches this point is a stale value, and
 * nothing in the API can match it against `license_state`.
 */
function licenceState(value) {
  const text = String(value ?? '').trim().toUpperCase();

  return /^[A-Z]{2}$/.test(text) ? text : undefined;
}

/** The licence search results, in the spellings the contacts result list binds. */
const licenceRows = (rows) => (rows || []).map((row) => ({
  ...row,
  firstName: row.user?.first_name ?? '',
  lastName: row.user?.last_name ?? '',
  license: row.license_number,
  // The result list shows `result.state` through `allData.states` (UserContact
  // `gg`), whose ids are the codes, so the code is emitted under both names:
  // `licenseState` is what `applyFoundContact` copies onto the contact being
  // edited.
  licenseState: row.license_state,
  state: row.license_state,
}));

/** States the licence search already reported as unreadable, so the console hears of each once. */
const unreadableStates = new Set();

/**
 * Main's `license/query` (LicenseForm::getLicenses), the contacts screens'
 * licensed-individual search: a licence number WITH its state, or a first AND
 * last name, answered with the people who hold that licence or that name.
 *
 * It is `GET /professional-licences` filtered by `license_number`, `state`,
 * `first_name` and `last_name`, which are exactly the endpoint's allowed
 * filters (ProfessionalLicenceController::index): any other `filter[...]` key
 * makes Spatie answer 400 `InvalidFilterQuery`. Anyone without `users:read` is
 * answered only a licence number WITH its state or a first AND last name (422
 * `lookup_too_broad`), within their organization, 20 to a page, names and
 * licence facts only.
 *
 * A state that was chosen and cannot be read (licenceState) is not "no state":
 * the search is not made and nobody is found, and a name beside it does not
 * stand in. Sent on without the state, "this licence in New York" became "this
 * licence number anywhere" - refused for a patient or a family contact, and for
 * an administrator (who may list freely) every holder of the number, shown as
 * if the state had filtered them. Main found nobody either: a state it did not
 * know matched no `ZV_license_state`, with a name beside it or not.
 *
 * `local` because that decision is made before any request; the path, query
 * and shape are the ones in `read`, once.
 */
function licenceLookup() {
  const read = {
    method: 'GET',
    path: () => '/professional-licences',
    query: (p) => ({
      'filter[license_number]': p.license || undefined,
      'filter[state]': licenceState(namedState(p)),
      'filter[first_name]': p.first || p.firstName || undefined,
      'filter[last_name]': p.last || p.lastName || undefined,
      include: 'user',
      per_page: 50,
    }),
    paginated: true,
    shape: (page) => okStatus('OK', { data: licenceRows(page.items) }),
  };

  return {
    ...read,
    local: async (p) => {
      const state = namedState(p);

      if (state !== undefined && licenceState(state) === undefined) {
        const shown = String(state).trim().slice(0, 40);

        if (!unreadableStates.has(shown)) {
          unreadableStates.add(shown);
          // eslint-disable-next-line no-console
          console.warn(
            `[legacyBridge] "license/query" was given the state ${JSON.stringify(shown)}, which is not a `
            + 'two-letter state code, so it answered nobody instead of searching without the state. '
            + 'UserContact sends the chosen option\'s `abbr`; a state list whose entries have none needs one.',
          );
        }

        return okStatus('OK', { data: [] });
      }

      return read.shape(await api.list(read.path(p), read.query(p)), p);
    },
  };
}

export const BE_OPERATIONS = {
  '#ping': {
    method: 'GET',
    path: () => '/me',
    shape: () => okStatus('OK'),
  },
  '#requestVitalSignsCodes': {
    method: 'GET',
    path: () => '/vital-sign-codes',
    paginated: true,
    // Main's answer is the vocabulary rows' integer ids, which the pages
    // `parseInt`; the readings are grouped under the same integers
    // (api2/vitalsBridge.js). Objects here made every code NaN.
    shape: (page) => okStatus('OK', { data: { codes: vitalSignCodeKeys(page.items) } }),
  },
  '#searchNpi': {
    method: 'GET',
    path: () => '/providers',
    query: (p) => ({ 'filter[search]': p.query || p.search, per_page: 25 }),
    paginated: true,
    shape: (page) => dataEnvelope(page.items),
  },
  '#resolveNpi': {
    method: 'POST',
    path: () => '/provider-resolutions',
    body: (p) => ({ npi: p.npi, first_name: p.first_name, last_name: p.last_name }),
    shape: (provider) => (provider && provider.id
      ? { id: provider.id }
      : okMessage('npiNotInCatalog')),
  },
  // The save modal's pickers list `{ccd, desc}` and send the `ccd` back as the
  // report's code; the vocabulary rows are `{code, display}`, so every result
  // rendered as a blank line that selected nothing.
  '#searchCategory': {
    method: 'GET',
    path: () => '/vocabularies/categories',
    query: (p) => ({ q: p.description }),
    paginated: true,
    shape: (page) => ({ categories: vocabularyChoices(page.items) }),
  },
  '#searchProcedure': {
    method: 'GET',
    path: () => '/vocabularies/procedures',
    query: (p) => ({ q: p.description }),
    paginated: true,
    shape: (page) => ({ procedures: vocabularyChoices(page.items) }),
  },

  // --- CDA ----------------------------------------------------------------
  '#ccda': {
    method: 'POST',
    path: () => '/cda-documents',
    upload: true,
    // FileUpload.vue's multipart, renamed to the API's two fields. Everything
    // else in it — `action-field`, the CSRF pair — was pacmny-be's and is left
    // behind.
    body: (p) => ({ file: fileUploadPart(p), patient_id: uploadPatientId(p) }),
    // The uid, not the sequential id: whatever the caller does with it goes
    // through `fileRef`, and a sequential id is a key that walks the
    // organization's other documents. (FileUpload.vue no longer passes it to
    // `#requestAutoAddCda`: the upload queues the C-CDA's one scan itself.)
    shape: (cda) => okMessage('autoAddedCdaMessage', { id: cda && (cda.public_id || cda.id) }),
    // pacmny-be's two upload refusals (OnlineController CdaExistsException,
    // InvalidFileFormatException), in its words: src/api/cdaApi.js turns them
    // into "CDA is already uploaded." and "Invalid CDA file format.". Any other
    // refusal stays a failure.
    refusals: {
      cda_exists: { message: 'cdaExists' },
      invalid_cda: { message: 'invalidCdaFileMessage' },
    },
  },
  // A stored file is addressed by its 32-hex uid wherever the caller holds one
  // (`fileRef`); the numeric id the pacmny-be screens were built on still
  // resolves on these routes, and `id()` would have turned a uid into `NaN`.
  '#requestAutoAddCda': {
    method: 'POST',
    path: (p) => `/cda-documents/${fileRef(p.id)}/scan`,
    shape: () => okMessage('cdaScanedMessage'),
  },
  '#requestCommitAutoAddCda': {
    method: 'POST',
    path: (p) => `/cda-documents/${fileRef(p.id)}/commit`,
    shape: () => okMessage('clearedCdaMessage'),
  },
  '#downloadCda': {
    method: 'GET',
    path: (p) => `/cda-documents/${fileRef(p.id)}/content`,
    download: true,
    shape: (file) => file.blob,
  },
  '#deleteCda': {
    method: 'DELETE',
    path: (p) => `/cda-documents/${fileRef(p.id)}`,
    shape: () => okStatus('OK'),
  },
  '#cdaMedicationImport': {
    method: 'POST',
    path: (p) => `/cda-documents/${fileRef(p.cdaId)}/medication-import-fields`,
    // The side bar's items (`{key, htmlname, lo, hi, name}`) as the API takes
    // them (`{key, html_name, date, end_date}`), and its answer back as main's,
    // keyed by the side bar's key with the RxNorm and NDC of any of the
    // medication's codes. Per row, not per name: two courses of one drug share an
    // anchor, and the API tells them apart by the key and the start and end days.
    body: (p) => ({ items: cdaMedicationImportItems(p.items) }),
    shape: (result, p) => okStatus('OK', { data: { fields: cdaMedicationImportFields(result, p.items) } }),
  },

  // --- PDF and reports ----------------------------------------------------
  '#pdf': {
    method: 'POST',
    path: () => '/pdf-documents',
    upload: true,
    // As `#ccda`.
    body: (p) => ({ file: fileUploadPart(p), patient_id: uploadPatientId(p) }),
    // The uid, as `#ccda` answers.
    shape: (pdf) => okMessage('pdfUploadedMessage', { id: pdf && (pdf.public_id || pdf.id) }),
  },
  // The extraction screen's three calls carry one shape that the API does not
  // take: a drawn selection in screen pixels, which main's server placed on its
  // page. pdfExtractionBridge.js places it, from the page sizes the viewer
  // reports, and says why.
  '#getGrabbedImage': {
    // PdfDoc sends `{pdfId, source, destination}` (the PDF as `pdfId`); the API
    // answers the picture as `image`, main's screen reads it as `url`. One
    // request per page the selection touches, so it is not a path.
    local: (p) => grabImage(p),
  },
  '#getPreview': {
    // PdfExtraction sends `{id, extractions}` (the PDF as `id`, not `pdfId`),
    // and pdfApi.getPreview decodes a base64 string; the API streams the PDF.
    local: (p) => previewDocument(p),
  },
  '#saveExtraction': {
    // The save modal (PdfExtractionSaveModal) sends `{id, date, npi,
    // extractions, procedure}` — the PDF as `id`, no patient, and since main's
    // #237 no document type. This read `pdfId`/`patientId`/`reportDate`/
    // `procedureCode`, none of which the modal sends, so no report could be
    // saved. The chart is the SPA's open one, as pacmny-be's was the session's.
    // Its `extractions` are the grab requests PdfDoc built (the record of each
    // extraction), which the API reads as pages.
    local: (p) => saveExtraction({ ...p, extractions: reportExtractions(p.extractions) }, currentPatientId(p)),
    errorStyle: 'message',
  },
  '#requestAutoAddReport': {
    method: 'POST',
    path: (p) => `/pdf-documents/${fileRef(p.id || p.pdfId)}/report-draft`,
    body: (p) => ({ patient_id: id(p.patientId) }),
    shape: (report) => okStatus('OK', { data: report }),
  },
  '#requestCommitAutoAddReport': {
    method: 'POST',
    path: (p) => `/reports/${id(p.id)}/commit`,
    shape: () => okStatus('OK'),
  },

  // ========================================================================
  // Yii2 controller routes.
  //
  // Keyed by route rather than by action: these are ordinary controller
  // endpoints, and several share an action name (`/contacts/rename` and
  // `/contacts/set-primary-contact` both send `renameContact`), so the action
  // alone does not identify the operation.
  //
  // Until the adapter matched the whole `index.php` base these never reached
  // the bridge at all — they went to a container that no longer exists and
  // surfaced as a proxy failure.
  // ========================================================================

  // --- Contacts -----------------------------------------------------------
  '/contacts/index#getContacts': {
    method: 'GET',
    path: (p) => `/patients/${currentPatientId(p)}/contacts`,
    paginated: true,
    shape: (page) => okStatus('OK', { data: page.items }),
  },
  '/contacts/add#createContact': {
    method: 'POST',
    path: (p) => `/patients/${currentPatientId(p)}/contacts`,
    body: (p) => contactBody(p),
    shape: (contact) => okStatus('OK', { data: contact }),
  },
  '/contacts/update#updateContact': {
    method: 'PATCH',
    path: (p) => `/patients/${currentPatientId(p)}/contacts/${id(p.contactId || p.id)}`,
    body: (p) => contactBody(p),
    shape: (contact) => okStatus('OK', { data: contact }),
  },
  '/contacts/rename#renameContact': {
    method: 'PATCH',
    path: (p) => `/patients/${currentPatientId(p)}/contacts/${id(p.contactId || p.id)}`,
    body: (p) => ({ label: p.label ?? p.name ?? p.newName }),
    shape: (contact) => okStatus('OK', { data: contact }),
  },
  '/contacts/set-primary-contact#renameContact': {
    method: 'PUT',
    path: (p) => `/patients/${currentPatientId(p)}/contacts/${id(p.contactId || p.id)}/primary`,
    shape: (contact) => okStatus('OK', { data: contact }),
  },
  '/contacts/delete#deleteContact': {
    method: 'DELETE',
    path: (p) => `/patients/${currentPatientId(p)}/contacts/${id(p.contactId || p.id)}`,
    shape: () => okStatus('OK', { data: null }),
  },

  // --- Notes --------------------------------------------------------------
  // pacmny-be's NotesController is the note popup's DRAFT, not the chart's
  // notes: `/notes/index`, `/update` and `/delete` read, replace and discard
  // one `frontendcache2` row per user and patient (str1 = 'notes'). Mapped as
  // committed-note edits, update and delete had no note id to address, built
  // `/notes/null` and threw, so every "Save Draft" was lost. They go to the
  // caller's own draft on the chart the popup names (`patientid`, the same
  // field the note save addresses). The `userid` main sent is not forwarded:
  // the author is the token's, which is what closes main's IDOR on draft text.
  '/notes/index#getNotes': {
    method: 'GET',
    path: (p) => `/patients/${id(p.patientid)}/note-draft`,
    // Main answered the frontendcache2 rows: `data` the encounter fields as
    // JSON, `data2` the text (NotePopup reads `res[0].data2` and
    // JSON.parse(res[0].data)). No draft is an empty list, not a failure.
    shape: (draft) => okStatus('OK', { data: draft ? [noteDraftRow(draft)] : [] }),
    refusals: { note_draft_not_found: okStatus('OK', { data: [] }) },
  },
  '/notes/update#updateNotes': {
    // "Save Draft": replaces the caller's draft on this chart (main deleted
    // the row and inserted the new one).
    method: 'PUT',
    path: (p) => `/patients/${id(p.patientid)}/note-draft`,
    body: (p) => noteDraftBody(p),
    shape: (draft) => okStatus('OK', { data: draft ? noteDraftRow(draft) : null }),
  },
  '/notes/delete#deleteNotes': {
    // Cancel, and the clean-up after a note is saved: the draft is discarded.
    method: 'DELETE',
    path: (p) => `/patients/${id(p.patientid)}/note-draft`,
    shape: () => okStatus('OK', { data: null }),
  },

  // --- Preferences --------------------------------------------------------
  '/preferences/index#getPreferences': {
    method: 'GET',
    path: () => '/me/preferences',
    shape: (prefs) => okStatus('OK', { data: prefs }),
  },
  '/preferences/update#updatePreferences': {
    method: 'PUT',
    path: () => '/me/preferences',
    // `ReplacePreferencesRequest` validates `preferences` as `present|array`.
    // The call site posts the settings object *flat* (HomeGrid.vue:3158 sends
    // `this.preferences`), so forwarding `p` unchanged 422'd on a missing
    // `preferences` key and no home-grid layout was ever saved.
    body: (p) => ({ preferences: stateBlob(p && p.preferences !== undefined ? p.preferences : p) }),
    shape: (prefs) => okStatus('OK', { data: prefs }),
  },

  // --- Devices and their readings -----------------------------------------
  '/meter/create#createDevice': {
    method: 'POST',
    path: (p) => `/patients/${currentPatientId(p)}/devices`,
    shape: (device) => okStatus('OK', { data: device }),
  },
  '/data/externalMeasures': {
    // Every reading on the chart, all pages, in main's
    // `{externalMeasures, externalMeasCcdMeasIds, externalMeasCcds}` - what
    // the vitals grid and graph read (api2/vitalsBridge.js). One 25-row page
    // of raw rows left both empty. No chart in view reads as no readings.
    local: async (p) => okStatus('OK', { data: await readExternalMeasures(currentPatientId(p), p || {}) }),
  },
  '/data/getLatestTime': {
    method: 'GET',
    path: (p) => `/patients/${currentPatientId(p)}/measurements/latest`,
    shape: (latest) => okStatus('OK', { data: latest }),
  },
  '/data/getContinuousGlucose': {
    // The Continual tab's weekly diary, in main's two answers - `{pageCount}`
    // for `{}` and `{data, scannedData}` for a page - built from the chart's
    // LibreLink measurements (glucoseBridge). The device-readings path this
    // used needs a device id the page never sends, so the bridge answered an
    // empty list and the graph threw on `item.data.length`.
    local: async (p) => okStatus('OK', { data: await readContinuousGlucose(currentPatientId(p), p || {}) }),
  },

  // --- Documents ----------------------------------------------------------
  '/file/cdas#requestCda': {
    // One C-CDA, rendered for reading: main's `requestCda` (pacmny-be
    // CdasAction) answered `{data: {cda: <html>}}` and cdaApi.getCdaDocById
    // reads exactly `data.data.cda`, which CdaDoc.vue writes into its iframe.
    // The list of a chart's C-CDAs is `allData.cdas` (stores/chartCdas.js),
    // not this. Addressed by the row's uid, like every C-CDA route.
    method: 'GET',
    path: (p) => `/cda-documents/${fileRef(p.id)}`,
    collection: false,
    shape: (rendered) => okStatus('OK', { data: { cda: renderedCdaHtml(rendered) } }),
  },

  // --- Licences -----------------------------------------------------------
  // The contacts screens' licensed-individual search (UserContact `queryContact`);
  // see licenceLookup for what it asks and when it asks nothing.
  '/license/query#query': licenceLookup(),
  '/license/update-status#updateStatus': {
    method: 'PATCH',
    path: (p) => `/professional-licences/${id(p.licenseId || p.id)}`,
    body: (p) => ({ status: p.status }),
    shape: (licence) => okStatus('OK', { data: licence }),
  },

  // --- Session-scoped user state ------------------------------------------
  //
  // `saveState` and `saveWatchList` wrote a blob onto the Yii2 session row.
  // They are now explicit sub-resources of `/me`, which is what they always
  // were: per-user UI state that happened to live wherever the session did.
  '/session/saveState#saveState': {
    method: 'PUT',
    path: () => '/me/ui-state',
    // The FSM posts `JSON.stringify(instance.root)` (fsm/index.js:429) and
    // `ReplaceUiStateRequest` validates `state` as `present|array`, which a JSON
    // *string* is not — so every FSM save 422'd and the persisted machine tree
    // was never written. `stateBlob` parses it, which is what keyon's text
    // column did one hop later.
    body: (p) => ({ state: stateBlob(p.text ?? p.state) }),
    shape: () => okStatus('OK'),
  },
  // `saveWatchList` and `requestWatchList` are in UNMAPPED: `/me/watchlist`
  // stores watched *patients* and the legacy blob is watched *vitals*.
  '/session/updateProfile#updateProfile': {
    method: 'PATCH',
    path: () => '/me',
    shape: (me) => okStatus('OK', { data: me }),
  },

  // --- Patient invitation --------------------------------------------------
  '/session/invitePatient#invitePatient': {
    method: 'POST',
    path: (p) => `/patients/${currentPatientId(p)}/invitation`,
    shape: () => okStatus('OK'),
  },
  '/session/uninvitePatient#uninvitePatient': {
    method: 'DELETE',
    path: (p) => `/patients/${currentPatientId(p)}/invitation`,
    shape: () => okStatus('OK'),
  },

  // `summaryByExternalId` and `acquireByExternalId` are in UNMAPPED: main
  // closed both (chart access on the first, the owner filter restored on the
  // second) and neither has a live caller there — imports and commented-out
  // code only.

  // --- Care-team assignment -------------------------------------------------
  //
  // One legacy call carried both halves of the change: providers to add and
  // providers to remove. Main gates it on chart access and privilege 5; the
  // port's care-team routes carry both gates. Each id is its own REST call —
  // an add is `POST .../care-team/providers`, a removal the member's DELETE —
  // run in order, adds first, so a failure part-way leaves nobody removed who
  // was not replaced.
  '/session/provAssignmentById#provAssignmentById': {
    local: (p) => assignProvidersById(p),
  },

  // --- Messaging ------------------------------------------------------------
  '/session/requestInitialMessage#requestInitialMessage': {
    method: 'GET',
    path: () => '/message-threads',
    query: () => ({ per_page: 1, sort: '-updated_at' }),
    paginated: true,
    shape: (page) => okStatus('OK', { data: (page.items || [])[0] || null }),
  },

  // --- Viewing a patient's chart --------------------------------------------
  //
  // Main renamed these in PR #249 ("the word impersonate is gone"): the staff
  // member views a chart while still signed in as themselves. Neither changes
  // the token; see `enterChart`. The chart is opened through the patient
  // context, which calls `POST /patients/{id}/chart-view` so the entry rule is
  // checked and the opening audited, and closed with the matching DELETE.
  '/session/enterPatientChart#enterPatientChart': {
    local: (p) => enterChart(p),
  },
  '/session/exitPatientChart#exitPatientChart': {
    local: () => exitChart(),
  },
  // The pre-#249 names, for any caller not yet renamed. Same operations: the
  // act-as exchange they used to run is off at the provider (decision D4).
  '/session/impersonate#impersonate': {
    local: (p) => enterChart(p),
  },
  '/session/revertToSelf#revertToSelf': {
    local: () => exitChart(),
  },

  // --- The application model -----------------------------------------------
  //
  // `getAllData` was the whole session in one response: user, current chart,
  // providers, taxonomies, NPI groupings. Roughly 129 components still read it,
  // which is why leaving it unmapped threw on almost every page.
  //
  // `GET /me/bootstrap` is its honest half — who you are, where you work, what
  // you may do. The other half, *which chart is open*, is not the server's to
  // answer any more: there is no session for it to live in. `patient` is left
  // empty on purpose, and `login/getAllData` falls through to the SPA's own
  // patient context when it is. Same for the provider and taxonomy maps, which
  // are their own resources now.
  '/online/ajax#getAllData': {
    method: 'GET',
    path: () => '/me/bootstrap',
    shape: (payload) => {
      const model = payload || {};
      const user = model.user || {};
      const org = model.organization || {};
      // Present only under an RFC 8693 act-as token, which the SPA no longer
      // asks for and pacmny-auth issues only when deliberately switched on.
      // `user` is then the person acted as — the patient — and `actor` the
      // staff member at the keyboard.
      const actor = model.actor && model.actor.id ? model.actor : null;

      return {
        status: 'OK',
        user: {
          ...user,
          externid: user.public_id,
          unique_ID: user.id,
          accountType: user.account_type,
          // Main's words, the spelling NotesPage reads after `manager`'s (MX-07).
          accounttype: legacyAccountType(user.account_type),
          // The legacy spellings the templates bind. Kept alongside the real
          // field names rather than instead of them, so a migrated component
          // and an unmigrated one can read the same object.
          userName: user.username,
          dob: user.date_of_birth,
          firstname: user.first_name,
          lastname: user.last_name,
          fname: user.first_name || (user.full_name || '').split(' ')[0],
          lname: user.last_name || (user.full_name || '').split(' ').slice(1).join(' '),
        },
        // The staff member at the keyboard — pacmny-be's `manager` (the
        // session's agent, DataManagerImpl::getAllData) — and never the
        // organization or the chart. Viewing a chart does not change who is
        // acting (decision D4), so this is who a text or call is from
        // (utils/messagingSender.js and the compose modals' `manager.phone1`),
        // who signs a note, who the directives actor is, and whose name the
        // account menu shows. It used to be the organization's contact block,
        // which put the agency's phone on every "From" line and its account
        // number where a signer's id belonged. A patient's own session has no
        // manager on main, and none here.
        //
        // Under an act-as token the person at the keyboard is `actor`, not
        // `user`; building this from `user` would make the patient the "From"
        // of every text and call the staff member sends (MSG-13).
        manager: actor
          ? staffManager(actor, org)
          : (user.account_type === 'patient' ? {} : staffManager(user, org)),
        // Main's flag for "the staff member is in a chart that is not theirs".
        // An act-as session is exactly that. Without one, which chart is open
        // is the SPA's own state, and `login/getAllData` reports it from the
        // patient context over whatever this says.
        viewingPatientChart: Boolean(actor),
        actor,
        // The organization's own contact block, for whatever wants the agency
        // rather than the person.
        organization: org,
        roles: model.roles || [],
        permissions: model.permissions || [],
        scopes: model.scopes || [],
        preferences: model.preferences || {},

        // `getAdministrator()` (src/data/getters.js) reads this key, and the
        // whole administrator half of both navigation bars is `v-if` on it —
        // Nurses, Patients, Notes and Billing. The legacy sign-in response
        // carried it; `/me/bootstrap` does not, so without deriving it here it
        // is `undefined` and an administrator sees the *non*-administrator
        // menus and none of their own.
        administrator: (model.roles || []).includes('administrator')
          || user.account_type === 'administrator',

        // Deliberately empty — see above. Present so the ~129 call sites that
        // destructure these keys read `undefined`, not a TypeError.
        patient: {},
        npis: {},
        taxonomies: {},
        npiGroupings: {},
      };
    },
  },

  // --- Ping and vital-sign codes -------------------------------------------
  //
  // Both already had `#action` entries; naming the routes too means they
  // resolve without relying on the action-only fallback.
  '/session/ping#ping': {
    method: 'GET',
    path: () => '/me',
    shape: () => okStatus('OK'),
  },
  '/session/requestVitalSignsCodes': {
    method: 'GET',
    path: () => '/vital-sign-codes',
    paginated: true,
    // The same integer keys as `#requestVitalSignsCodes` above.
    shape: (page) => okStatus('OK', { data: { codes: vitalSignCodeKeys(page.items) } }),
  },
};

/**
 * Routes the resource server deliberately does not answer.
 *
 * Authentication moved out of the API. `/session/login`, `/session/logout` and
 * `/session/changePassword` belong to the OIDC provider and are reached through
 * the authorization endpoint, not through a JSON call the SPA makes on the
 * user's behalf — that is the whole reason for the provider. Bridging them to
 * *something* would be worse than refusing: it would put a credential-handling
 * path back inside the resource server.
 *
 * Listed rather than omitted so the failure names the reason.
 */
export const BE_RETIRED = {
  '/session/login': 'Sign-in is handled by the OIDC provider; start the authorization flow instead.',
  '/session/logout': 'Sign-out is RP-initiated logout at the OIDC provider.',
  '/session/changePassword': 'Passwords are managed by the OIDC provider, not the API.',
};

/**
 * `NPILookup.php` was a single-operation file rather than an envelope dispatch:
 * every caller posted `methname` and read a bare array back.
 */
export const NPI_LOOKUP_OPERATION = {
  method: 'GET',
  path: () => '/provider-registry-search',
  query: (p) => ({
    npi: p.npi || undefined,
    first_name: p.first || undefined,
    last_name: p.last || undefined,
    organization_name: p.business || undefined,
    specialty: p.specialty || undefined,
  }),
  // The registry rows are re-flattened to the NPPES-ish shape the save modals
  // parse, including the `addresses[]` sub-objects they index by hand.
  shape: (result) => {
    const rows = (result && result.results) || [];

    if (!rows.length) return { results: 'No Results Found' };

    return rows.map((row) => ({
      npinumber: row.npi,
      name: row.display_name,
      first_name: row.first_name,
      middle_name: row.middle_name,
      last_name: row.last_name,
      addresses: (row.addresses || []).map((a) => ({
        address_1: a.line1,
        address_2: a.line2,
        city: a.city,
        state: a.state,
        tel: a.phone,
        fax: a.fax,
      })),
    }));
  },
};

/**
 * `icd_calls.php` was, like `NPILookup.php`, a single-operation file outside the
 * envelope dispatch: callers post `{action: 'CodeLookUp' | 'Search',
 * searchTerm}` and read `{result, actualResults: [{code, description}]}`.
 * Nothing bridged it, so every ICD picker — Current Diagnosis, Current
 * Procedures, the order and pending-order popups, the medication form, the
 * device screen and main's DiagnosisCombobox — called a backend that no longer
 * exists. `icdLookup` answers from `GET /vocabularies/icd10`.
 */
export const ICD_LOOKUP_OPERATION = {
  local: (p) => icdLookup(p),
};

/**
 * Legacy operations the SPA still calls that this table deliberately does NOT
 * map. Each one throws `BridgeUnmappedOperation` at the call site, which is the
 * point: a loud failure naming the gap beats a request to a path that does not
 * exist. This list is the burn-down; it shrinks as components are rewritten.
 */
export const UNMAPPED = {
  // A misspelling a few dead callers send. keyon has no branch for it either
  // (TwilioAccess::covers('SendPhoneCalls') is false), so there is nothing to
  // be faithful to; Twilio.SendPhoneCall is the call.
  'Twilio.SendPhoneCalls': 'keyon has no SendPhoneCalls branch; the call operation is Twilio.SendPhoneCall.',
  'Account.SendContactInvitationEmail': 'No caller left: UserContact.inviteContact (hidden on main, which points at REGISTER) now gives the saved contact a login through POST /patients/{patient}/contacts/{contact}/account, as REGISTER does; this payload named neither the chart nor the contact.',
  // Main's SaveNewInsurance (tswebhook.php:6565) writes a NURSE's malpractice
  // carrier, dates and limits onto account_user/nurses, for the nurse its `pid`
  // names. This was mapped to POST /patients/{pid}/insurance-policies, which
  // files a PATIENT's insurance policy on whichever chart has the nurse's user
  // id as its own id - a write on another subject's chart, limited only by the
  // caller's billing:write. No caller is left that reaches it (CurrentNurseBio,
  // its only one, is rendered only from the commented-out templates of
  // CurrentNurse.vue and CurrentAid.vue). A nurse's cover is on the user in the
  // port: PATCH /users/{user} with malpractice_carrier, malpractice_starts_on,
  // malpractice_ends_on, malpractice_per_incident_limit and
  // malpractice_aggregate_limit (users:write). Never a patient route.
  'Insurance.SaveNewInsurance': 'No caller left, and it was mapped to the wrong subject: a nurse\'s malpractice cover is PATCH /users/{user}, not a patient\'s insurance policy.',

  // Trello 0LGomfTd. Main's only caller was MyAccount's DeleteUserAccount,
  // dead code that named the OPEN CHART's patient (allData.patient.externId)
  // as the person to delete; it is gone, and keyon has no handler either.
  'Provider.DeleteAccountUser': 'Removed with its only (dead) caller. Taking a person\'s sign-in away is POST /users/{user}/deactivate; removing a member of staff is DELETE /users/{user}; neither is ever addressed by the open chart.',

  // Addressed by an id the payload never carries.
  //
  // `PATCH /patients/{patient}/care-team/{member}` binds a `CareAssignment`.
  // The call site sends `provid: provider.providers_row_id` and no patient at
  // all (CurrentProviders.vue:502-521), and `providers_row_id` is a legacy
  // `providers` table key that no port resource emits — `careTeamSlot` has
  // never produced it, which is also why the button's own `v-if` on that field
  // is false. There is nothing in the payload to resolve either segment from,
  // and the sibling operation's care-team lookup cannot help without a chart.
  'Provider.UpdateProviderOnTeam': 'PATCH /patients/{patient}/care-team/{member} needs both a chart and the assignment id; the call site sends neither — only a legacy providers.providerid that nothing emits.',

  // Shape mismatches that need the component rewritten, not re-enveloped.
  // No caller remains: the Devices page (UserDevices.vue) and the CPT page
  // (CptPage.vue) both read GET /patients/{patient}/devices through api2/devices
  // (meterRowsFromDevices) and no longer post this. Mapping it would only
  // reintroduce the XML blob the pages used to parse out of `ReMsg`.
  'GetPatientInfo.GetPatientDevice': 'The page parses a Fora XML blob out of `ReMsg`; GET /patients/{patient}/devices answers JSON rows.',

  // Moved to pacmny-auth (§3). The SPA screen that called these is gone,
  // replaced by MFA inside the authorization-code flow.
  'Sms.GetAccountSMSPHone': 'MFA channel enumeration belongs to pacmny-auth.',
  'Sms.Sms6DigitText': 'MFA challenge belongs to pacmny-auth.',
  'Sms.VerifyLoginCode': 'MFA verification belongs to pacmny-auth.',

  // Never existed on the server.
  'Orders.SignAllOrders': 'No keyon branch and no catalogue row; the call site is an unfinished stub.',

  // The watched-*vitals* blob, which is not the watchlist the port has.
  //
  // Rows 8, 9 and 11 (`/me/preferences`, `/me/ui-state`) were listed here as
  // unregistered and are now in the router (routes/api/platform.php:108-120);
  // they are mapped above. Rows 12 and 13 are registered too, and are still
  // unmapped, for a different and permanent reason: `/me/watchlist` stores
  // watched *patient charts* as ids and filters them to charts the caller may
  // see, while `saveWatchList` posts `JSON.stringify({measures, procedures,
  // checkedMeasures})` — the vital-sign types the trend widgets plot
  // (store/modules/watch.js:340-347). They are different resources wearing one
  // name, and writing measure ids into `patient_ids` would either 422 or, worse,
  // store numbers that happen to be patient ids.
  //
  // The port has no store for watched vitals. `/me/preferences` is the closest
  // home and would need a read-modify-write to avoid clobbering the settings
  // stored beside it, which is a race the component should not be given; both
  // actions' live callers are commented out today (watch.js:178-206), so
  // whoever restores the feature should add the key deliberately rather than
  // inherit one from here.
  '#requestWatchList': 'GET /me/watchlist returns watched patient charts; the legacy action returned the watched vital-sign blob. Different resources — the port has no store for the latter.',
  '#saveWatchList': 'PUT /me/watchlist takes patient_ids; the call site posts a serialized {measures, procedures, checkedMeasures} blob. Writing one into the other would store measure ids as patient ids.',

  // pacmny-be actions the port drops outright (02-api-endpoints.md §4.6).
  '#getAllData': 'Decomposed across /me, /patients/{id} and the resource lists; no single replacement.',
  '#summaryByExternalId': 'No live caller on main (imports and commented-out code only), which gated it on chart access; GET /patients/{id} is the chart header, behind the same gate.',
  '#acquireByExternalId': 'No live caller on main, which restored its owner filter so it can no longer grant the caller a relation; the port grants none — a contact joins through POST /contact-invitations.',
  '#getState': 'Server-side Vuex mirroring is gone; the store is the client’s.',
  '#requestNpis': 'Dead action — the capability is /providers and /provider-registry-search.',
  '#insertGrabRequest': 'No callers in the SPA.',
  '#savePages': 'No callers, and the legacy implementation threw ReferenceError.',

  // Counted by the inventory but never actually posted: these strings appear
  // only in AuditLogPage.vue's API_Meth filter list.
  'Account.CreateAccount': 'No request site; the string appears only in the audit-log filter list.',
  'Account.VerifyPaymentInfo': 'No request site; the string appears only in the audit-log filter list.',
  'Account.BusinessEINLookup': 'No request site; the string appears only in the audit-log filter list.',
};

// --------------------------------------------------------------------------
// Shared body builders
//
// Six legacy operations post the same medication, and four post the same
// prescriber — flattened into one object with a different prefix each time.
// Building them once here keeps the table a table.

/**
 * The structured allergy form's record, in the port's vocabulary.
 *
 * Every enum here is validated with `Rule::in` against `App\Models\Condition`,
 * and the form's option values are a different vocabulary — `'drug'` for
 * `medication`, `'environmental'` for `environment`, hyphens where the port uses
 * underscores. Forwarded verbatim, the commonest allergy a clinician records
 * (type Drug, severity Life-Threatening) failed two rules at once, so a
 * medication allergy could not be entered at all. The translation lives here
 * rather than in `AllergyForm.vue` because those option values are also the
 * union members of `Allergy` in `stores/allergyStore.ts`, which the list and
 * card components render from — see the enum maps in `bridgeShapes`.
 */
function allergyBody(params) {
  const a = params || {};
  const retracted = allergyRetraction(a.status);

  return {
    name: a.name,
    type: allergyType(a.type),
    category: allergyType(a.category),
    criticality: allergyCriticality(a.criticality),
    severity: allergySeverity(a.severity),
    status: allergyClinicalStatus(a.status),
    // "Entered in error" on the Status select outranks whatever the Verification
    // select happens to hold: it is the stronger assertion, and it is the one
    // FHIR — and `Condition::VERIFICATION_STATUSES` — has a member for.
    verification_status: retracted ? 'entered_in_error' : allergyVerification(a.verificationStatus),
    onset_date: dateOnly(a.onsetDate),
    resolved_date: dateOnly(a.resolvedDate),
    reactions: allergyReactions(a.reactions),
    // Terms only (the API validates `cross_reactivity.*` as strings): keyon
    // saved "none chosen" as `[{}]`, and a list read from a row that holds it
    // must not turn a save into a 422. Left out when the caller sent no
    // list, so an edit that does not mention it leaves it alone.
    cross_reactivity: Array.isArray(a.crossReactivity)
      ? a.crossReactivity.filter((term) => typeof term === 'string' && term.trim() !== '')
      : undefined,
    comments: a.comments,
  };
}

/**
 * Whether the signed-in person holds a permission, as `GET /me` reported it.
 *
 * For choosing between two routes only, never as the authority: the API
 * decides, and a wrong guess here is a refusal the person is shown.
 */
function sessionCan(permission) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useSessionStore } = require('@/stores/session');
    const session = useSessionStore();

    return Array.isArray(session?.permissions) && session.permissions.includes(permission);
  } catch (e) {
    return false;
  }
}

/**
 * The count on hand when a medication is added (MED-13), for a supply started
 * part-way through. Sent only when it is a real count: every add screen sends
 * `tabsAvailable: x || 0`, and none of today's forms asks for it, so a 0 means
 * "not given" — forwarded, it would start every new medication with nothing on
 * hand. Main's add ignores the field and starts the stock full; so does the
 * server when it is absent, and it holds a count to the quantity dispensed.
 */
function openingStock(p) {
  const count = Number(p.tabsAvailable);

  return Number.isFinite(count) && count > 0 ? Math.floor(count) : undefined;
}

function medicationBody(p) {
  return {
    name: p.medname || p.name,
    shorthand: p.shorthand,
    rxnorm_code: p.rxnorns,
    ndc_code: p.ndcnumber,
    dose_amount: p.doseamount || p.dosage,
    dose_uom: p.doseUOM || p.unitType,
    route: p.medRoute || p.route,
    alt_route: p.altroute,
    frequency: p.frequency,
    administration: p.administration,
    quantity: p.quantity || p.totalTabs,
    start_date: dateOnly(p.medstrtdt || p.startDate),
    end_date: dateOnly(p.medenddt || p.endDate),
    instructions: p.instruction || p.directionuse || p.instructions,
    // The code only (MED-26): the diagnosis combobox saves "CODE -
    // description", and `diagnosis_code` holds at most 32 characters.
    diagnosis_code: diagnosisCode(p.diagcode || p.diagnosis),
    is_prn: bool(p.prn),
    is_prepour: bool(p.prepour),
    is_nebulizer: bool(p.nebulizer),
    // Add Meds' "new medication" tick (main's isnewtimetag 'New', PP-08): the
    // first day's doses are tagged New, which the pre-pour sheet shows.
    is_new: p.newMedication === undefined ? undefined : bool(p.newMedication),
    status: p.medstatus || p.status,
    drug_classification: p.durgclassification,
    // `additional_settings` is `sometimes|nullable|array` — the legacy stored
    // the select's value as a bare string (tswebhook.php:8586) and forwarding it
    // failed with "additional_settings must be an array" whenever a setting was
    // actually chosen. An empty selection is nulled by the API's own middleware
    // and is left alone.
    additional_settings: settingsList(p.additionalsettings),
    change_type: p.changetype,
    source: p.source,
  };
}

/** The additional-settings select, as the list the column stores. */
function settingsList(value) {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || String(value).trim() === '') return undefined;

  return String(value).split(',').map((entry) => entry.trim()).filter(Boolean);
}

function prescriberBody(p) {
  return {
    npi: p.primeprovNpi || p.providerNpi,
    name: p.primeprovName || p.providerName,
    dea: p.providerDea,
    email: p.primeprovEmail || p.providerEmail,
    phone: p.primeprovPhone || p.providerOffice,
    cell: p.providerCell,
    fax: p.primeprovFax,
    address: p.primeprovAddress || p.providerAddress,
  };
}

function pharmacyBody(p) {
  return {
    name: p.pharmacy || p.pharmacyName,
    npi: p.pharmacyNpi,
    dea: p.pharmacyDea,
    email: p.pharmacyEmail,
    phone: p.pharmacyPhone,
    address: p.pharmacyAddress,
  };
}

function doseNumber(value) {
  if (value === null || value === undefined || String(value).trim() === '') return undefined;
  const n = Number(value);

  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function scheduleBody(p) {
  return {
    // `times.*` is validated as a bare `'HH:MM'` string on every schedule
    // endpoint. The legacy wire format is `[{time, status, dosage}]` and the
    // legacy handlers read `$stime->time`, so the slot objects have to be
    // flattened here — forwarded whole they 422'd every administration-schedule
    // write, which is the whole prescribe flow plus both MAR time editors. The
    // status and the dose are not lost: a new slot's status is the server's to
    // decide, and the dose comes from the medication.
    times: scheduleTimes(p.slotedtimes || p.scheduletimes || p.administrationTimes),
    // A flag the caller did not send is not sent (CRIT-06): `bool(undefined)`
    // is false, and a false here took the medication off the MAR, out of
    // pre-pour and off the nebulizer list on every time edit. The server
    // changes a flag only when one arrives.
    administration: p.administration === undefined ? undefined : bool(p.administration),
    frequency: p.frequency,
    // The dose the time editor sends with every save. A dose that differs
    // from the line's needs a change order, and the server refuses it on a
    // times-only edit (main #335's 402); not forwarded, a nurse's new dose was
    // dropped while the save answered "Updated". Only a number goes: the
    // field is a count per administration, and a label is not a dose.
    dose_amount: doseNumber(p.dosage),
    // The dose's unit, which both time editors send with every save and the
    // MAR's unit select sends through its change-order prompt. It is the
    // prescription's as the dose is: resent as it stands it changes nothing,
    // new it needs a change order (contract D6, FX-MED follow-up).
    dose_unit: p.unitType ? String(p.unitType) : undefined,
    // The course's dates only when the caller names them (PP-OM-03). MAR's
    // "No, just update times" sends the day of the edit as `adminDate`; sent as
    // the start date, it moved every edited medication's start to today. The
    // time editors now send a start or end only when the clinician changed it
    // (utils/medicationCourseDates.js), and it is a local calendar day: a
    // Date taken through `dateOnly` was the UTC day, tomorrow from 20:00 in
    // New York.
    start_date: p.startDate ? toLocalYMD(p.startDate) : undefined,
    end_date: p.endDate ? toLocalYMD(p.endDate) : undefined,
    is_prn: p.prn === undefined ? undefined : bool(p.prn),
    is_prepour: p.prepour === undefined ? undefined : bool(p.prepour),
    is_nebulizer: p.nebulizer === undefined ? undefined : bool(p.nebulizer),
    rate: p.rate,
    total_volume: p.totalVolume,
    total_volume_unit: p.totalVolumeUnit,
    via: p.via,
  };
}

/**
 * A hold, in the port's vocabulary.
 *
 * The two legacy fields swap meanings on the way through, which is the whole of
 * the fix:
 *
 *   legacy `status`  ('hold' | 'discontinue')  -> `type`   (what to do)
 *   legacy `type`    ('all'  | 'specific')     -> `times`  (how much of the day)
 *
 * `status` is then the endpoint's own — `applied` for a hold placed now,
 * `scheduled` for one queued — and is not read from the payload at all, because
 * the legacy value there was never a status in this sense. `PATCH
 * /scheduled-holds/{hold}` accepts neither `status` nor `archive_previous`, so
 * an amendment omits both rather than being refused for sending them.
 *
 * `date_range` members are omitted when absent: `date_range.from` is
 * `sometimes|date` with no `nullable`, so a present-and-null bound is a 422
 * naming a field the clinician left empty on purpose.
 */
function holdBody(hold, { queued = false, amend = false } = {}) {
  const h = hold || {};
  // HoldTimeSelector's days are local calendar days (main #335, PP-22). Taken
  // through `dateOnly` they became UTC days, so from 20:00 in New York a hold
  // starting today was sent as starting tomorrow, and its end a day late.
  const from = toLocalYMD(h.dateRange && h.dateRange[0]);
  const to = toLocalYMD(h.dateRange && h.dateRange[1]);
  const range = {};

  if (from) range.from = from;
  if (to) range.to = to;

  // `scheduleDates` is the day picker's list and only the queued endpoints take
  // it; the immediate hold is "from now", so a list of days there would say
  // nothing the window does not.
  const days = queued && Array.isArray(h.scheduleDates) && h.scheduleDates.length
    ? h.scheduleDates.map(toLocalYMD).filter(Boolean)
    : null;

  return {
    type: holdKind(h.status),
    reason: h.reason,
    status: amend ? undefined : (queued ? 'scheduled' : 'applied'),
    scheduled_dates: days && days.length ? days : undefined,
    date_range: Object.keys(range).length ? range : undefined,
    times: holdTimes(h.type, h.times),
    archive_previous: amend ? undefined : archiveFlag(h.archiveHLDDC),
  };
}

const HOLD_KINDS = { hold: 'hold', discontinue: 'discontinue' };

/**
 * What a hold does, from the legacy `holdobjec.status`.
 *
 * MedAdmin routes both a hold and a discontinue through `HoldMedication`
 * (MedAdmin.vue:4494), distinguishing them only by this string, and
 * `MedicationSchedule::HOLD_TYPES` is exactly that pair.
 */
const holdKind = (v) => HOLD_KINDS[String(v ?? '').toLowerCase()] ?? 'hold';

/**
 * `archive_previous`, only when the caller really expressed one.
 *
 * The flag defaults to **true** server-side — holding an ordered medication
 * changes the order and the change needs signing — and `archiveHLDDC` arrives
 * as an object from the hold selector, on which `bool()` is unconditionally
 * false. Sending that false suppressed the change order on every hold. An
 * unrecognisable value now sends nothing and lets the endpoint's default stand.
 */
function archiveFlag(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number' || typeof value === 'string') return bool(value);

  return undefined;
}

function orderBody(p) {
  return {
    patient_id: id(p.patientID),
    type: p.ordertype,
    ordered_on: dateOnly(p.orderdate),
    ordered_at: p.ordertime,
    description: p.description,
    abn_delivery: p.abndelivery,
    read_back: bool(p.ordReadback),
    primary_physician_id: id(p.primphysician),
    secondary_physician_id: id(p.secphysician),
    status: p.status,
    wound_care: p.woundcare,
    is_verbal: bool(p.verbalorder),
    verbal_start_date: dateOnly(p.verbalStartdt),
    verbal_end_date: dateOnly(p.verbalEnddt),
    notify_physicians: bool(p.sendtophysicans),
  };
}

/**
 * A patient note.
 *
 * Two things the legacy wire got wrong and this translates.
 *
 * **The type.** `NotePopup` posts 'Nursing', 'Providers', 'Vitals', '9898X' and
 * '9945X'; `Note::TYPES` is lowercase and spells the last two
 * `therapeutic_monitoring` / `physiologic_monitoring`. `Rule::in` is
 * case-sensitive and has no aliases, so every note save 422'd — the primary
 * note-writing flow had no working path.
 *
 * **The unit.** `timeLength` is **seconds** off a one-per-second timer
 * (NotePopup.vue:1139), and keyon billed it against a 1200-second cycle. It was
 * posted into `duration_minutes`, which `NoteLedger` multiplies by 60 — a 60x
 * inflation of billable RPM/RTM time, and `max:1440` rejected anything timed for
 * over 24 minutes.
 *
 * `notify.email` reads only the checkbox: the call site also sends `email` as
 * the patient's *address*, which `bool()` silently read as false. The two
 * flags always addressed the PATIENT (keyon's SavePatientNote e-mails and
 * texts the patient), so `notify.patient` says so; the API sends to the
 * patient's own address and number on file, the text through the telephony
 * gate. The posted `email` / `phone` are profile copies and are not forwarded.
 */
/**
 * A note save's path, refused without a request when no chart is named: a
 * locally-resolved operation skips the bridge's unresolved-path guard, and a
 * write with no target must fail rather than post to `/patients/null/notes`.
 */
function notePath(patientId) {
  const chart = routeId(patientId);

  if (chart === null) {
    throw new ApiError({ status: 404, code: 'not_found', message: 'The chart this note is for could not be identified.' });
  }

  return `/patients/${chart}/notes`;
}

function noteBody(p) {
  return {
    // Videos the note carries (#334, main's attachment_uids and
    // patient_visible_uids). Judged by the API in main's codes; absent when
    // the note names none, so a note without video is the body it always was.
    ...(Array.isArray(p.attachment_uids) && p.attachment_uids.length ? { attachment_uids: p.attachment_uids } : {}),
    ...(Array.isArray(p.patient_visible_uids) && p.patient_visible_uids.length ? { patient_visible_uids: p.patient_visible_uids } : {}),
    type: noteType(p.type),
    title: p.pname,
    body_html: p.PatientNote || p.pnotes || p.body_html,
    start_at: p.startDt ? `${dateOnly(p.startDt)}T${p.startTime || '00:00'}` : undefined,
    end_at: p.endDt ? `${dateOnly(p.endDt)}T${p.endTime || '00:00'}` : undefined,
    duration_minutes: billableMinutes(p.timeLength),
    provider_signature: p.provsignature,
    provider_signed_at: dateOnly(p.provsigdt),
    notify: {
      email: bool(p.notify),
      sms: bool(p.notifysms),
      patient: bool(p.notify) || bool(p.notifysms),
    },
  };
}

/**
 * The note popup's draft (`draftNote`): its text and the four encounter fields
 * main stored as `frontendcache2.data`. `userid` is dropped on purpose — the
 * API takes the author from the token.
 */
function noteDraftBody(p) {
  const times = (p && typeof p.timestamps === 'object' && p.timestamps) || {};
  const clock = (value) => (/^\d{2}:\d{2}(:\d{2})?$/.test(String(value || '')) ? String(value) : null);

  return {
    body: p.draft ?? '',
    started_on: dateOnly(times.startDt) || null,
    ended_on: dateOnly(times.endDt) || null,
    start_time: clock(times.startTime),
    end_time: clock(times.endTime),
  };
}

/** A draft as main's `/notes/index` row: `data` the encounter fields as JSON, `data2` the text. */
function noteDraftRow(draft) {
  return {
    patientid: draft.patient_id,
    data: JSON.stringify({
      startDt: draft.started_on,
      endDt: draft.ended_on,
      startTime: draft.start_time,
      endTime: draft.end_time,
    }),
    data2: draft.body,
  };
}

/**
 * A weekly series' days as the API takes them: weekday indices, 0 = Sunday.
 * Main's call site joins them into a comma list for keyon's varchar column; an
 * array is accepted as well. Anything that is not a weekday is dropped.
 */
function seriesWeekdays(days) {
  const list = Array.isArray(days) ? days : String(days == null ? '' : days).split(',');

  return list
    .map((d) => String(d).trim())
    .filter((d) => d !== '')
    .map(Number)
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
}

/**
 * The calendar's caregiver types (availability.ts CaregiverType) in the API's
 * spelling (Appointment::CAREGIVER_TYPES): three are abbreviated on the
 * screen, the rest are the same word. The API's own spellings pass through.
 */
const API_CAREGIVER_TYPES = Object.freeze({
  md: 'md', rn: 'rn', hha: 'hha', pt: 'pt', ot: 'ot', slp: 'slp', sw: 'sw', bh: 'bh',
  comp: 'companion', cm: 'care_manager', resp: 'respite',
  companion: 'companion', care_manager: 'care_manager', respite: 'respite',
});

/**
 * The rubric column a booking screen evaluated the visit under, for the API.
 * Main's calendar computes it from the caregiver's credential
 * (caregiverTypeForCredential); the API needs it for an allied professional
 * — one account type, several rubric columns — and refuses that booking
 * without it (422 caregiver_type_required). Anything that is not a column is
 * left out, and the API derives the column from the account type.
 */
function apiCaregiverType(type) {
  return Object.prototype.hasOwnProperty.call(API_CAREGIVER_TYPES, type) ? API_CAREGIVER_TYPES[type] : undefined;
}

/**
 * Who an appointment's notices go to, besides the patient and the provider:
 * main's `notifyGroup`, the care-team members the scheduler ticked, as the
 * picker shows them ({name, email, phone} — no id). The API matches each to a
 * person in the organization by e-mail and texts only a number on file
 * (AppointmentNotifier); the name is display text and stays here.
 */
function notifyBody(p) {
  const members = (Array.isArray(p.notifyGroup) ? p.notifyGroup : [])
    .filter((m) => m && typeof m === 'object')
    .map((m) => ({ email: m.email || undefined, phone: m.phone ? String(m.phone) : undefined }))
    .filter((m) => m.email || m.phone);

  return {
    email: bool(p.notifyEmail),
    sms: bool(p.notifySms),
    care_team: members.length > 0,
    care_team_members: members.length > 0 ? members : undefined,
  };
}

/**
 * Who a text or call is for. `patient_unique_ID` names the charted patient
 * (allData.patient.externId, which is the patient's id here) and wins: the API
 * uses the number on the patient's own record, as keyon does, and the posted
 * number — a profile copy — is sent only so a request naming nobody else still
 * reads the same. Otherwise the numbers themselves, each of which must be on
 * file.
 */
function twilioRecipient(p, numbers) {
  const patientId = routeId(p.patient_unique_ID);

  return {
    patient_id: patientId === null ? undefined : patientId,
    to: splitNumbers(numbers),
  };
}

/** keyon's `smsnumber` / `conferencenumbers`: a comma list, blanks dropped. */
function splitNumbers(value) {
  const parts = Array.isArray(value) ? value : String(value == null ? '' : value).split(',');

  return parts.map((n) => String(n == null ? '' : n).trim()).filter(Boolean);
}

/**
 * keyon's success body (TwilioAccess::accepted): the provider's id as
 * `status`, and TwilioStatus 'successful'. Only a real id counts — an answer
 * without one is keyon's failure body, never "sent".
 */
function twilioSent(sid) {
  return sid
    ? { status: String(sid), TwilioStatus: 'successful' }
    : { status: 'Error', code: 'sms_failed', message: 'The text message could not be sent.', TwilioStatus: 'failed' };
}

function blockBody(p) {
  return {
    service_mode: p.serviceMode,
    context_label: p.contextLabel,
    start_time: p.startTime,
    end_time: p.endTime,
    exclusivity: p.exclusivity,
    recurrence_type: p.recurrenceType,
    recurrence_day: p.recurrenceDay,
    specific_date: dateOnly(p.specificDate),
    notes: p.notes,
  };
}

export default {
  KEYON_OPERATIONS,
  KEYON_FLAT_OPERATIONS,
  BE_OPERATIONS,
  NPI_LOOKUP_OPERATION,
  ICD_LOOKUP_OPERATION,
  UNMAPPED,
};
